import * as i0 from '@angular/core';
import { Injectable, Inject, NgModule } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { FirebaseDatabasePaths, EndpointUrl, GoogleTagManagerEventsConfig, MailChimpConfig, GoogleAPIConfig } from '@ztarmobile/zwp-service';
import * as i1$2 from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import { distinctUntilChanged, take, debounceTime, map, combineLatest, filter, throttleTime } from 'rxjs/operators';
import * as i1 from '@ztarmobile/zwp-services-auth';
import { serverTimestamp } from 'firebase/database';
import * as i1$1 from '@angular/fire/compat/database';
import { ValuesPipe, OrderByPipe } from 'ngx-pipes';
import { getAuth, updateProfile } from 'firebase/auth';
import { omitBy, isNil } from 'lodash';
import * as i2 from '@ztarmobile/zwp-services-util';

/**
 * implementation of the IUser interface to set and get user data
 */
class UserData {
    ;
    constructor(initialUser) {
        this.createdAt = serverTimestamp();
        if (!!initialUser) {
            this.id = initialUser.uid;
            this.email = initialUser.email;
            this.verified = initialUser.emailVerified;
            // this.hubspotDetails.ipAddress = '0.0.0.0';
        }
    }
    get fullName() {
        return this.firstName + ' ' + this.lastName;
    }
}

class FirebaseUserProfileService {
    constructor(simpleAuth, angularFireDatabase, userAuthService) {
        this.simpleAuth = simpleAuth;
        this.angularFireDatabase = angularFireDatabase;
        this.userAuthService = userAuthService;
        this.userProfileSubject = new ReplaySubject(1);
        this.userProfileObservable = this.userProfileSubject.asObservable();
        this.simpleAuth.registerDependentService('firebase-user-profile', this);
    }
    buildSubscriptions(fbUser) {
        console.log('building subscriptions for firebase-user-profile.service');
        const subscriptions = [];
        this.fbUser = fbUser;
        subscriptions.push(this.angularFireDatabase.object(FirebaseDatabasePaths.USER_PROFILE(fbUser.uid))
            .valueChanges()
            .pipe(distinctUntilChanged((userProfile1, userProfile2) => {
            return !!userProfile1 && !!userProfile2 && userProfile1.updatedAt === userProfile2.updatedAt;
        }))
            .subscribe((user) => {
            if (!!user) {
                this.fillUserProfile(user);
            }
            else {
                this.userProfileSubject.next(undefined);
            }
        }));
        return subscriptions;
    }
    updateProfileObservable(user) {
        this.userProfileSubject.next(user);
    }
    onLoggedOut() {
        this.userProfileSubject.next(undefined);
    }
    updateUserProfile(newUser, password) {
        return new Promise((resolve, reject) => {
            if (!!this.fbUser) {
                const fbUser = new UserData(this.fbUser);
                if (!newUser.email || fbUser.email === newUser.email) {
                    this.updateFirebaseProfile(newUser, fbUser).then(() => resolve(), (error) => reject(error));
                }
                else {
                    if (!!password && password.length > 0) {
                        this.userAuthService.changeEmailAddress(fbUser.email, password, newUser.email).then(() => {
                            this.updateFirebaseProfile(newUser, fbUser).then(() => resolve(), (error) => reject(error));
                        }, (error) => {
                            reject(error);
                        });
                    }
                    else {
                        reject({ message: 'You should provide your current password to change your current email' });
                    }
                }
            }
        });
    }
    bffUpdateUserProfile(newUser, password) {
        return new Promise((resolve, reject) => {
            //from auth when user signs in / up this object gets created
            if (!!this.fbUser) {
                this.bffUpdateFirebaseProfile(newUser).then(() => resolve(), (error) => reject(error));
            }
        });
    }
    bffUpdateUserEmail(newEmail) {
        return new Promise((resolve, reject) => {
            this.userAuthService.changeEmailThroughBFF(newEmail).then(() => {
                resolve();
            }, (error) => {
                reject(error);
            });
        });
    }
    addShippingAddress(address) {
        if (!address.alias) {
            address.alias = this.fbUser.displayName;
        }
        return new Promise((resolve, reject) => {
            this.addAddress(address, FirebaseDatabasePaths.USER_SHIPPING_ADDRESSES(this.fbUser.uid)).then(() => {
                // this step to update address with id!
                this.editShippingAddress(address).then((updatedAddress) => resolve(updatedAddress), (error) => reject(error));
            });
        });
    }
    editShippingAddress(address) {
        return this.editAddress(address, FirebaseDatabasePaths.USER_SHIPPING_ADDRESSES(this.fbUser.uid, address.id));
    }
    setDefaultShippingAddress(addressKey) {
        return this.setDefaultAddress(addressKey, FirebaseDatabasePaths.USER_SHIPPING_ADDRESSES(this.fbUser.uid));
    }
    removeShippingAddress(addressId) {
        if (!!addressId) {
            return this.removeAddress(FirebaseDatabasePaths.USER_SHIPPING_ADDRESSES(this.fbUser.uid, addressId));
        }
        else {
            return new Promise((resolve, reject) => reject('addressId is missing'));
        }
    }
    getExistShippingAddress(address) {
        return this.callGetExistShippingAddress(address);
    }
    updateFirebaseProfileDate() {
        this.angularFireDatabase
            .object(FirebaseDatabasePaths.USER_PROFILE(this.fbUser.uid))
            .update({ updatedAt: serverTimestamp() })
            .catch((error) => console.error('Update user on database failed', error));
    }
    setReferralId(referralCode) {
        if (!!this.fbUser && !!this.fbUser.uid) {
            return this.updateUserProfile({ referralCode });
        }
        else {
            this.userAuthService.loginAnonymous().then((user) => {
                this.fbUser = user;
                return this.setReferralId(referralCode);
            });
        }
    }
    removeReferralId() {
        if (!!this.fbUser && !!this.fbUser.uid) {
            this.angularFireDatabase.object(FirebaseDatabasePaths.USER_PROFILE(this.fbUser.uid) + '/referralCode').remove();
        }
    }
    // Should be used before adding address to confirm unique address.s
    callGetExistShippingAddress(address) {
        return new Promise((resolve) => {
            this.angularFireDatabase.list(FirebaseDatabasePaths.USER_SHIPPING_ADDRESSES(this.fbUser.uid)).valueChanges().pipe(take(1)).subscribe((addressList) => {
                resolve(addressList.find((existingAddress) => this.compareAddressInfo(address, existingAddress)));
            });
        });
    }
    fillUserProfile(user) {
        user.firstName = user.firstName || '';
        user.lastName = user.lastName || '';
        if (!user.fullName) {
            user.fullName = user.firstName + ' ' + user.lastName;
        }
        if (!!user.shippingAddresses) {
            user.shippingAddresses = ((new ValuesPipe()).transform(user.shippingAddresses));
        }
        if (!!user.paymentMethods) {
            user.paymentMethods = ((new ValuesPipe()).transform(user.paymentMethods));
        }
        this.userProfileSubject.next(user);
    }
    addAddress(address, refPath) {
        return new Promise((resolve, reject) => {
            if (!!this.fbUser) {
                const ref = this.angularFireDatabase.list(refPath).push({
                    country: address.country,
                    city: address.city,
                    state: address.state,
                    postalCode: address.postalCode,
                    address1: address.address1,
                    address2: address.address2,
                    alias: address.alias
                });
                address.id = ref.key;
                this.updateFirebaseProfileDate();
                resolve(address);
            }
            else {
                reject('No User Info Provided');
            }
        });
    }
    editAddress(address, refPath) {
        return new Promise((resolve, reject) => {
            if (!!this.fbUser) {
                const newAddress = {
                    id: address.id,
                    address1: address.address1,
                    address2: !!address.address2 ? address.address2.replace(address.address1, '') : address.address2,
                    country: address.country,
                    state: address.state,
                    city: address.city,
                    createdAt: address.createdAt,
                    postalCode: address.postalCode,
                    isDefault: !!address.isDefault, // this will remove undefined!
                    alias: address.alias
                };
                this.angularFireDatabase.object(refPath).update(newAddress).then(() => {
                    this.updateFirebaseProfileDate();
                    resolve(newAddress);
                });
            }
            else {
                reject('No User Info Provided');
            }
        });
    }
    setDefaultAddress(addressKey, refPath) {
        return new Promise((resolve, reject) => {
            if (!!this.fbUser) {
                const addressList = this.angularFireDatabase.list(refPath);
                addressList.valueChanges().pipe(take(1)).subscribe((addresses) => {
                    addresses.forEach((address) => addressList.update(address.id, { isDefault: address.id === addressKey }));
                });
                this.updateFirebaseProfileDate();
                resolve();
            }
            else {
                reject('No User Info Provided');
            }
        });
    }
    removeAddress(refPath) {
        return new Promise((resolve, reject) => {
            this.angularFireDatabase.object(refPath).remove();
            this.updateFirebaseProfileDate();
            resolve();
        });
    }
    updateFirebaseProfile(newUser, fbUser) {
        return new Promise(((resolve, reject) => {
            //solution to ignore addresses changes, since their structure get changed for the view.
            delete newUser.paymentMethods;
            delete newUser.shippingAddresses;
            newUser.firstName = newUser.firstName || '';
            newUser.lastName = newUser.lastName || '';
            newUser.fullName = newUser.firstName + (newUser.lastName.length > 0 ? ' ' : '') + newUser.lastName;
            this.angularFireDatabase.object(FirebaseDatabasePaths.USER_PROFILE(this.fbUser.uid)).update(Object.assign({}, fbUser, newUser)).then(() => {
                this.updateFirebaseProfileDate();
                const displayName = newUser.fullName;
                const auth = getAuth();
                const user = auth.currentUser;
                updateProfile(user, {
                    displayName,
                    photoURL: null
                }).then(() => {
                    this.fbUser.reload().then(() => {
                        resolve();
                    });
                });
            }).catch((error) => {
                // I don't understand why this is not working!
                console.error('Create user on database failed', error.message);
                reject(error);
            });
        }));
    }
    bffUpdateFirebaseProfile(newUser) {
        return new Promise((resolve, reject) => {
            //solution to ignore addresses changes, since their structure get changed for the view.
            delete newUser.paymentMethods;
            delete newUser.shippingAddresses;
            newUser.firstName = newUser.firstName || '';
            newUser.lastName = newUser.lastName || '';
            this.userAuthService.bffUpdateFirebaseProfile(newUser)
                .then(() => {
                this.fbUser.reload().then(() => {
                    resolve();
                });
            })
                .catch((error) => {
                reject(error);
            });
        });
    }
    // Compare should be int return but we only need to confirm it's unique
    compareAddressInfo(address1, existingAddress) {
        return address1.country === existingAddress.country &&
            address1.address1 === existingAddress.address1 &&
            address1.address2 === existingAddress.address2 &&
            address1.state === existingAddress.state &&
            address1.city === existingAddress.city &&
            address1.postalCode === existingAddress.postalCode;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FirebaseUserProfileService, deps: [{ token: i1.SimpleAuthService }, { token: i1$1.AngularFireDatabase }, { token: i1.UserAuthService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FirebaseUserProfileService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FirebaseUserProfileService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.SimpleAuthService }, { type: i1$1.AngularFireDatabase }, { type: i1.UserAuthService }] });

/**
 * Implementation of the IFirebaseAccountPaymentService
 * @property paymentMethodsList:  list of credit cards details of the user
 * @property defaultPaymentMethod: the default credit card to be used
 * @property paymentMethodListSubject: replySubject to subscribe for the paymentMethodsList
 * @property defaultPaymentMethodSubject : replySubject to subscribe for the defaultPaymentMethod
 */
class FirebaseAccountPaymentService {
    constructor(angularFireDatabase, simpleAuth, firebaseUserProfileService) {
        this.angularFireDatabase = angularFireDatabase;
        this.simpleAuth = simpleAuth;
        this.firebaseUserProfileService = firebaseUserProfileService;
        this.paymentMethodListSubject = new ReplaySubject(1);
        this.defaultPaymentMethodSubject = new ReplaySubject(1);
        this.paymentMethodsList = this.paymentMethodListSubject.asObservable();
        this.defaultPaymentMethod = this.defaultPaymentMethodSubject.asObservable();
        this.simpleAuth.registerDependentService('firebase-account-payment', this);
    }
    /**
     * Build all subscriptions for this service
     * @param fbUser : current firebase logged in user
     */
    buildSubscriptions(fbUser) {
        console.log('building subscriptions for firebase-account-payment.service');
        const subscriptions = [];
        subscriptions.push(
        //get payment methods list from firebase
        this.angularFireDatabase.list(FirebaseDatabasePaths.USER_PAYMENT_METHODS(fbUser.uid), (ref) => ref.orderByChild('lastUpdate'))
            .valueChanges()
            .pipe(debounceTime(30)).subscribe((methods) => this.paymentMethodListSubject.next(!!methods ? methods.reverse() : methods)));
        subscriptions.push(this.paymentMethodsList.subscribe((methods) => this.defaultPaymentMethodSubject.next(methods.find((method) => method.isDefault))));
        return subscriptions;
    }
    /**
     * Clear payment methods list when logging out
     */
    onLoggedOut() {
        this.paymentMethodListSubject.next([]);
    }
    /**
     * Get payment method details by id
     * @param paymentMethodId : id of payment method
     */
    getPaymentMethod(paymentMethodId) {
        return new Promise((resolve, reject) => {
            this.simpleAuth.userState.pipe(take(1)).subscribe((user) => {
                if (!!user) {
                    this.angularFireDatabase.list(FirebaseDatabasePaths.USER_PAYMENT_METHODS(user.uid), (ref) => ref.orderByChild('id').equalTo(paymentMethodId).limitToFirst(1))
                        .valueChanges()
                        .pipe(take(1)).subscribe((cards) => resolve(cards[0]));
                }
                else {
                    reject('No User Info Provided');
                }
            }, (error) => {
                reject(error);
            });
        });
    }
    /**
     * Update payment method information in the firebase
     * @param paymentMethod : payment method updated data
     */
    editPaymentMethod(paymentMethod) {
        this.simpleAuth.userState.pipe(take(1)).subscribe((user) => {
            this.getPaymentMethod(paymentMethod.id).then((fbPaymentMethod) => {
                const key = this.getPaymentMethodFirebaseKey(fbPaymentMethod);
                fbPaymentMethod.lastUpdate = serverTimestamp();
                this.angularFireDatabase.object(FirebaseDatabasePaths.USER_PAYMENT_METHODS(user.uid, key)).update(fbPaymentMethod).then(() => {
                    this.firebaseUserProfileService.updateFirebaseProfileDate();
                });
            });
        }, (error) => {
            console.log(error);
        });
    }
    /**
     * Remove payment method by id from firebase
     * @param paymentMethodId : id of payment method to be removed
     */
    removePaymentMethod(paymentMethodId) {
        this.simpleAuth.userState.pipe(take(1)).subscribe((user) => {
            if (!!user) {
                const cardsList = this.angularFireDatabase.list(FirebaseDatabasePaths.USER_PAYMENT_METHODS(user.uid), (ref) => ref.orderByChild('id').equalTo(paymentMethodId).limitToFirst(1));
                cardsList.snapshotChanges()
                    .pipe(map((actions) => {
                    return actions.map((action) => ({ key: action.key, ...action.payload.val() }));
                }))
                    .pipe(take(1)).subscribe((cards) => {
                    if (!!cards) {
                        const keys = cards.map((card) => card.key);
                        if (!!keys && keys.length > 0 && !!keys[0]) {
                            this.angularFireDatabase.object(FirebaseDatabasePaths.USER_PAYMENT_METHODS(user.uid, keys[0])).remove()
                                .then(() => console.debug(`Payment method key ${keys[0]} with id ${paymentMethodId} removed`), (error) => console.warn('remove failed', error));
                        }
                    }
                    setTimeout(() => this.firebaseUserProfileService.updateFirebaseProfileDate(), 1000);
                });
            }
            else {
                console.error('No User Info Provided');
            }
        }, (error) => {
            console.log(error);
        });
    }
    /**
     * Deprecated method
     * @param paymentMethodId
     */
    setPaymentMethodAsDefault(paymentMethodId) {
        throw new Error('Method is now allowed');
    }
    /**
     * Deprecated method
     * @param paymentMethodId
     * @param userPlanId
     */
    unsetPaymentMethodAsDefault(paymentMethodId, userPlanId) {
        throw new Error('Method not implemented.');
    }
    getPaymentMethodFirebaseKey(paymentMethod) {
        return `${paymentMethod.last4}-${(paymentMethod.expirationDate)}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FirebaseAccountPaymentService, deps: [{ token: i1$1.AngularFireDatabase }, { token: i1.SimpleAuthService }, { token: FirebaseUserProfileService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FirebaseAccountPaymentService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FirebaseAccountPaymentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$1.AngularFireDatabase }, { type: i1.SimpleAuthService }, { type: FirebaseUserProfileService }] });

const PAYMENT_SORT_FIELDS = {
    STATUS: 'status',
    PAYMENT_DATE: 'dateTime',
    CONFIRMATION_NUMBER: 'confirmationNumber',
    METHOD: 'paymentMethod',
    AMOUNT: 'amount'
};
const PAYMENT_SORT_DIRECTION = {
    ASC: 'asc',
    DESC: 'desc',
};
/**
 * Implementation of the IAccountPaymentService
 * @property shortenAddress: limit the length of the entered address to 30 characters and save the rest in address 2
 * @property paymentHistory: list of user payment records
 * @property paymentHistoryErrors : list of payment record errors
 * @property paymentMethodsList: list of user's payment methods
 * @property counter: counter for errors
 * @property paymentMethodsSubscription: reply subject to paymentMethodsList subscription
 * @property paymentHistorySubscription: reply subject to paymentHistory subscription
 * @property paymentHistoryErrorsSubscription: reply subject to paymentHistoryErrors subscription

 */
class AccountPaymentService {
    static shortenAddress(text, maxLen = 30, separator = ' ') {
        if (!!text && typeof text === 'object') {
            text = text['main_text'];
        }
        if (!text || text?.length <= maxLen) {
            return text;
        }
        return text.substr(0, text?.lastIndexOf(separator, maxLen));
    }
    constructor(endpointUrl, http, simpleAuthService, firebaseAccountPaymentService) {
        this.endpointUrl = endpointUrl;
        this.http = http;
        this.simpleAuthService = simpleAuthService;
        this.firebaseAccountPaymentService = firebaseAccountPaymentService;
        this.counter = 0;
        this.paymentMethodsSubscription = new ReplaySubject(1);
        this.paymentHistorySubscription = new ReplaySubject(1);
        this.paymentHistoryErrorsSubscription = new ReplaySubject(1);
        this.paymentMethodsList = this.paymentMethodsSubscription.asObservable();
        this.paymentHistory = this.paymentHistorySubscription.asObservable();
        this.paymentHistoryErrors = this.paymentHistoryErrorsSubscription.asObservable();
        this.simpleAuthService.registerDependentService('account-payment', this);
    }
    /**
     * Build subscriptions for the service
     * @param fbUser: firebase logged in user
     */
    buildSubscriptions() {
        console.log('building subscriptions for account-payment.service');
        const subscriptions = [];
        subscriptions.push(this.firebaseAccountPaymentService.paymentMethodsList.subscribe((methods) => this.paymentMethodsSubscription.next(methods)));
        return subscriptions;
    }
    /**
     * Clear subscriptions data
     */
    onLoggedOut() {
        this.paymentMethodsSubscription.next(undefined);
        this.paymentHistorySubscription.next({ payments: [], totalItems: 0 });
        this.paymentHistoryErrorsSubscription.next(undefined);
    }
    /**
     * Get payment history data filtered from backend (not firebase)
     * @param mdn : phone number of the plan
     * @param orderId : order id of the plan
     * @param limit : number of records to get
     * @param page : page number to get
     * @param sortByField : field to sort data by,its value will be one of the PAYMENT_SORT_FIELDS
     * @param sortDirection : sort data 'asc' or 'desc'
     */
    reloadPaymentHistory(mdn, orderId, limit, page, sortByField, sortDirection) {
        return this.getBackendPaymentHistory(mdn, orderId, limit, page, sortByField, sortDirection);
    }
    getPaymentHistorySubscription(mdn, orderId, limit, page, sortByField, sortDirection) {
        if (!!mdn || !!orderId) {
            this.getBackendPaymentHistory(mdn, orderId, limit, page, sortByField, sortDirection).then((history) => {
                if (!!history.payments) {
                    this.paymentHistorySubscription.next(history);
                    this.paymentHistoryErrorsSubscription.next(undefined);
                }
                else {
                    this.paymentHistorySubscription.next({ payments: [], totalItems: 0 });
                    this.paymentHistoryErrorsSubscription.next(undefined);
                }
            }, (error) => {
                const errorObject = Object.assign({}, error, { datetime: new Date(), id: ++this.counter });
                this.paymentHistoryErrorsSubscription.next(errorObject);
                this.paymentHistorySubscription.next({ payments: [], totalItems: 0 });
            });
        }
        else {
            console.warn('No mdn or orderId provided');
            this.paymentHistoryErrorsSubscription.next(undefined);
            this.paymentHistorySubscription.next({ payments: [], totalItems: 0 });
        }
    }
    /**
     * Get payment method details by id from FIREBASE
     * @param paymentMethodId : id of payment method
     */
    getPaymentMethod(paymentMethodId) {
        return new Promise((resolve, reject) => {
            this.paymentMethodsList.pipe(take(1)).subscribe((methods) => resolve(methods.find((method) => method.id == paymentMethodId)), (error) => reject(error));
        });
    }
    /**
     * Map payment info data from firebase, user data and mdn into IPaymentInfo object
     * @param paymentMethod : payment method info from firebase
     * @param user : user data
     * @param mdn : phone number of the plan
     */
    getPaymentInfoFromMethod(paymentMethod, user, mdn) {
        return new Promise((resolve, reject) => {
            resolve({
                mdn,
                id: paymentMethod.id,
                fullName: user.fullName,
                email: user.email,
                type: 'creditCardProfile',
                primary: false,
                address1: paymentMethod.address1,
                address2: paymentMethod.address2,
                state: paymentMethod.state,
                country: paymentMethod.country,
                city: paymentMethod.city,
                postalCode: paymentMethod.postalCode
            });
        });
    }
    /**
     * Add new payment method and save it in the BACKEND
     * @param paymentInfo : credit card info
     * @returns {Promise<string>} profile key of payment
     */
    addPaymentMethod(paymentInfo, recaptchaResponse, allowDuplicate, action, invisibleRecaptcha) {
        return new Promise((resolve, reject) => {
            this.addBackendPaymentMethod(paymentInfo, recaptchaResponse, allowDuplicate, action, invisibleRecaptcha)
                .then((basicInfo) => {
                resolve(basicInfo.id);
            }, (error) => reject(error));
        });
    }
    /**
     * Updates ONLY the address of the payment method info
     * @param existPaymentMethodId : current payment method to be updated
     * @param newPaymentInfo : updated info
     */
    editPaymentMethod(existPaymentMethodId, newPaymentInfo, recaptchaResponse, action, invisibleRecaptcha) {
        newPaymentInfo.address1 = AccountPaymentService.shortenAddress(newPaymentInfo.address1);
        newPaymentInfo.address2 = !!newPaymentInfo?.address2 && newPaymentInfo?.address2?.length > 0 ? `#${newPaymentInfo.address2}` : '';
        return this.editBackendPaymentMethod(existPaymentMethodId, newPaymentInfo, recaptchaResponse, action, invisibleRecaptcha);
    }
    /**
     * Get payment method info from credit card info
     * @param paymentInfo: credit card details
     */
    getExistPaymentMethod(paymentInfo) {
        return new Promise((resolve, reject) => {
            const last4 = paymentInfo.cardNumber.replace(' ', '').substr(paymentInfo.cardNumber.length - 5);
            this.paymentMethodsList.pipe(take(1)).subscribe((methods) => {
                const storedMethod = methods.find((method) => method.last4 == parseInt(last4, 10) && method.expirationDate == paymentInfo.expirationDate);
                if (!!storedMethod) {
                    resolve(storedMethod);
                }
                else {
                    resolve(undefined);
                }
            }, (error) => reject(error));
        });
    }
    removePaymentMethod(paymentMethodId) {
        this.firebaseAccountPaymentService.removePaymentMethod(paymentMethodId);
    }
    setNewPaymentMethodAsDefault(paymentInfo, userPlanId) {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.endpointUrl}/api/payment/creditCard/new/plan/${userPlanId}/setDefault`, paymentInfo)
                .pipe(take(1)).subscribe((result) => resolve(), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    setPaymentMethodAsDefault(paymentMethodId, userPlanId) {
        return new Promise((resolve, reject) => {
            this.http.put(`${this.endpointUrl}/api/payment/creditCard/${paymentMethodId}/plan/${userPlanId}/setDefault`, {})
                .pipe(take(1)).subscribe((result) => resolve(), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    /**
     * a method to set or unset a user payment as default without plan id
     * @param paymentMethodId id of payment method to set or unset as default
     * @param setDefault : flag to set or unset as default
     */
    setDefaultPaymentMethod(paymentMethodId, setDefault) {
        return new Promise((resolve, reject) => {
            this.http.patch(`${this.endpointUrl}/api/payment/creditCard/${paymentMethodId}/defaultCreditCard?defaultCreditCard=${setDefault}`, {})
                .pipe(take(1)).subscribe((result) => resolve(), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    isDefaultPaymentMethod(paymentMethodId) {
        return new Promise((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/payment/creditCard/${paymentMethodId}/defaultCreditCard`)
                .pipe(take(1)).subscribe((store) => resolve(store), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    unsetPaymentMethodAsDefault(paymentMethodId, userPlanId) {
        return new Promise((resolve, reject) => {
            if (!!paymentMethodId) {
                this.http.delete(`${this.endpointUrl}/api/payment/creditCard/${paymentMethodId}/plan/${userPlanId}/unsetDefault`, {})
                    .pipe(take(1)).subscribe((result) => resolve(), (error) => this.http.handlePromiseError(error, reject));
            }
            else {
                reject({ message: 'Payment method is missing, you maybe already canceled it?' });
            }
        });
    }
    updateAutoRenewPlan(userPlanId, paymentMethodId, autoRenewFlag) {
        return new Promise((resolve, reject) => {
            this.http.patch(`${this.endpointUrl}/api/payment/creditCard/plan/${userPlanId}`, { paymentMethodKey: paymentMethodId, primary: autoRenewFlag }).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    checkCreditCardExistance(cardInfo) {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.endpointUrl}/api/payment/creditCard/validate`, cardInfo).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    makeAccountPayment() {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.endpointUrl}/api/account/payments`, {}).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    getPayments(limit, page, sortBy, sortMethod) {
        return new Promise((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/account/payments`, { limit, page, sort: sortBy + ':' + sortMethod }).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    deletePaymentMethod(methodId) {
        return new Promise((resolve, reject) => {
            this.http.delete(`${this.endpointUrl}/api/payment/creditCard/${methodId}`, {}).pipe(take(1))
                .subscribe(() => resolve(), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    sendReceiptEmail(orderId, emailInfo) {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.endpointUrl}/api/payment/${orderId}/email`, emailInfo)
                .pipe(take(1)).subscribe((result) => resolve(), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    getReceiptPaymentDetails(orderId) {
        return new Promise((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/payment/${orderId}/details`).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    getBackendPaymentHistory(mdn, orderId, limit, page, sortBy, sortMethod) {
        if (!!mdn) {
            return new Promise((resolve, reject) => {
                this.http.get(`${this.endpointUrl}/api/account/${mdn}/payments`, { limit, page, sort: sortBy + ':' + sortMethod })
                    .pipe(take(1))
                    .subscribe((history) => resolve(history), (error) => this.http.handlePromiseError(error, reject));
            });
        }
        else {
            return new Promise((resolve, reject) => {
                this.http.get(`${this.endpointUrl}/api/payment/${orderId}/history`, { limit, page, sort: sortBy + ':' + sortMethod })
                    .pipe(take(1))
                    .subscribe((history) => resolve(history), (error) => this.http.handlePromiseError(error, reject));
            });
        }
    }
    addBackendPaymentMethod(paymentData, recaptchaResponse, allowDuplicate, action, invisibleRecaptcha) {
        let header = new HttpHeaders()
            .set('reCaptcha', recaptchaResponse);
        if (!!action) {
            header = header.set('action', action);
        }
        header = header.set('invisibleRecaptcha', `${!!invisibleRecaptcha}`);
        let url = `${this.endpointUrl}/api/payment/creditCard`;
        if (!!allowDuplicate) {
            url = `${this.endpointUrl}/api/payment/creditCard?allowDuplicate=true`;
        }
        return new Promise((resolve, reject) => {
            this.http.postWithHeader(`${url}`, paymentData, header)
                .pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    editBackendPaymentMethod(existPaymentMethodId, paymentData, recaptchaResponse, action, invisibleRecaptcha) {
        let header = new HttpHeaders()
            .set('reCaptcha', recaptchaResponse);
        if (!!action) {
            header = header.set('action', action);
        }
        header = header.set('invisibleRecaptcha', `${!!invisibleRecaptcha}`);
        return new Promise((resolve, reject) => {
            this.http.put(`${this.endpointUrl}/api/payment/creditCard/${existPaymentMethodId}`, paymentData, header)
                .pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    getBackendPaymentMethod(id) {
        return new Promise((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/payment/creditCard/${id}`)
                .pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AccountPaymentService, deps: [{ token: EndpointUrl }, { token: i1.AuthHttp }, { token: i1.SimpleAuthService }, { token: FirebaseAccountPaymentService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AccountPaymentService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AccountPaymentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [EndpointUrl]
                }] }, { type: i1.AuthHttp }, { type: i1.SimpleAuthService }, { type: FirebaseAccountPaymentService }] });

class AccountRefillService {
    constructor(endpointUrl, http) {
        this.endpointUrl = endpointUrl;
        this.http = http;
    }
    /**
     * a method used when the user wants to refill account balance using voucher
     * @param userPlanId : selected plan id that user wants to refill its account balance
     * @param voucherCode : code of the used voucher
     */
    refillUsingVoucherCode(userPlanId, voucherCode) {
        return new Promise((resolve, reject) => {
            this.http.put(`${this.endpointUrl}/api/account/${userPlanId}/refill/voucher/${voucherCode}`, {}).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    /**
     * a method used when the user wants to refill his account balance using an existing credit card
     * @param {string} userPlanId: selected plan id that user wants to refill its account balance
     * @param {string} paymentInfoKey: id of the selected credit card
     * @param {IRefillPlanInfo} refillInfo: refill info(autorenew + amount) (if amount not sent it will try to refill plan with the price of plan by bff)
     * @returns {Promise<IRefillPlanResult>}
     */
    refillUsingExistingCreditCard(userPlanId, paymentInfoKey, refillInfo) {
        return new Promise((resolve, reject) => {
            this.http.put(`${this.endpointUrl}/api/account/${userPlanId}/refill/creditCard/${paymentInfoKey}`, refillInfo).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    /**
     * a method used when the user wants to refill his account using new credit card
     * @param userPlanId: selected plan id that user wants to refill its account balance
     * @param creditCardInfo: new credit card info the user wants to use
     */
    refillUsingNewCreditCard(userPlanId, creditCardInfo) {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.endpointUrl}/api/account/${userPlanId}/refill/creditCard`, creditCardInfo).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    /**
     * a method used when the user wants to refill account balance using voucher
     * @param mdn : mdn that user wants to refill its account balance
     * @param voucherCode : code of the used voucher
     */
    refillUsingMobileNumber(mdn, voucherCode, recaptchaResponse, action, invisibleRecaptcha) {
        let header = new HttpHeaders()
            .set('reCaptcha', recaptchaResponse);
        if (!!action) {
            header = header.set('action', action);
        }
        header = header.set('invisibleRecaptcha', `${!!invisibleRecaptcha}`);
        return new Promise((resolve, reject) => {
            this.http.put(`${this.endpointUrl}/api/account/${mdn}/refill/dealer/voucher/${voucherCode}`, {}, header).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    /**
     * a method used when the user wants to refill his account regardless of payment method type (Voucher, existing CC or new CC)
     * @param userPlanId: selected plan id that user wants to refill its account balance
     * @param refillInfo: refill info the user wants to use
     */
    refillUserPlan(userPlanId, refillInfo) {
        return new Promise((resolve, reject) => {
            this.http.put(`${this.endpointUrl}/api/account/${userPlanId}/refill`, refillInfo).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AccountRefillService, deps: [{ token: EndpointUrl }, { token: i1.AuthHttp }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AccountRefillService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AccountRefillService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [EndpointUrl]
                }] }, { type: i1.AuthHttp }] });

const USAGE_SORT_FIELDS = {
    TYPE: 'type',
    DATETIME: 'dateTime',
    AMOUNT: 'amount',
    DIRECTION: 'direction'
};
const USAGE_SORT_DIRECTION = {
    ASC: 'asc',
    DESC: 'desc',
};
/**
 * implementation of the IAccountUsageService interface
 * @property usageHistory: observable of user's usage history to subscribe on it
 * @property usageHistoryErrors: observable of errors of usage history to subscribe on (for logging purposes)
 * @property usageHistorySubscription: reply object for the usage history observable
 * @property usageHistoryErrorsSubscription: reply object for the usage history errors observable
 * @property counter: counter of errors
 */
class AccountUsageService {
    constructor(endpointUrl, http, simpleAuthService) {
        this.endpointUrl = endpointUrl;
        this.http = http;
        this.simpleAuthService = simpleAuthService;
        this.usageHistorySubscription = new ReplaySubject(1);
        this.usageHistoryErrorsSubscription = new ReplaySubject(1);
        this.planHistorySubscription = new ReplaySubject(1);
        this.planHistoryErrorsSubscription = new ReplaySubject(1);
        this.counter = 0;
        this.usageHistory = this.usageHistorySubscription.asObservable();
        this.usageHistoryErrors = this.usageHistoryErrorsSubscription.asObservable();
        this.planHistory = this.planHistorySubscription.asObservable();
        this.planHistoryErrors = this.planHistoryErrorsSubscription.asObservable();
        this.simpleAuthService.registerDependentService('account-usage', this);
    }
    /**
     * a method to build all subscriptions in AccountUsageService
     * @param fbUser: firebase logged in user
     */
    buildSubscriptions() {
        console.log('building subscriptions for account-usage.service');
        return [];
    }
    /**
     * clear usage array when logging out
     */
    onLoggedOut() {
        this.usageHistorySubscription.next({ usage: [], totalItems: 0 });
    }
    /**
     * a method to get all the user's usage history based on the mdn
     * @param mdn : user's phone number to get its usage history
     * @param limit : number of items to get per request (for pagination)
     * @param page : page number to get
     * @param sortByField : id of the sort by field
     * @param sortDirection :id of sort direction (asc or desc)
     */
    refreshUsageHistory(mdn, limit, page, sortByField, sortDirection, notRefreshAfterError) {
        if (!!mdn) {
            this.getBackendUsageHistory(mdn, limit, page, sortByField, sortDirection).then((history) => {
                if (!!history.usage) {
                    this.usageHistorySubscription.next(history);
                }
                else {
                    this.usageHistorySubscription.next({ usage: [], totalItems: 0 });
                    console.log('subscribed to usage history');
                }
            }, (error) => {
                const errorObject = Object.assign({}, error, { datetime: new Date(), id: ++this.counter });
                this.usageHistoryErrorsSubscription.next(errorObject);
                if (!notRefreshAfterError) {
                    this.usageHistorySubscription.next({ usage: [], totalItems: 0 });
                }
            });
        }
        else {
            this.usageHistoryErrorsSubscription.next({ message: 'No mdn provided' });
            this.usageHistorySubscription.next({ usage: [], totalItems: 0 });
        }
    }
    /**
     *  a method to get all the user's plan history based on the mdn
     * @param mdn : user's phone number to get its usage history
     * @param limit : number of items to get per request (for pagination)
     * @param page : page number to get
     * @param sortByField : id of the sort by field
     * @param sortDirection :id of sort direction (asc or desc)
     */
    refreshPlanHistory(mdn, limit, page, sortByField, sortDirection, notRefreshAfterError) {
        if (!!mdn) {
            this.getBackendPlanHistory(mdn, limit, page, sortByField, sortDirection).then((history) => {
                if (!!history.plans) {
                    this.planHistorySubscription.next(history);
                }
                else {
                    this.planHistorySubscription.next({ plans: [], totalItems: 0 });
                    console.log('subscribed to plans history');
                }
            }, (error) => {
                const errorObject = Object.assign({}, error, { datetime: new Date(), id: ++this.counter });
                this.planHistoryErrorsSubscription.next(errorObject);
                if (!notRefreshAfterError) {
                    this.planHistorySubscription.next({ plans: [], totalItems: 0 });
                }
            });
        }
        else {
            this.planHistoryErrorsSubscription.next({ message: 'No mdn provided' });
            this.planHistorySubscription.next({ plans: [], totalItems: 0 });
        }
    }
    /**
     * a method to call bff getUsageHistory method
     * @param mdn : user's phone number to get its usage history
     * @param limit : number of items to get per request (for pagination)
     * @param page : page number to get
     * @param sortByField : id of the sort by field
     * @param sortDirection :id of sort direction (asc or desc)
     */
    getBackendUsageHistory(mdn, limit, page, sortBy, sortMethod) {
        return new Promise((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/account/${mdn}/usage`, { limit, page, sort: sortBy + ':' + sortMethod })
                .pipe(take(1))
                .subscribe((history) => resolve(history), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    /**
     * a method to call bff getPlanHistory method
     * @param mdn : user's phone number to get its usage history
     * @param limit : number of items to get per request (for pagination)
     * @param page : page number to get
     * @param sortByField : id of the sort by field
     * @param sortDirection :id of sort direction (asc or desc)
     */
    getBackendPlanHistory(mdn, limit, page, sortBy, sortMethod) {
        return new Promise((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/account/${mdn}/plans`, { limit, page, sort: sortBy + ':' + sortMethod })
                .pipe(take(1))
                .subscribe((history) => resolve(history), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AccountUsageService, deps: [{ token: EndpointUrl }, { token: i1.AuthHttp }, { token: i1.SimpleAuthService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AccountUsageService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AccountUsageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [EndpointUrl]
                }] }, { type: i1.AuthHttp }, { type: i1.SimpleAuthService }] });

/**
 * Represent purchase intent for google analytics
 */
const PURCHASE_INTENT = {
    NEW: 'New',
    CHANGE: 'Change',
    RENEWAL: 'Renewal',
    ADDON: 'Addon',
    REPLACEMENT: 'Replacement'
};
/**
 * Represents the possible cases for cart actions
 */
const CART_TYPES = {
    NEW_PLAN: 'new_plan',
    CHANGE_PLAN: 'change_plan',
    TOPUP_PLAN: 'topup_plan',
    PLAN_ITEMS: 'plan_items',
    MIGRATION: 'migration',
    MIGRATION_PLAN: 'migration_plan',
    GENERIC_CART: 'generic_cart'
};
/**
 * Represents the coupon values (for GA)
 */
const COUPONS = {
    AUTO_RENEW: 'AUTO_RENEW_DISCOUNT',
    FREE_SIM: 'FREE_SIM_CARD',
    INTRODUCTORY_OFFER: 'INTRODUCTORY_OFFER'
};
/**
 * Contains all plan details including minutes, data and messages available in each plan
 */
class MobilePlanDetails {
    constructor(minutes, messages, data, mms, extraData) {
        this.minutes = minutes;
        this.messages = messages;
        this.data = data;
        this.mms = mms;
        this.extraData = extraData;
    }
    /**
     * Checks if the plan has unlimited minutes and messages
     */
    isUnlimitedTalkAndText() {
        return this.minutes === 99999 && this.messages === 99999;
    }
    /**
     * Checks mixed plans (contains mms, minutes and messages)
     */
    isMixedPlan() {
        return this.mms > 0 && this.minutes > 0 && this.messages > 0;
    }
    /**
     * Adds new mobile plan (used for configuration not in the UI)
     * @param details :plan details
     */
    addMobilePlanDetails(details) {
        if (details === undefined) {
            return this;
        }
        else {
            return new MobilePlanDetails(this.add(this.minutes, (!!details && !!details.minutes ? details.minutes : 0)), this.add(this.messages, (!!details && !!details.messages ? details.messages : 0)), this.add(this.data, (!!details && !!details.data ? details.data : 0)), this.add(this.mms, (!!details && !!details.mms ? details.mms : 0)), this.add(this.extraData, (!!details && !!details.extraData ? details.extraData : 0)));
        }
    }
    add(a, b) {
        return (a + b > 99999) ? 99999 : a + b;
    }
}
/**
 * contains the plan or the product details required in the UI
 * you can't access or use this object directly, you need to extend it to set its values
 */
class MobilePlanItem {
    constructor(title) {
        this._quantity = 1;
        this._promoPrice = 0;
        this._promoMonths = 1;
        this._cycleDays = 30;
        this._title = title;
    }
    get id() {
        return this._id;
    }
    get title() {
        return this._title;
    }
    get subtitle() {
        return this._subtitle;
    }
    get type() {
        return this._type;
    }
    get price() {
        return this._price;
    }
    get parentId() {
        return this._parentId;
    }
    get virtual() {
        return this._virtual;
    }
    get buttonText() {
        return this._buttonText;
    }
    get extTitle() {
        return this._extTitle;
    }
    get details() {
        return null;
    }
    get data() {
        return this._data;
    }
    get description() {
        return this._description;
    }
    get promoMessage() {
        return this._promoMessage;
    }
    get quantity() {
        return this._quantity;
    }
    get isParent() {
        return this._virtual || this._parentId == undefined;
    }
    get promoCode() {
        return this._promoCode;
    }
    get promoPrice() {
        return this._promoPrice;
    }
    get promoMonths() {
        return this._promoMonths;
    }
    get cycleDays() {
        return this._cycleDays;
    }
    get subscriptionCycles() {
        return this._subscriptionCycles;
    }
    get promoMoPrice() {
        return this._promoMoPrice;
    }
    get category() {
        return this._category;
    }
    get annualSaving() {
        return this._annualSaving;
    }
    get isSpecialPromotion() {
        return this._isSpecialPromotion;
    }
    get specialPromotion() {
        return this._specialPromotion;
    }
    get availableForChange() {
        return this._availableForChange;
    }
    get promoAvailableForChange() {
        return this._promoAvailableForChange;
    }
    get isVacation() {
        return this._isVacation;
    }
    get multiLineDisount() {
        return this._multiLineDisount;
    }
    get ebb() {
        return this._ebb;
    }
    get subscriptionPrice() {
        return this._subscriptionPrice;
    }
    get subscriptionPromoPrice() {
        return this._subscriptionPromoPrice;
    }
    get hotspotData() {
        return this._hotspotData;
    }
    set uniquePlanIdentifier(uniquePlanIdentifier) {
        this._uniquePlanIdentifier = uniquePlanIdentifier;
    }
    get uniquePlanIdentifier() {
        return this._uniquePlanIdentifier;
    }
    get bundleId() {
        return this._bundleId;
    }
    get isGo2Plan() {
        return this._isGo2Plan;
    }
    get go2PlansAvailable() {
        return this._go2PlansAvailable;
    }
}
/**
 * this class is only used to set the values of the MobilePlanItem objects
 */
class GenericMobilePlanItem extends MobilePlanItem {
    constructor(id, title, subtitle, price, type, _details, parentId, virtual, buttonText, extTitle, description, promoMessage, promoCode, promoPrice, promoMonths, cycleDays, promoMoPrice, subscriptionCycles, category, annualSaving, isSpecialPromotion, specialPromotion, availableForChange, isVacation, promoAvailableForChange, multiLineDisount, ebb, subscriptionPrice, subscriptionPromoPrice, phones, data, hotspotData, uniquePlanIdentifier, bundleId, isGo2Plan, go2PlansAvailable) {
        super();
        this._details = _details;
        this._id = id;
        this._parentId = parentId;
        this._title = title;
        this._subtitle = subtitle;
        this._price = price;
        this._type = type;
        this._virtual = virtual;
        this._buttonText = buttonText;
        this._extTitle = extTitle;
        this._description = description;
        this._promoMessage = promoMessage;
        this._promoCode = promoCode;
        this._promoPrice = promoPrice;
        this._promoMonths = promoMonths;
        this._cycleDays = cycleDays;
        this._promoMoPrice = promoMoPrice;
        this._subscriptionCycles = subscriptionCycles;
        this._category = category;
        this._annualSaving = annualSaving;
        this._isSpecialPromotion = isSpecialPromotion;
        this._specialPromotion = specialPromotion;
        this._availableForChange = availableForChange;
        this._promoAvailableForChange = promoAvailableForChange;
        this._isVacation = isVacation;
        this._multiLineDisount = multiLineDisount;
        this._ebb = ebb;
        this._subscriptionPrice = subscriptionPrice;
        this._subscriptionPromoPrice = subscriptionPromoPrice;
        this._data = data;
        this._hotspotData = hotspotData;
        this._uniquePlanIdentifier = uniquePlanIdentifier;
        this._bundleId = bundleId;
        this._isGo2Plan = isGo2Plan;
        this._go2PlansAvailable = go2PlansAvailable;
    }
    get details() {
        return this._details;
    }
    get isParent() {
        return this._virtual || this._parentId == undefined;
    }
}
/**
 * for configuring plan addons (not used directly by UI)
 */
class OptionsMobilePlanItem extends MobilePlanItem {
    constructor(title) {
        super(title);
        this._optionsDict = {};
    }
    get id() {
        return this.getSelectedOption().id;
    }
    get price() {
        return this.getSelectedOption().price;
    }
    get title() {
        return this.getSelectedOption().title;
    }
    get type() {
        return this.getSelectedOption().type;
    }
    get description() {
        return this.getSelectedOption().description;
    }
    get options() {
        return Object.keys(this._optionsDict).map((key) => {
            return this._optionsDict[key];
        });
    }
    get details() {
        return this.selectedOption.details;
    }
    addOption(option) {
        this._optionsDict[option.id] = option;
    }
    selectOption(option) {
        this.selectedOption = option;
    }
    getSelectedOption() {
        return this.selectedOption;
    }
}
/**
 * All objects that will be in user cart should be created with CustomizableMobilePlan type
 * (any object will be handled with the checkout service for g2g or mobile plan service must be in this type)
 * each object is connected with the user using user id
 * every cart in the session is saved in firebase by default for anonymous users, once the user looged in the cart will be copied
 * and connected to the user id.
 * cart = CustomizableMobilePlan
 * @property _basePlan: plan(or the product) the user wants to buy.
 * @property _planDevice: device linked to the user cart after passing imei or imed check. (user already has a device and I need only to link it with the user account.)
 * @property _planExpectedDevice: the device that the user selectes from the phone shop of the website (currently available in hero and g2g only otherwise it is setted as null)
 * @property _taxes: its value is read from the configuration and is added to the cart based on the user address(in which state he lives)
 * @property _fees: its value is read from the configuration and represents the sales tax in each U.S state
 * @property _activationCode: optional parameter(can be null) represents the activation code for the user's mobile number. its value is entered by the user
 * @property _voucherData: voucher details entered by the user
 * @property _simsQuantity: number of sim cards the user requests (most websites gives one sim free)
 * @property _userId : id of logged in user or anonymous user
 * @property _autoRenewPlan: flag to specify if the user wants to auto renew plan (only for new plans. no renew available for addons)
 * @property _locked : flag to lock firebase object while syncing data
 * @property _addOns: array of addons the user wants to buy
 * @property _finaningDetails: details of futurePay order
 */
class CustomizableMobilePlan extends MobilePlanItem {
    get userId() {
        return this._userId;
    }
    set userId(value) {
        this._userId = value;
    }
    get marketingObject() {
        return this._marketingObject;
    }
    set marketingObject(value) {
        this._marketingObject = value;
    }
    get basePlan() {
        return this._basePlan;
    }
    set basePlan(plan) {
        this._basePlan = plan;
    }
    get hasPlanItem() {
        return !!this.basePlan && (!this.basePlan.virtual || this.simsQuantity > 0);
    }
    get planDevice() {
        return this._planDevice;
    }
    set planDevice(value) {
        this._planDevice = value;
    }
    get planExpectedDevice() {
        return this._planExpectedDevice;
    }
    set planExpectedDevice(value) {
        this._planExpectedDevice = value;
    }
    get taxes() {
        return this._taxes;
    }
    set taxes(value) {
        this._taxes = value;
    }
    get fees() {
        return this._fees;
    }
    set fees(value) {
        this._fees = value;
    }
    get activationCode() {
        return this._activationCode;
    }
    set activationCode(value) {
        this._activationCode = value;
    }
    get voucherData() {
        return this._voucherData;
    }
    set voucherData(value) {
        this._voucherData = value;
    }
    get simsQuantity() {
        return !!this._simsQuantity ? this._simsQuantity : 0;
    }
    set simsQuantity(value) {
        this._simsQuantity = value;
    }
    get id() {
        return this._basePlan.id;
    }
    get title() {
        return this._basePlan.title;
    }
    get autoRenewPlan() {
        return this._autoRenewPlan;
    }
    set autoRenewPlan(value) {
        this._autoRenewPlan = value;
    }
    get locked() {
        return this._locked;
    }
    set locked(value) {
        this._locked = value;
    }
    get price() {
        if (!!this._basePlan) {
            // update price
            this._price = this._basePlan.price;
            return this._price;
        }
        return 0;
    }
    get dueNow() {
        if (!!this.voucherData) {
            return (this.price - this.voucherData.limit) >= 0 ? this.price - this.voucherData.limit : 0;
        }
        else {
            return this.price + this.simsQuantity * 10;
        }
    }
    get remainingBalance() {
        if (!!this.voucherData) {
            return (this.voucherData.limit - this.price) >= 0 ? this.voucherData.limit - this.price : 0;
        }
        else {
            return 0;
        }
    }
    get type() {
        return 'custom-mobile-plan';
    }
    get details() {
        return new MobilePlanDetails(0, 0, 0, 0, 0).addMobilePlanDetails(this.basePlan.details);
    }
    get taxesAndFees() {
        if (!!this.voucherData) {
            return 0;
        }
        if (this.simsQuantity > 0 && this.basePlan.id == 'NO-PLAN') {
            return this._taxes;
        }
        else {
            return this._taxes + this._fees;
        }
    }
    get isParent() {
        return this._virtual || this._parentId == undefined;
    }
    get addOns() {
        return this._addOns;
    }
    set addOns(value) {
        this._addOns = value;
    }
    get mdn() {
        return this._mdn;
    }
    set mdn(value) {
        this._mdn = value;
    }
    get cartType() {
        return this._cartType;
    }
    set cartType(value) {
        this._cartType = value;
    }
    get activePlanId() {
        return this._activePlanId;
    }
    set activePlanId(value) {
        this._activePlanId = value;
    }
    get promoCodes() {
        return this._promoCodes;
    }
    set promoCodes(value) {
        this._promoCodes = value;
    }
    get referralCode() {
        return this._referralCode;
    }
    set referralCode(value) {
        this._referralCode = value;
    }
    get phoneNumber() {
        return this._phoneNumber;
    }
    set phoneNumber(value) {
        this._phoneNumber = value;
    }
    get pin() {
        return this._pin;
    }
    set pin(value) {
        this._pin = value;
    }
    get phones() {
        return this._phones;
    }
    set phones(value) {
        this._phones = value;
    }
    get eSIM() {
        return this._eSIM;
    }
    set eSIM(value) {
        this._eSIM = value;
    }
    get qrScanned() {
        return this._qrScanned;
    }
    set qrScanned(value) {
        this._qrScanned = value;
    }
    get financingDetails() {
        return this._financingDetails;
    }
    set financingDetails(value) {
        this._financingDetails = value;
    }
    get acpDevice() {
        return this._acpDevice;
    }
    set acpDevice(value) {
        this._acpDevice = value;
    }
    get storePickup() {
        return this._storePickup;
    }
    set storePickup(value) {
        this._storePickup = value;
    }
    get agentCode() {
        return this._agentCode;
    }
    set agentCode(value) {
        this._agentCode = value;
    }
    get catalogArea() {
        return this._catalogArea;
    }
    set catalogArea(value) {
        this._catalogArea = value;
    }
    get go2Plan() {
        return this._go2Plan;
    }
    set go2Plan(plan) {
        this._go2Plan = plan;
    }
    get go2SIM() {
        return this._go2SIM;
    }
    set go2SIM(sim) {
        this._go2SIM = sim;
    }
}

class ActionsAnalyticsService {
    constructor(gtmEventsConfig, simpleAuthService) {
        this.simpleAuthService = simpleAuthService;
        this.simpleAuthService.registerDependentService('actions-analytics-service', this);
        this.gtmEvents = gtmEventsConfig;
    }
    buildSubscriptions(fbUser) {
        this.fbUser = fbUser;
        return [
        // this.mobilePlansService.simCardConfig.subscribe((simConfig) => this.simConfig = simConfig)
        ];
    }
    onLoggedOut() {
        // this.fbUser = undefined;
    }
    trackPageView(navEnd) {
        const event = 'Page View';
        this.pushToDataLayer({ event, 'content-name': navEnd.url, 'userId': !!this.fbUser && !this.fbUser.isAnonymous ? this.fbUser.uid : undefined });
    }
    /**
     * Track user logins and signups
     * @param action login or signup
     * @param id user id
     */
    trackUserAuthentication(userAction, id, eventLabel) {
        this.pushToDataLayer({ event: userAction, userId: !!id ? id : undefined, category: 'Authentication', action: userAction, label: eventLabel });
    }
    trackUserId(id) {
        this.pushToDataLayer({ userId: id });
    }
    /**
     * https://developers.google.com/tag-manager/enhanced-ecommerce#purchase
     * @param {string} transactionId
     * @param {CustomizableMobilePlan} currentPlan
     * @param {number} shippingCost
     */
    trackSucceededCheckout(transactionId, currentPlan, shippingCost, orderTaxes, orderFees, includeAutoRenew, includeFreeSIM, simDetails, usePromoPrice, mgtNewPlan) {
        const event = 'purchase';
        const products = [currentPlan];
        let discountValue;
        if (!!currentPlan.basePlan.specialPromotion && !!currentPlan.basePlan.specialPromotion.promotionDiscount) {
            const discountAmount = currentPlan.basePlan.specialPromotion.promotionDiscount.split('%')[0];
            const discountAmountValue = parseInt(discountAmount, 10);
            discountValue = !!includeAutoRenew ? ((currentPlan.basePlan.price - currentPlan.basePlan.promoPrice) * (discountAmountValue / 100)) : currentPlan.basePlan.price * (discountAmountValue / 100);
        }
        let coupon = '';
        coupon = !!includeAutoRenew ? COUPONS.AUTO_RENEW : '';
        if (!!usePromoPrice && currentPlan.basePlan.subscriptionCycles == 3) {
            coupon = !!includeAutoRenew ? coupon + '|' + COUPONS.INTRODUCTORY_OFFER : coupon;
        }
        if (!!currentPlan.basePlan.specialPromotion) {
            coupon = coupon.length > 0 ? coupon + '|' + currentPlan.basePlan.specialPromotion.code : coupon;
        }
        this.trackSucceededCheckoutEnhancedGA4(transactionId, products, {
            fees: orderFees,
            price: !!includeAutoRenew ? currentPlan.price - currentPlan.basePlan.promoPrice : currentPlan.price,
            taxes: orderTaxes,
            coupon: coupon,
            discount: !!discountValue ? discountValue : 0,
            voucherCode: !!currentPlan.voucherData ? currentPlan.voucherData.code : null
        }, shippingCost, includeFreeSIM, simDetails, usePromoPrice, mgtNewPlan, !!currentPlan.marketingObject ? currentPlan.marketingObject : null, !!currentPlan?.phones ? currentPlan?.phones : null);
    }
    /**
     * https://developers.google.com/tag-manager/enhanced-ecommerce#purchase
     * @param {string} transactionId
     * @param {IProductItem[]} products
     * @param {ICalculatedTaxesAndFees} cost
     * @param {number} shippingCost
     */
    trackItemSucceededPurchase(transactionId, cart, orderTaxes, orderFees, shippingCost, simPrice, simId) {
        const event = 'purchase';
        const transactionProducts = [];
        let purchaseCost = 0;
        if (cart.addOns && cart.addOns.length > 0) {
            for (const product of cart.addOns) {
                purchaseCost = purchaseCost + (product.price * product.quantity);
                transactionProducts.push({
                    id: product.id, // string - product sku (required)
                    name: product.title, // string - product name (required)
                    category: product.type, // string - product category
                    price: product.price, // numeric - product price (required)
                    quantity: product.quantity, // numeric – qty (required)
                    intent: PURCHASE_INTENT.ADDON, // string - purchase type
                    dimension1: PURCHASE_INTENT.ADDON
                });
            }
        }
        else { //SIM replacement purchase
            transactionProducts.push({
                id: simId, // string - product sku (required)
                name: 'SIM CARD', // string - product name (required)
                category: 'REPLACEMENT', // string - product category
                price: simPrice, // numeric - product price (required)
                quantity: cart.simsQuantity, // numeric – qty (required)
                intent: PURCHASE_INTENT.REPLACEMENT, // string - purchase type
                dimension1: PURCHASE_INTENT.REPLACEMENT
            });
            purchaseCost = simPrice * cart.simsQuantity;
        }
        const transactionTax = orderFees + orderTaxes;
        const transactionTotal = transactionTax + purchaseCost + shippingCost;
        const layerToPush = {
            event,
            ecommerce: {
                purchase: {
                    actionField: {
                        id: transactionId,
                        revenue: transactionTotal,
                        tax: transactionTax,
                        shipping: shippingCost,
                    },
                    // PRODUCTS INFORMATION
                    products: transactionProducts
                }
            }
        };
        this.pushToDataLayer(layerToPush);
    }
    /**
     * https://developers.google.com/tag-manager/enhanced-ecommerce#purchase
     * @param {string} transactionId
     * @param {IProductItem[] | CustomizableMobilePlan []} products
     * @param {ICalculatedTaxesAndFees} cost
     * @param {number} shippingCost
     */
    trackSucceededCheckoutEnhancedGA4(transactionId, products, cost, shippingCost, includeFreeSIM, simDetails, usePromoMoPrice, mgtNewPlan, marketingInfo, phones) {
        let event = 'purchase';
        if (!!mgtNewPlan) {
            event = 'place_your_order';
        }
        const transactionProducts = [];
        for (const product of products) {
            const dataDetails = {
                item_id: product.basePlan.id, // string - product sku (required)
                item_name: product.basePlan.id, // string - product name (required)
                item_category: product.basePlan.type, // string - product category
                price: !!usePromoMoPrice ? product.basePlan.promoMoPrice * product.basePlan.subscriptionCycles :
                    (cost.coupon == COUPONS.AUTO_RENEW ? product.basePlan.price - product.basePlan.promoPrice : product.basePlan.price), // numeric - product price (required)
                quantity: product.basePlan.quantity, // numeric – qty (required)
                intent: product.cartType, // string - cart type (new or change plan order)
                autoRenew: product.autoRenewPlan, //plan auto renewal
                dimension1: product.cartType, //purchase type
                coupon: cost.coupon, //product discount
                currency: 'USD',
                discount: cost.discount
            };
            if (!!product.basePlan.subscriptionCycles) {
                dataDetails.dimension2 = product.basePlan.subscriptionCycles;
                dataDetails.category = 'subscription-plan';
                if (!!usePromoMoPrice) {
                    cost.price = product.basePlan.subscriptionCycles * product.basePlan.promoMoPrice;
                }
                else {
                    cost.price = product.basePlan.subscriptionCycles * product.basePlan.price;
                }
            }
            if (product.cartType == PURCHASE_INTENT.NEW) {
                dataDetails.dimension2 = !!product.planDevice && product.planDevice.manufacturer ? product.planDevice.manufacturer : '-'; //device manufacturer
            }
            transactionProducts.push(dataDetails);
            if (!!includeFreeSIM) {
                transactionProducts.push({
                    item_id: simDetails.id,
                    item_name: 'FREE SIM CARD',
                    item_category: 'SIM',
                    price: 0,
                    quantity: 1,
                    coupon: COUPONS.FREE_SIM,
                    dimension3: !!product.planDevice && product.planDevice.skuIdentifier ? product.planDevice.skuIdentifier : '-' //device sku identifier
                });
            }
            if (!!product.phones) {
                for (const phone of product.phones) {
                    const dataDetails = {
                        item_id: phone.sku, // string - product sku (required)
                        item_name: phone.name, // string - product name (required)
                        item_category: phone.type, // string - product category
                        price: phone.price, // numeric - product price (required)
                        quantity: phone.quantity, // numeric – qty (required)
                        intent: phone.cartType, // string - cart type (new)
                        dimension1: phone.cartType, //purchase type
                        coupon: cost.coupon, //product discount
                        currency: 'USD',
                        discount: cost.discount
                    };
                    transactionProducts.push(dataDetails);
                    cost.price = cost.price + phone.price;
                }
            }
        }
        const transactionTax = cost.taxes + (cost.fees || 0);
        const transactionTotal = transactionTax + cost.price + shippingCost - cost.discount;
        const layerToPush = {
            event,
            ecommerce: {
                transaction_id: transactionId,
                revenue: transactionTotal,
                tax: transactionTax,
                shipping: shippingCost,
                items: transactionProducts,
                marketingDetails: marketingInfo
            }
        };
        if (!marketingInfo) {
            delete layerToPush.ecommerce.marketingDetails;
        }
        this.pushToDataLayer(layerToPush);
    }
    /**
     * A method to track the add to cart event in google analytics
     * @param intent goal of purchase (new purchase, renewal, change)
     * @param products items added to cart
     */
    trackAddToCart(intent, products, calculatedPromoPrice) {
        const transactionProducts = [];
        for (const product of products) {
            transactionProducts.push({
                id: product.id, // string - product sku (required)
                name: product.id, // string - product name (required)
                category: product.type, // string - product category
                price: !!calculatedPromoPrice ? calculatedPromoPrice : product.price, // numeric - product price (required)
                quantity: product.quantity, // numeric – qty (required)
                userId: !!this.fbUser && !this.fbUser.isAnonymous ? this.fbUser.uid : undefined, //user Id
                dimension1: intent,
            });
        }
        const layerToPush = {
            event: 'addToCart',
            ecommerce: {
                currencyCode: 'USD',
                add: {
                    products: transactionProducts
                }
            }
        };
        this.pushToDataLayer(layerToPush);
    }
    trackAddToCartGA4(intent, products, calculatedPromoPrice) {
        const transactionProducts = [];
        for (const product of products) {
            transactionProducts.push({
                item_id: product.id, // string - product sku (required)
                item_name: !!product.title ? product.title : !!product.name ? product.name : product.id, // string - product name (required)
                item_category: product.type, // string - product category
                price: !!calculatedPromoPrice ? calculatedPromoPrice : product.price, // numeric - product price (required)
                quantity: product.quantity, // numeric – qty (required)
                userId: !!this.fbUser && !this.fbUser.isAnonymous ? this.fbUser.uid : undefined, //user Id
                dimension1: intent,
            });
        }
        const layerToPush = {
            event: 'add_to_cart',
            ecommerce: {
                items: transactionProducts
            }
        };
        this.pushToDataLayer(layerToPush);
    }
    trackProductDetails(product) {
        const transactionProducts = [];
        transactionProducts.push({
            id: product.id, // string - product sku (required)
            name: product.id, // string - product name (required)
            category: product.type, // string - product category
            price: product.price, // numeric - product price (required)
        });
        const layerToPush = {
            event: 'productDetails',
            ecommerce: {
                detail: {
                    actionField: { list: 'plans-list' },
                    products: transactionProducts
                }
            }
        };
        this.pushToDataLayer(layerToPush);
    }
    /**
     * A method to track the remove from cart event in google analytics
     * @param products items removed from cart
     */
    trackRermoveFromCart(products, type) {
        const transactionProducts = [];
        for (const product of products) {
            transactionProducts.push({
                id: product.id, // string - product sku (required)
                name: product.id, // string - product name (required)
                category: product.type, // string - product category
                price: product.price, // numeric - product price (required)
                quantity: product.quantity, // numeric – qty (required)
                userId: !!this.fbUser && !this.fbUser.isAnonymous ? this.fbUser.uid : undefined //logged in user Id
            });
        }
        const layerToPush = {
            event: 'removeFromCart',
            ecommerce: {
                remove: {
                    products: transactionProducts
                }
            }
        };
        this.pushToDataLayer(layerToPush);
    }
    trackRermoveFromCartGA4(products, type) {
        const transactionProducts = [];
        for (const product of products) {
            transactionProducts.push({
                item_id: product.id, // string - product sku (required)
                item_name: product.id, // string - product name (required)
                item_category: product.type, // string - product category
                price: product.price, // numeric - product price (required)
                quantity: product.quantity, // numeric – qty (required)
                userId: !!this.fbUser && !this.fbUser.isAnonymous ? this.fbUser.uid : undefined //logged in user Id
            });
        }
        const layerToPush = {
            event: 'remove_from_cart',
            ecommerce: {
                items: transactionProducts
            }
        };
        this.pushToDataLayer(layerToPush);
    }
    /**
     * track checkout steps
     * @param stepNumber checkout step number
     * @param stepOption extra option
     */
    trackCheckoutSteps(stepNumber, stepOption) {
        const layerToPush = {
            event: 'checkout',
            ecommerce: {
                checkout: {
                    actionField: { step: stepNumber, option: stepOption }
                }
            }
        };
        this.pushToDataLayer(layerToPush);
    }
    trackCheckoutOptionSteps(stepName, stepOption) {
        const layerToPush = {
            event: 'checkoutOption',
            ecommerce: {
                checkout_option: {
                    actionField: { step: stepName, option: stepOption },
                }
            }
        };
        this.pushToDataLayer(layerToPush);
    }
    trackBeginCheckoutGA4(cart, simPrice, simId) {
        this.clearEcommerceDataLayer();
        const transactionProducts = [];
        if (cart.addOns && cart.addOns.length > 0) {
            for (const product of cart.addOns) {
                transactionProducts.push({
                    item_id: product.id, // string - product sku (required)
                    item_name: product.id, // string - product name (required)
                    item_category: product.type, // string - product category
                    price: product.price, // numeric - product price (required)
                    quantity: product.quantity, // numeric – qty (required)
                    intent: PURCHASE_INTENT.ADDON, // string - purchase type
                    dimension1: PURCHASE_INTENT.ADDON
                });
            }
        }
        else if (cart.cartType === CART_TYPES.PLAN_ITEMS && cart.simsQuantity > 0) {
            transactionProducts.push({
                item_id: simId, // string - product sku (required)
                item_name: 'SIM CARD', // string - product name (required)
                item_category: 'REPLACEMENT', // string - product category
                price: simPrice, // numeric - product price (required)
                quantity: cart.simsQuantity, // numeric – qty (required)
                intent: PURCHASE_INTENT.REPLACEMENT, // string - purchase type
                dimension1: PURCHASE_INTENT.REPLACEMENT
            });
        }
        else { //purchase plan
            transactionProducts.push({
                item_id: cart.basePlan.id, // string - product sku (required)
                item_name: cart.basePlan.id, // string - product name (required)
                item_category: cart.basePlan.type, // string - product category
                price: cart.basePlan.price, // numeric - product price (required)
                quantity: cart.basePlan.quantity, // numeric – qty (required)
                intent: PURCHASE_INTENT.NEW, // string - purchase type
                dimension1: PURCHASE_INTENT.NEW
            });
        }
        const layerToPush = {
            event: 'begin_checkout',
            ecommerce: {
                items: transactionProducts
            }
        };
        this.pushToDataLayer(layerToPush);
    }
    trackAddProratedInfoGA4(cart, paymentType, simPrice, simId) {
        const transactionProducts = [];
        if (cart.addOns && cart.addOns.length > 0) {
            for (const product of cart.addOns) {
                transactionProducts.push({
                    item_id: product.id, // string - product sku (required)
                    item_name: product.id, // string - product name (required)
                    item_category: product.type, // string - product category
                    price: product.price, // numeric - product price (required)
                    quantity: product.quantity, // numeric – qty (required)
                    intent: PURCHASE_INTENT.ADDON, // string - purchase type
                    dimension1: PURCHASE_INTENT.ADDON,
                    currency: 'USD'
                });
            }
        }
        else if (cart.cartType === CART_TYPES.PLAN_ITEMS && cart.simsQuantity > 0) {
            transactionProducts.push({
                item_id: simId, // string - product sku (required)
                item_name: 'SIM CARD', // string - product name (required)
                item_category: 'REPLACEMENT', // string - product category
                price: simPrice, // numeric - product price (required)
                quantity: cart.simsQuantity, // numeric – qty (required)
                intent: PURCHASE_INTENT.REPLACEMENT, // string - purchase type
                dimension1: PURCHASE_INTENT.REPLACEMENT,
                currency: 'USD'
            });
        }
        else { //purchase plan
            transactionProducts.push({
                item_id: cart.basePlan.id, // string - product sku (required)
                item_name: cart.basePlan.id, // string - product name (required)
                item_category: cart.basePlan.type, // string - product category
                price: cart.basePlan.price, // numeric - product price (required)
                quantity: cart.basePlan.quantity, // numeric – qty (required)
                intent: PURCHASE_INTENT.NEW, // string - purchase type
                dimension1: PURCHASE_INTENT.NEW,
                currency: 'USD'
            });
        }
        const layerToPush = {
            event: 'add_prorated_payment',
            ecommerce: {
                items: transactionProducts,
                payment_type: paymentType
            }
        };
        this.pushToDataLayer(layerToPush);
    }
    trackAddPaymentInfoGA4(cart, paymentType, simPrice, simId) {
        const transactionProducts = [];
        if (cart.addOns && cart.addOns.length > 0) {
            for (const product of cart.addOns) {
                transactionProducts.push({
                    item_id: product.id, // string - product sku (required)
                    item_name: product.id, // string - product name (required)
                    item_category: product.type, // string - product category
                    price: product.price, // numeric - product price (required)
                    quantity: product.quantity, // numeric – qty (required)
                    intent: PURCHASE_INTENT.ADDON, // string - purchase type
                    dimension1: PURCHASE_INTENT.ADDON,
                    currency: 'USD'
                });
            }
        }
        else if (cart.cartType === CART_TYPES.PLAN_ITEMS && cart.simsQuantity > 0) {
            transactionProducts.push({
                item_id: simId, // string - product sku (required)
                item_name: 'SIM CARD', // string - product name (required)
                item_category: 'REPLACEMENT', // string - product category
                price: simPrice, // numeric - product price (required)
                quantity: cart.simsQuantity, // numeric – qty (required)
                intent: PURCHASE_INTENT.REPLACEMENT, // string - purchase type
                dimension1: PURCHASE_INTENT.REPLACEMENT,
                currency: 'USD'
            });
        }
        else { //purchase plan
            transactionProducts.push({
                item_id: cart.basePlan.id, // string - product sku (required)
                item_name: cart.basePlan.id, // string - product name (required)
                item_category: cart.basePlan.type, // string - product category
                price: cart.basePlan.price, // numeric - product price (required)
                quantity: cart.basePlan.quantity, // numeric – qty (required)
                intent: PURCHASE_INTENT.NEW, // string - purchase type
                dimension1: PURCHASE_INTENT.NEW,
                currency: 'USD'
            });
        }
        const layerToPush = {
            event: 'add_payment_info',
            ecommerce: {
                items: transactionProducts,
                payment_type: paymentType
            }
        };
        this.pushToDataLayer(layerToPush);
    }
    trackAddShippingInfoGA4(cart, shippingTier, simPrice, simId) {
        const transactionProducts = [];
        if (cart.addOns && cart.addOns.length > 0) {
            for (const product of cart.addOns) {
                transactionProducts.push({
                    item_id: product.id, // string - product sku (required)
                    item_name: product.id, // string - product name (required)
                    item_category: product.type, // string - product category
                    price: product.price, // numeric - product price (required)
                    quantity: product.quantity, // numeric – qty (required)
                    intent: PURCHASE_INTENT.ADDON, // string - purchase type
                    dimension1: PURCHASE_INTENT.ADDON
                });
            }
        }
        else if (cart.cartType === CART_TYPES.PLAN_ITEMS && cart.simsQuantity > 0) {
            transactionProducts.push({
                item_id: simId, // string - product sku (required)
                item_name: 'SIM CARD', // string - product name (required)
                item_category: 'REPLACEMENT', // string - product category
                price: simPrice, // numeric - product price (required)
                quantity: cart.simsQuantity, // numeric – qty (required)
                intent: PURCHASE_INTENT.REPLACEMENT, // string - purchase type
                dimension1: PURCHASE_INTENT.REPLACEMENT
            });
        }
        else { //purchase plan
            transactionProducts.push({
                item_id: cart.basePlan.id, // string - product sku (required)
                item_name: cart.basePlan.id, // string - product name (required)
                item_category: cart.basePlan.type, // string - product category
                price: cart.basePlan.price, // numeric - product price (required)
                quantity: cart.basePlan.quantity, // numeric – qty (required)
                intent: PURCHASE_INTENT.NEW, // string - purchase type
                dimension1: PURCHASE_INTENT.NEW
            });
        }
        const layerToPush = {
            event: 'add_shipping_info',
            ecommerce: {
                items: transactionProducts,
                shipping_tier: shippingTier
            }
        };
        this.pushToDataLayer(layerToPush);
    }
    clearEcommerceDataLayer() {
        const layerToPush = {
            ecommerce: null
        };
        this.pushToDataLayer(layerToPush);
    }
    trackLanguageSwitching(language) {
        const langTrigger = language === 'en' ? 'English_trigger' : 'Chinese_trigger';
        const layerToPush = {
            event: 'language-switch',
            lang_trigger: langTrigger
        };
        this.pushToDataLayer(layerToPush);
    }
    trackActivation(type, carrierType, simId, deviceName, postal, userPlanId, userPlan) {
        const layerToPush = {
            event: 'new_activation',
            category: 'Activation',
            action: type,
            label: 'Plan Activation',
            device: deviceName.toLowerCase(),
            postalCode: postal,
            carrier: carrierType,
            simNumber: simId,
            planId: userPlanId,
            plan: userPlan,
            userId: !!this.fbUser && !this.fbUser.isAnonymous ? this.fbUser.uid : undefined
        };
        this.pushToDataLayer(layerToPush);
    }
    trackPortIn(type, carrierType, simId, deviceName, postal, userPlanId, userPlan) {
        const layerToPush = {
            event: 'port_in',
            category: 'Activation',
            action: type,
            label: 'Plan Activation',
            device: deviceName.toLowerCase(),
            postalCode: postal,
            carrier: carrierType,
            simNumber: simId,
            planId: userPlanId,
            plan: userPlan,
            userId: !!this.fbUser && !this.fbUser.isAnonymous ? this.fbUser.uid : undefined
        };
        this.pushToDataLayer(layerToPush);
    }
    trackMoneySavingProClick(clickId) {
        const layerToPush = {
            event: 'msp_conversion',
            category: 'Marketing',
            msp_click_id: clickId,
            userId: !!this.fbUser && !this.fbUser.isAnonymous ? this.fbUser.uid : undefined
        };
        this.pushToDataLayer(layerToPush);
    }
    sendUserIdToHotjar(id) {
        const _hj = window.hj = window.hj || [];
        _hj('identify', id, { isLoggedIn: true });
    }
    trackProductsImpressions(plans, listPage) {
        const transactionProducts = [];
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < plans.length; i++) {
            transactionProducts.push({
                id: plans[i].id,
                name: plans[i].id,
                price: plans[i].price,
                category: plans[i].type,
                position: i + 1,
                list: listPage
            });
        }
        const layerToPush = {
            ecommerce: {
                currencyCode: 'USD',
                impressions: transactionProducts
            }
        };
        this.pushToDataLayer(layerToPush);
    }
    /**
     * A method to track the ACP events in google analytics
     * @param eventData event data details to be sent
     */
    trackACPEvent(eventData) {
        this.pushToDataLayer(eventData);
    }
    /**
   * A method to track vents in google analytics
   * @param eventData event data details to be sent
   */
    trackGAEvent(eventData) {
        this.pushToDataLayer(eventData);
    }
    pushToDataLayer(item) {
        const dataLayer = window.dataLayer = window.dataLayer || [];
        dataLayer.push(item);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ActionsAnalyticsService, deps: [{ token: GoogleTagManagerEventsConfig }, { token: i1.SimpleAuthService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ActionsAnalyticsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ActionsAnalyticsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [GoogleTagManagerEventsConfig]
                }] }, { type: i1.SimpleAuthService }] });

class PlansConfigurationService {
    constructor(angularFireDatabase) {
        this.angularFireDatabase = angularFireDatabase;
        this.planConfiguration = new ReplaySubject(1);
        this.angularFireDatabase.list(FirebaseDatabasePaths.PLANS_CONFIGURATION.PLANS).valueChanges()
            .pipe(combineLatest(this.angularFireDatabase.list(FirebaseDatabasePaths.PLANS_CONFIGURATION.PLAN_ADD_ONS).valueChanges(), (allPlans, planAddOns) => {
            const activePlans = allPlans.filter((plan) => !plan.archived);
            return { activePlans, plans: activePlans, planAddOns, allPlans };
        }))
            .subscribe((conf) => {
            this.planConfiguration.next(conf);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PlansConfigurationService, deps: [{ token: i1$1.AngularFireDatabase }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PlansConfigurationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PlansConfigurationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$1.AngularFireDatabase }] });

/**
 * Implementation of the IFirebaseAdsService
 * @property bannerSwiperSlides: observable for the list of the slides in the banner
 * @property socialSwiperSlides: observable for the list of the social slides
 * @property featuredPlans: observable for the featured plans available for the user
 * @property whyGood2GoSlides: observable for the list of why good2go slides
 */
class FirebaseAdsService {
    constructor(angularFireDatabase, plansConfigurationService) {
        this.angularFireDatabase = angularFireDatabase;
        this.plansConfigurationService = plansConfigurationService;
        this.bannerSwiperSlidesSubject = new ReplaySubject(1);
        this.socialSwiperSlidesSubject = new ReplaySubject(1);
        this.featuredPlansSubject = new ReplaySubject(1);
        this.whyGood2GoSubject = new ReplaySubject(1);
        this.bannerSwiperSlides = this.bannerSwiperSlidesSubject.asObservable();
        this.socialSwiperSlides = this.socialSwiperSlidesSubject.asObservable();
        this.featuredPlans = this.featuredPlansSubject.asObservable();
        this.whyGood2GoSlides = this.whyGood2GoSubject.asObservable();
        //get data from firebase
        this.angularFireDatabase.list(FirebaseDatabasePaths.ADS_CONFIGURATION.BANNER_SWIPER_PATH, (ref) => ref.orderByChild('order'))
            .valueChanges().subscribe((slides) => this.bannerSwiperSlidesSubject.next(slides));
        this.angularFireDatabase.list(FirebaseDatabasePaths.ADS_CONFIGURATION.SOCIAL_SWIPER_PATH, (ref) => ref.orderByChild('order'))
            .valueChanges().subscribe((slides) => this.socialSwiperSlidesSubject.next(slides));
        this.plansConfigurationService.planConfiguration
            .pipe(filter((config) => !!config && !!config.activePlans), map((config) => config.activePlans.filter((plans) => plans.featured)))
            .subscribe((featuredPlans) => this.featuredPlansSubject.next(featuredPlans));
        this.angularFireDatabase.list(FirebaseDatabasePaths.ADS_CONFIGURATION.WHY_GOOD2GO_SWIPER_PATH, (ref) => ref.orderByChild('order'))
            .valueChanges().subscribe((slides) => this.whyGood2GoSubject.next(slides));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FirebaseAdsService, deps: [{ token: i1$1.AngularFireDatabase }, { token: PlansConfigurationService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FirebaseAdsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FirebaseAdsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$1.AngularFireDatabase }, { type: PlansConfigurationService }] });

class ChangePlanService {
    constructor(endpointUrl, http) {
        this.endpointUrl = endpointUrl;
        this.http = http;
    }
    /**
     * a method to call backend(bff) method for changing the plan
     * @param changeRequest: change request details
     * @param userPlanId: id of the plan that needs to be changed
     */
    changePlan(changeRequest, userPlanId) {
        //  changeRequest.nextCycle = true;
        return this.postBackendChangePlan(changeRequest, userPlanId);
    }
    /**
     * a method to cancel the request of changing the plan
     * @param userPlanId : id of the plan that needs to be changed
     */
    cancelChangePlan(userPlanId) {
        return this.deleteBackendChangePlan(userPlanId);
    }
    /**
     * a method to add new addons to the plan
     * @param changeRequest: change request details
     * @param userPlanId: id of the plan that needs to add the addon for it
     */
    addPlanDataAddOn(changeRequest, userPlanId) {
        changeRequest.addOnRenewable = false;
        changeRequest.nextCycle = false;
        return this.postBackendChangePlanAddOn(changeRequest, userPlanId);
    }
    postBackendChangePlan(changeRequest, userPlanId) {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.endpointUrl}/api/plan/${userPlanId}/changePlan`, changeRequest).pipe(take(1))
                .subscribe(() => resolve(), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    deleteBackendChangePlan(userPlanId) {
        return new Promise((resolve, reject) => {
            this.http.delete(`${this.endpointUrl}/api/plan/${userPlanId}/changePlan`, { userPlanId }).pipe(take(1))
                .subscribe(() => resolve(), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    postBackendChangePlanAddOn(changeRequest, userPlanId) {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.endpointUrl}/api/plan/${userPlanId}/addon`, changeRequest).pipe(take(1))
                .subscribe((result) => resolve(result), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ChangePlanService, deps: [{ token: EndpointUrl }, { token: i1.AuthHttp }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ChangePlanService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ChangePlanService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [EndpointUrl]
                }] }, { type: i1.AuthHttp }] });

class ContactEmailService {
    constructor(endpointUrl, http, angularFireDatabase) {
        this.endpointUrl = endpointUrl;
        this.http = http;
        this.angularFireDatabase = angularFireDatabase;
    }
    /**
     * a method to send and save contact us message
     * @param contactUsDetails: details for the contact us issue
     */
    sendContactUsEmail(contactUsDetails, action, invisibleRecaptcha) {
        return this.getBackendCheckCompatibility(contactUsDetails, action, invisibleRecaptcha);
    }
    /**
     * a method to save contact us message (special case after the user plan cancellation)
     * @param email: email of the logged in user
     * @param userId : id of the logged in user
     * @param planKey: id of the cancelled plan
     */
    saveCancelPlanFeedback(email, userId, planKey) {
        this.callSaveCancelPlanFeedback(email, userId, planKey);
    }
    getBackendCheckCompatibility(email, action, invisibleRecaptcha) {
        let header = new HttpHeaders()
            .set('reCaptcha', email?.reCaptcha);
        if (!!action) {
            header = header.set('action', action);
        }
        header = header.set('invisibleRecaptcha', `${!!invisibleRecaptcha}`);
        return new Promise((resolve, reject) => {
            this.http.postWithHeader(this.endpointUrl + '/api/contactUs', email, header).pipe(take(1))
                .subscribe(() => resolve(), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    callSaveCancelPlanFeedback(email, userId, planKey) {
        this.angularFireDatabase.object(FirebaseDatabasePaths.USER_CANCEL_PLAN_FEEDBACK_LOG(userId, planKey))
            .update(Object.assign({}, email, { updatedAt: serverTimestamp() }))
            .catch((error) => console.error('Logging cancel plan feedback failed', error));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ContactEmailService, deps: [{ token: EndpointUrl }, { token: i1.AuthHttp }, { token: i1$1.AngularFireDatabase }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ContactEmailService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ContactEmailService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [EndpointUrl]
                }] }, { type: i1.AuthHttp }, { type: i1$1.AngularFireDatabase }] });

class DealersService {
    constructor(mailChimpConfig, endpointUrl, http) {
        this.mailChimpConfig = mailChimpConfig;
        this.endpointUrl = endpointUrl;
        this.http = http;
    }
    sendStoreInfo(dealerInfo, reCaptchaResponse, action, invisibleRecaptcha) {
        return this.backendSendStoreInfo(dealerInfo, reCaptchaResponse, action, invisibleRecaptcha);
    }
    getMailChimpUrl(dealerInfo) {
        // Send the information to MailChimp
        // implemented it in front-end to benefit from the API authentication used on MailChimp
        // ex: Duplicate subscribtion, there is also a second reCaptcha done on their side as well
        // open this url which opens a template form, if all the field are valid (we already validate) it will send an email to ask user for confirmation on subscribtion.
        const phoneNumbers = dealerInfo.phone.match(/\d/g);
        const phone = phoneNumbers.join('');
        return this.mailChimpConfig.MAIL_CHIMP_ENDPOINT + '?' + this.objectToUrl({
            id: this.mailChimpConfig.DEALERS_FORM_ID,
            u: this.mailChimpConfig.MAIL_CHIMP_USER_ID,
            FNAME: dealerInfo.firstName,
            LNAME: dealerInfo.lastName,
            EMAIL: dealerInfo.email,
            MMERGE3: dealerInfo.storeName,
            subscibe: 'Subscibe'
        }) +
            //FIXME
            // I dont like this..but the MailChimp expects 3 fields for phone number,
            // interestingly though it helps as well to extract the number from the input mask as well.
            '&MMERGE4[area]=' + phone.substr(0, 3) +
            '&MMERGE4[detail1]=' + phone.substr(3, 3) +
            '&MMERGE4[detail2]=' + phone.substr(6);
    }
    backendSendStoreInfo(dealerInfo, reCaptchaResponse, action, invisibleRecaptcha) {
        let header = new HttpHeaders()
            .set('reCaptcha', reCaptchaResponse);
        if (!!action) {
            header = header.set('action', action);
        }
        header = header.set('invisibleRecaptcha', `${!!invisibleRecaptcha}`);
        return new Promise((resolve, reject) => {
            this.http.postWithHeader(this.endpointUrl + '/api/dealersInfo', dealerInfo, header).pipe(take(1))
                .subscribe(() => {
                resolve();
            }, (error) => this.http.handlePromiseError(error, reject));
        });
    }
    objectToUrl(obj) {
        return Object.keys(obj).map((key) => {
            return encodeURIComponent(key) + '=' + encodeURIComponent(obj[key]);
        }).join('&');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: DealersService, deps: [{ token: MailChimpConfig }, { token: EndpointUrl }, { token: i1.AuthHttp }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: DealersService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: DealersService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [MailChimpConfig]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [EndpointUrl]
                }] }, { type: i1.AuthHttp }] });

class InternationalCallingConfigurationService {
    constructor(angularFireDatabase) {
        this.angularFireDatabase = angularFireDatabase;
        this.isReadySubject = new ReplaySubject(1);
        this.isReady = this.isReadySubject.asObservable();
        this.angularFireDatabase.object(FirebaseDatabasePaths.INTERNATIONAL_CALL_CONFIGURATION.BASE).valueChanges()
            .subscribe((conf) => {
            this.rates = (new OrderByPipe()).transform((new ValuesPipe()).transform(conf.ratePerMinute), 'name');
            this.eligibility = (new OrderByPipe()).transform((new ValuesPipe()).transform(conf.unlimitedPlanEligibility), 'name');
            this.payGo = (new OrderByPipe()).transform((new ValuesPipe()).transform(conf.payGoRates), 'name');
            this.isReadySubject.next(true);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: InternationalCallingConfigurationService, deps: [{ token: i1$1.AngularFireDatabase }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: InternationalCallingConfigurationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: InternationalCallingConfigurationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$1.AngularFireDatabase }] });

class FirebaseMobilePlansCartService {
    constructor(simpleAuthService, angularFireDatabase, userAuthService) {
        this.simpleAuthService = simpleAuthService;
        this.angularFireDatabase = angularFireDatabase;
        this.userAuthService = userAuthService;
        this.userCartSubscription = new ReplaySubject(1);
        this.userCart = this.userCartSubscription.asObservable().pipe(debounceTime(50));
        simpleAuthService.registerDependentService('firebase-mobile-plans', this);
    }
    buildSubscriptions(fbUser) {
        console.log('building subscriptions for firebase-mobile-plans.service');
        const subscriptions = [];
        this.fbUser = fbUser;
        subscriptions.push(this.angularFireDatabase.object(FirebaseDatabasePaths.USER_CART(fbUser.uid))
            .valueChanges()
            .pipe(debounceTime(100))
            .subscribe((savedPlanFB) => {
            if (!!savedPlanFB && !!savedPlanFB.userId) {
                this.userCartSubscription.next(savedPlanFB);
            }
            else {
                this.userCartSubscription.next(undefined);
            }
        }));
        return subscriptions;
    }
    onLoggedOut() {
        this.fbUser = null;
        this.userCartSubscription.next(undefined);
    }
    clearUserCart() {
        return new Promise((resolve) => {
            if (!!this.fbUser && !!this.fbUser.uid) {
                setTimeout(() => {
                    this.angularFireDatabase
                        .object(FirebaseDatabasePaths.USER_CART(this.fbUser.uid))
                        .remove()
                        .then(() => {
                        resolve();
                    });
                });
            }
            else {
                resolve();
            }
        });
    }
    updateUserCart(partialCart, previousResolve, previousReject) {
        return new Promise((resolve, reject) => {
            this.simpleAuthService.servicesIsReady.pipe(take(1)).subscribe((ready) => {
                if (ready) {
                    this.callUpdateUserCart(partialCart, previousResolve || resolve, previousReject || reject);
                }
                else {
                    // TODO this need a better way to handle, anyway it is not really major issue for now
                    console.log('simple auth service not ready');
                    setTimeout(() => this.updateUserCart(partialCart, resolve, reject), 500);
                }
            });
        });
    }
    deleteFromUserCart(property) {
        return new Promise((resolve, reject) => {
            if (!!this.fbUser && !!this.fbUser.uid && !!property) {
                this.angularFireDatabase.object(FirebaseDatabasePaths.USER_CART(this.fbUser.uid) + '/' + property).remove()
                    .then(() => {
                    console.log(`Property ${property} from cart removed Successfully`);
                    resolve();
                })
                    .catch((error) => {
                    console.error(`Remove ${property} from cart Failed `, error.message);
                    reject(error);
                });
            }
            else {
                console.warn('User is not loggedIn, or property undefined');
                resolve();
            }
        });
    }
    callUpdateUserCart(partialCart, resolve, reject) {
        if (!!this.fbUser && !!this.fbUser.uid) {
            this.updateUserCartInFirebase(partialCart, this.fbUser.uid)
                .then(() => resolve(), (error) => reject(error));
        }
        else {
            try {
                //FIXME? this pass doesn't always return a promise
                // Fix made inside 'loginAnonymous' but keeping try/catch for a while
                this.userAuthService.loginAnonymous().then((user) => {
                    if (!!user) {
                        this.fbUser = user;
                        this.updateUserCartInFirebase(partialCart, this.fbUser.uid)
                            .then(() => resolve(), (error) => reject(error));
                    }
                    else {
                        console.error('user not created');
                    }
                });
            }
            catch (error) {
                console.warn(error);
                reject(error);
            }
        }
    }
    updateUserCartInFirebase(userPlan, userId) {
        return new Promise((resolve, reject) => {
            const firebaseUserPlan = Object.assign({}, omitBy(userPlan, isNil));
            if (Object.keys(firebaseUserPlan).length === 0) { // if the object empty no need to update anything!
                resolve();
            }
            if (!!userId) {
                firebaseUserPlan.userId = userId;
                firebaseUserPlan.updatedAt = serverTimestamp();
                if (!firebaseUserPlan.createdAt) {
                    firebaseUserPlan.createdAt = serverTimestamp();
                }
                this.angularFireDatabase.object(FirebaseDatabasePaths.USER_CART(userId)).update(firebaseUserPlan)
                    .then(() => {
                    console.log('User Cart Updated Successfully userId', userId);
                    resolve();
                }).catch((error) => {
                    console.error('Update User Cart Update Failed ', error.message);
                    reject();
                });
            }
            else {
                console.warn('User is not loggedIn');
                reject();
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FirebaseMobilePlansCartService, deps: [{ token: i1.SimpleAuthService }, { token: i1$1.AngularFireDatabase }, { token: i1.UserAuthService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FirebaseMobilePlansCartService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FirebaseMobilePlansCartService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.SimpleAuthService }, { type: i1$1.AngularFireDatabase }, { type: i1.UserAuthService }] });

class FirebaseUserPlansService {
    constructor(simpleAuthService, angularFireDatabase) {
        this.simpleAuthService = simpleAuthService;
        this.angularFireDatabase = angularFireDatabase;
        this.userPlansSubject = new ReplaySubject(1);
        this.pendingUserPlansSubject = new ReplaySubject(1);
        this.userPlans = this.userPlansSubject.asObservable();
        this.pendingUserPlans = this.pendingUserPlansSubject.asObservable();
        this.simpleAuthService.registerDependentService('firebase-user-plans', this);
    }
    buildSubscriptions(fbUser) {
        console.log('building subscriptions for firebase-user-plans.service');
        const subscriptions = [];
        subscriptions.push(this.angularFireDatabase.list(FirebaseDatabasePaths.USER_PLANS(fbUser.uid), (ref) => ref.orderByChild('createdAt'))
            .valueChanges().pipe(throttleTime(50))
            .subscribe((plans) => {
            this.userPlansSubject.next(!!plans ? plans.reverse() : plans);
        }));
        subscriptions.push(this.angularFireDatabase
            .list(FirebaseDatabasePaths.USER_PENDING_PLANS(fbUser.uid), (ref) => ref.orderByKey())
            .valueChanges().pipe(throttleTime(50))
            .subscribe((pendingUserPlans) => this.pendingUserPlansSubject.next(pendingUserPlans)));
        return subscriptions;
    }
    onLoggedOut() {
        this.userPlansSubject.next([]);
        this.pendingUserPlansSubject.next([]);
    }
    addUserPlan(userId, userPlan) {
        userPlan = Object.assign(omitBy(userPlan, isNil));
        userPlan.createdAt = serverTimestamp();
        userPlan.updatedAt = serverTimestamp();
        return new Promise(((resolve, reject) => {
            this.angularFireDatabase
                .list(FirebaseDatabasePaths.USER_PLANS(userId))
                .push(userPlan).then((plan) => resolve(plan.key), (error) => reject(error));
        }));
    }
    updateUserPlan(userId, key, updatedUserPlan) {
        const firebaseUserPlan = Object.assign({}, omitBy(updatedUserPlan, isNil));
        return new Promise(((resolve, reject) => {
            if (!!userId && !!key) {
                const firebaseObjectObservable = this.angularFireDatabase.object(FirebaseDatabasePaths.USER_PLANS(userId, key));
                firebaseObjectObservable.update(firebaseUserPlan).then((plan) => {
                    resolve();
                }).catch((error) => reject(error));
                firebaseObjectObservable.valueChanges().subscribe((userPlan) => {
                    // now the updateUserPlan overwrites the changes and deletes the mdn and activation code when activating a new purchased plan directly after the purchase
                    if (!!userPlan) {
                    }
                    else {
                        console.warn(`User plan for user ${userId} with ${key} not found`);
                        reject(`User plan for user ${userId} with ${key} not found`);
                    }
                });
            }
            else {
                reject('userId or key is missing');
                console.warn(`userId (${userId}) or key (${key}) is missing`);
            }
        }));
    }
    setUserPlanId(userId, key) {
        const firebaseUserPlan = { id: key };
        firebaseUserPlan.updatedAt = serverTimestamp();
        return new Promise(((resolve, reject) => {
            if (!!userId && !!key) {
                const firebaseObjectObservable = this.angularFireDatabase.object(FirebaseDatabasePaths.USER_PLANS(userId, key));
                firebaseObjectObservable.valueChanges().pipe(take(1)).subscribe((userPlan) => {
                    if (!!userPlan) {
                        firebaseObjectObservable
                            .update(firebaseUserPlan)
                            .then(() => resolve())
                            .catch((error) => reject(error));
                    }
                    else {
                        console.warn(`User plan for user ${userId} with ${key} not found`);
                        reject(`User plan for user ${userId} with ${key} not found`);
                    }
                });
            }
            else {
                reject('userId or key is missing');
                console.warn(`userId (${userId}) or key (${key}) is missing`);
            }
        }));
    }
    updatePendingUserPlan(userId, key, userPlan) {
        userPlan = Object.assign({}, omitBy(userPlan, isNil));
        userPlan.updatedAt = serverTimestamp();
        return new Promise(((resolve, reject) => {
            if (!!userId && !!key) {
                this.angularFireDatabase.object(FirebaseDatabasePaths.USER_PENDING_PLANS(userId, key))
                    .set(userPlan)
                    .then((plan) => resolve())
                    .catch((error) => reject(error));
            }
            else {
                reject('userId or key is missing');
                console.warn(`userId (${userId}) or key (${key}) is missing`);
            }
        }));
    }
    deletePendingUserPlan(userId, key) {
        return new Promise(((resolve, reject) => {
            if (!!userId && !!key) {
                this.angularFireDatabase.object(FirebaseDatabasePaths.USER_PENDING_PLANS(userId, key))
                    .remove()
                    .then(() => resolve())
                    .catch((error) => reject(error));
            }
            else {
                reject('userId or key is missing');
                console.warn(`userId (${userId}) or key (${key}) is missing`);
            }
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FirebaseUserPlansService, deps: [{ token: i1.SimpleAuthService }, { token: i1$1.AngularFireDatabase }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FirebaseUserPlansService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FirebaseUserPlansService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.SimpleAuthService }, { type: i1$1.AngularFireDatabase }] });

class UserPlansService {
    constructor(endpointUrl, plansConf, simpleAuthState, authHttp, firebaseUserPlansService, firebaseAccountPaymentService, firebaseUserProfileService) {
        this.endpointUrl = endpointUrl;
        this.plansConf = plansConf;
        this.simpleAuthState = simpleAuthState;
        this.authHttp = authHttp;
        this.firebaseUserPlansService = firebaseUserPlansService;
        this.firebaseAccountPaymentService = firebaseAccountPaymentService;
        this.firebaseUserProfileService = firebaseUserProfileService;
        this.userPlansSubject = new ReplaySubject(1);
        this.pendingActivationUserPlansSubject = new ReplaySubject(1);
        this.selectedUserPlanSubject = new ReplaySubject(1);
        this.isSelectedPlanReadySubject = new ReplaySubject(1);
        this.userPlans = this.userPlansSubject.asObservable();
        this.pendingActivationUserPlans = this.pendingActivationUserPlansSubject.asObservable();
        this.selectedUserPlanObservable = this.selectedUserPlanSubject.asObservable();
        this.isSelectedPlanReady = this.isSelectedPlanReadySubject.asObservable();
        this.plansConf.planConfiguration.subscribe((config) => this.config = config);
        this.simpleAuthState.registerDependentService('user-plans', this);
    }
    buildSubscriptions(fbUser) {
        console.log('building subscriptions for user-plans.service');
        const subscriptions = [];
        // subscription to update userPlans
        subscriptions.push(this.firebaseUserPlansService.userPlans.subscribe((plans) => {
            if (!!plans) {
                const firebaseUserPlans = plans.map((plan) => {
                    /// TODO this is really add more headache for processor, this should be fixed for better way
                    const customizablePlan = this.getCustomizableMobilePlanFromUserPlan(plan);
                    plan.title = customizablePlan.title;
                    plan.noPlan = customizablePlan.basePlan.id == 'NO-PLAN';
                    return plan;
                }).sort((firstPlan, secondPlan) => firstPlan.createdAt > secondPlan.createdAt ? -1 : (firstPlan.createdAt < secondPlan.createdAt ? 1 : 0));
                this.userPlansSubject.next(firebaseUserPlans);
            }
        }));
        // subscription to prepare and update isSelectedPlanReady
        subscriptions.push(this.selectedUserPlanObservable
            .pipe(combineLatest(this.firebaseUserProfileService.userProfileObservable, this.firebaseAccountPaymentService.paymentMethodsList, this.firebaseUserPlansService.pendingUserPlans, (userPlan, userProfile, paymentMethods, pendingPlans) => this.loadSelectedUserPlan(userPlan, userProfile, paymentMethods, pendingPlans)), throttleTime(100)).subscribe((isReady) => {
            console.debug(`Selected plan is${isReady ? '' : ' NOT'} ready`);
            this.isSelectedPlanReadySubject.next(isReady);
        }));
        this.selectFirstUserPlan();
        return subscriptions;
    }
    onLoggedOut() {
        return this.clearSelectedUserPlan();
    }
    addUserPlanMDN(userId, mdn) {
        return new Promise(((resolve, reject) => {
            if (!!userId && !!mdn) {
                this.searchFirebasePlanByMDN(mdn).then((userPlan) => {
                    if (!!userPlan) {
                        reject(`MDN ${mdn} is already added`);
                    }
                    else {
                        this.firebaseUserPlansService.addUserPlan(userId, Object.assign({ mdn, userId })).then((key) => {
                            console.log('Added user plan Successfully');
                            this.firebaseUserPlansService.setUserPlanId(userId, key).then(() => resolve(key), (error) => console.warn(error));
                        }).catch((error) => {
                            console.error('Add user plan failed on database failed', error.message || error);
                            reject(error);
                        });
                    }
                });
            }
        }));
    }
    bffAddUserPlanMDN(mdn) {
        return new Promise(((resolve, reject) => {
            let updateParams;
            updateParams = new Map();
            updateParams['mdn'] = mdn;
            this.authHttp.post(this.endpointUrl + '/api/plan/addActivatedMdn', updateParams).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    getPendingActivationsFromBff(userId) {
        return new Promise(((resolve, reject) => {
            this.authHttp.get(this.endpointUrl + '/api/account/' + userId + '/pendingPlansList').pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    isValidShippingAddress() {
        return !!this.selectedPlanShippingAddress;
    }
    getShippingAddressById(shippingAddressId) {
        return new Promise(((resolve, reject) => {
            this.authHttp.get(`${this.endpointUrl}/api/account/shippingAddress/${shippingAddressId}`).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    selectUserPlan(id) {
        console.debug('Selecting plan with id ', id);
        this.searchFirebasePlanByIds(id).then((fbPlane) => {
            if (!!fbPlane) {
                if (fbPlane.noPlan) {
                    console.warn('You are trying to select plan without base plan, this is usually a plan should be updated with real plan later', id);
                }
                this.selectedUserPlanSubject.next(fbPlane);
            }
            else {
                console.warn(`Plan with id (${id}) not found!!`);
            }
        });
    }
    selectFirstUserPlan(selectFirstActive = false) {
        this.userPlans.pipe(filter((plansAttr) => plansAttr.length > 0), take(1)).subscribe((plans) => {
            const filteredPlans = selectFirstActive ? plans.filter((plan) => !!plan.mdn) : plans;
            if (!!filteredPlans && filteredPlans.length > 0) {
                const firstPlan = filteredPlans.find((plan) => plan.noPlan === false);
                if (!!firstPlan) {
                    this.selectUserPlan(firstPlan.id);
                }
            }
            else {
                console.warn('Select First UserPlan: There is no plan to select');
                this.clearSelectedUserPlan();
            }
        });
    }
    clearSelectedUserPlan() {
        this.selectedUserPlanSubject.next(undefined);
    }
    updateUserPlan(userId, updatedPlan) {
        return new Promise((resolve, reject) => {
            if (!!userId && !!updatedPlan) {
                this.firebaseUserPlansService.updateUserPlan(userId, updatedPlan.id, updatedPlan).then(() => {
                    console.log(`User plan ${updatedPlan.id} updated Successfully`);
                    resolve();
                }, (error) => reject(error));
            }
            else {
                reject('One of parameters is missing userId, or plan');
            }
        });
    }
    updateUserPlanBasePlan(userId, userPlan, basePlan) {
        return new Promise((resolve, reject) => {
            if (!!userPlan && !!basePlan) {
                userPlan.basePlan = basePlan;
                this.firebaseUserPlansService.updateUserPlan(userId, userPlan.id, userPlan).then(() => {
                    console.log(`User base plan for plan ${userPlan.id} updated Successfully`);
                    resolve();
                }, (error) => reject(error));
            }
            else {
                reject('One of parameters is missing userId, or plan');
            }
        });
    }
    updateUserPlanAddOns(userId, userPlan, addOns) {
        return new Promise((resolve, reject) => {
            if (!!userPlan) {
                if (!!addOns) {
                    userPlan.addOns = addOns;
                }
                else {
                    delete userPlan.addOns;
                }
                this.firebaseUserPlansService.updateUserPlan(userId, userPlan.id, userPlan).then(() => {
                    console.log(`User add-ons for plan ${userPlan.id} updated Successfully`, addOns);
                    resolve();
                }, (error) => reject(error));
            }
            else {
                reject('One of parameters is missing userId, or addOns');
            }
        });
    }
    addDeviceToUserPlan(userId, id, device) {
        return new Promise((resolve, reject) => {
            this.getUserPlan(id).then((plan) => {
                if (!!plan) {
                    if (!!plan.planExpectedDevice) {
                        delete plan.planExpectedDevice;
                    }
                    plan.planDevice = device;
                    this.updateUserPlan(userId, plan).then(() => resolve(), (error) => reject(error));
                }
                else {
                    reject(`plan with id (${id}) not found`);
                }
            }, (error) => reject(error));
        });
    }
    addExpectedDeviceToUserPlan(userId, id, device) {
        return new Promise((resolve, reject) => {
            this.getUserPlan(id).then((plan) => {
                if (!!plan) {
                    if (!!plan.planDevice) {
                        delete plan.planDevice;
                    }
                    plan.planExpectedDevice = device;
                    this.updateUserPlan(userId, plan).then(() => resolve(), (error) => reject(error));
                }
                else {
                    reject(`plan with id (${id}) not found`);
                }
            }, (error) => reject(error));
        });
    }
    setPlanDeviceFromBFF(userPlanId, planDevice) {
        return new Promise((resolve, reject) => {
            this.authHttp.patch(`${this.endpointUrl}/api/plan/${userPlanId}/planDevice`, planDevice)
                .pipe(take(1)).subscribe((result) => resolve(), (error) => this.authHttp.handlePromiseError(error, reject));
        });
    }
    deletePlanDeviceFromBFF(userPlanId) {
        return new Promise((resolve, reject) => {
            this.authHttp.delete(`${this.endpointUrl}/api/plan/${userPlanId}/planDevice`)
                .pipe(take(1)).subscribe((result) => resolve(), (error) => this.authHttp.handlePromiseError(error, reject));
        });
    }
    setPlanExpectedDeviceFromBFF(userPlanId, planExpectedDevice) {
        return new Promise((resolve, reject) => {
            this.authHttp.patch(`${this.endpointUrl}/api/plan/${userPlanId}/planExpectedDevice`, planExpectedDevice)
                .pipe(take(1)).subscribe((result) => resolve(), (error) => this.authHttp.handlePromiseError(error, reject));
        });
    }
    deletePlanExpectedDeviceFromBFF(userPlanId) {
        return new Promise((resolve, reject) => {
            this.authHttp.delete(`${this.endpointUrl}/api/plan/${userPlanId}/planExpectedDevice`)
                .pipe(take(1)).subscribe((result) => resolve(), (error) => this.authHttp.handlePromiseError(error, reject));
        });
    }
    updatePendingPlanShippingAddress(planId, address, orderId) {
        return new Promise((resolve, reject) => {
            this.authHttp.put(`${this.endpointUrl}/api/pendingPlan/shippingAddress?userPlanId=${planId}&orderId=${orderId}`, address)
                .pipe(take(1)).subscribe((result) => resolve(), (error) => this.authHttp.handlePromiseError(error, reject));
        });
    }
    getUserPlan(id) {
        return this.searchFirebasePlanByIds(id);
    }
    getUserPlanFromCustomizableMobilePlan(userId, userPlan) {
        return this.loadFirebasePlanWithCustomizablePlan(userId, userPlan);
    }
    getCustomizableMobilePlanFromUserPlan(userPlan) {
        return this.loadCustomizableMobilePlanFromUserPlan(userPlan);
    }
    getUserPlanByOrderId(orderId) {
        return this.searchFirebasePlanByOrderIds(orderId);
    }
    deletePendingUserPlan(userId, userPlanId) {
        return new Promise((resolve, reject) => {
            if (!!userId && !!userPlanId) {
                this.firebaseUserPlansService.deletePendingUserPlan(userId, userPlanId).then(() => {
                    resolve();
                }, (error) => reject(error));
            }
            else {
                reject('One of parameters is missing userId, or userPlanId');
            }
        });
    }
    clearShowActivationNotification(userId, userPlanId) {
        return new Promise((resolve, reject) => {
            this.getUserPlan(userPlanId).then((plan) => {
                if (!!plan) {
                    plan.showActivationNotification = false;
                    this.updateUserPlan(userId, plan).then(() => resolve(), (error) => reject(error));
                }
                else {
                    reject(`plan with id (${userPlanId}) not found`);
                }
            }, (error) => reject(error));
        });
    }
    setIsPhonePurchased(userId, userPlanId, isPhonePurchased) {
        return new Promise((resolve, reject) => {
            this.getUserPlan(userPlanId).then((plan) => {
                if (!!plan) {
                    plan.isPhonePurchased = isPhonePurchased;
                    this.updateUserPlan(userId, plan).then(() => resolve(), (error) => reject(error));
                }
                else {
                    reject(`plan with id (${userPlanId}) not found`);
                }
            }, (error) => reject(error));
        });
    }
    reverseCancelPlan(mdn, userPlanId) {
        return new Promise((resolve, reject) => {
            this.authHttp.delete(`${this.endpointUrl}/api/account/canceledOnExpiry/${mdn}/plan/${userPlanId}`)
                .pipe(take(1)).subscribe((result) => resolve(), (error) => this.authHttp.handlePromiseError(error, reject));
        });
    }
    submitMigrationRequest(mdn, body) {
        return new Promise((resolve, reject) => {
            this.authHttp.post(`${this.endpointUrl}/api/plans/${mdn}/migrate`, body).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.authHttp.handlePromiseError(error, reject));
        });
    }
    getMigrationDetails(mdn) {
        return new Promise((resolve, reject) => {
            this.authHttp.get(`${this.endpointUrl}/api/plans/${mdn}/migrate`)
                .subscribe((response) => resolve(response), (error) => this.authHttp.handlePromiseError(error, reject));
        });
    }
    calculateDeferredTaxes(orderId, paymentInfo) {
        return new Promise((resolve, reject) => {
            this.authHttp.post(`${this.endpointUrl}/api/plans/deferred/${orderId}/taxes`, paymentInfo).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.authHttp.handlePromiseError(error, reject));
        });
    }
    setCallForwarding(mdn, enableCalls) {
        return new Promise((resolve, reject) => {
            this.authHttp.patch(`${this.endpointUrl}/api/plans/${mdn}/forward`, { enable: enableCalls }).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.authHttp.handlePromiseError(error, reject));
        });
    }
    loadSelectedUserPlan(userPlan, userProfile, paymentMethods, pendingPlans) {
        this.userProfile = userProfile;
        this.selectedUserPlan = userPlan;
        this.selectedPlanPaymentMethod = null;
        if (!!userPlan && !!userProfile) {
            this.selectedPendingUserPlan = pendingPlans.find((pendings) => pendings.id === userPlan.id);
            if (!!paymentMethods && !!userPlan.paymentMethodId) {
                this.selectedPlanPaymentMethod = paymentMethods.find((method) => method.id === userPlan.paymentMethodId);
            }
            if (!!userProfile.shippingAddresses && userPlan.shippingAddressId) {
                this.selectedPlanShippingAddress = userProfile.shippingAddresses.find((address) => address.id === userPlan.shippingAddressId);
            }
            return true;
        }
        else {
            this.selectedPlanShippingAddress = null;
        }
        return false;
    }
    searchFirebasePlanByIds(id) {
        return new Promise((resolve, reject) => {
            this.firebaseUserPlansService.userPlans.subscribe((plans) => {
                resolve(this.searchPlansByPlanId(plans, id));
            }, (error) => reject(error));
        });
    }
    searchFirebasePlanByOrderIds(orderId) {
        return new Promise((resolve, reject) => {
            this.firebaseUserPlansService.userPlans.pipe(take(1)).subscribe((plans) => {
                resolve(this.searchPlansByPlanOrderId(plans, orderId));
            }, (error) => reject(error));
        });
    }
    searchFirebasePlanByMDN(mdn) {
        return new Promise((resolve, reject) => {
            this.firebaseUserPlansService.userPlans.pipe(take(1)).subscribe((plans) => {
                resolve(this.searchPlansByPlanMDN(plans, mdn));
            }, (error) => reject(error));
        });
    }
    searchPlansByPlanId(plans, planId) {
        return plans.find((plan) => plan.id == planId);
    }
    searchPlansByPlanOrderId(plans, planOrderId) {
        return plans.find((plan) => plan.orderId == planOrderId);
    }
    searchPlansByPlanMDN(plans, mdn) {
        return plans.find((plan) => plan.mdn == mdn);
    }
    loadFirebasePlanWithCustomizablePlan(userId, customPlan) {
        const newPlan = { userId, title: customPlan.title };
        newPlan.createdAt = serverTimestamp();
        newPlan.basePlan = { id: customPlan.basePlan.id, type: customPlan.basePlan.type, price: customPlan.basePlan.price, promoPrice: customPlan.basePlan.promoPrice };
        if (!!customPlan.basePlan.parentId) {
            newPlan.basePlan.parentId = customPlan.basePlan.parentId;
        }
        if (customPlan.basePlan.virtual) {
            newPlan.basePlan.virtual = customPlan.basePlan.virtual;
        }
        if (!!customPlan.planDevice) {
            newPlan.planDevice = customPlan.planDevice;
        }
        if (!!customPlan.planExpectedDevice) {
            newPlan.planExpectedDevice = customPlan.planExpectedDevice;
        }
        return newPlan;
    }
    loadCustomizableMobilePlanFromUserPlan(savedPlanFB) {
        if (!!this.config) {
            const plan = new CustomizableMobilePlan();
            plan.userId = savedPlanFB.userId;
            plan.uniquePlanIdentifier = savedPlanFB?.uniquePlanIdentifier;
            // console.log('savedPlanFB.', savedPlanFB);
            if (!!savedPlanFB.basePlan) {
                if (savedPlanFB.basePlan.type !== 'base-plan') {
                    plan.basePlan = this.config.planAddOns
                        .filter((bPlan) => bPlan.id === savedPlanFB.basePlan.id)
                        .map((bPlan) => new GenericMobilePlanItem(bPlan.id, bPlan.title, bPlan.subtitle, bPlan.price, bPlan.type, new MobilePlanDetails(bPlan.minutes, bPlan.messages, bPlan.data, bPlan.mms), null, false, '', '', bPlan.description, '', '', 0, 0)).pop();
                }
                else {
                    plan.basePlan = this.config.allPlans
                        .filter((bPlan) => bPlan.id === savedPlanFB.basePlan.id)
                        .map((bPlan) => new GenericMobilePlanItem(bPlan.id, bPlan.title, bPlan.subtitle, bPlan.price, 'base-plan', new MobilePlanDetails(bPlan.minutes, bPlan.messages, bPlan.data, bPlan.mms, bPlan.extraData), bPlan.parentId, bPlan.virtual, bPlan.buttonText, bPlan.extTitle, bPlan.description, bPlan.promoMessage, bPlan.promoCode, bPlan.promoPrice, bPlan.promoMonths, bPlan.cycleDays, bPlan.promoMoPrice, bPlan.subscriptionCycles, bPlan.category, bPlan.annualSaving, bPlan.isSpecialPromotion, bPlan.specialPromotion, bPlan.availableForChange, bPlan.isVacation, bPlan.promoAvailableForChange, bPlan.multiLineDisount, bPlan.ebb, bPlan.subscriptionPrice, bPlan.subscriptionPromoPrice, null, bPlan.data, bPlan.hotspotData, bPlan?.uniquePlanIdentifier, bPlan?.bundleId, bPlan?.isGo2Plan, bPlan?.go2PlansAvailable)).pop();
                }
            }
            if (!plan.basePlan) {
                plan.basePlan = new GenericMobilePlanItem('NOT-FOUND', 'Not Found', '', 0, 'base-plan', null, null, true, '', '');
                console.debug('plan not found', savedPlanFB.basePlan);
            }
            if (!!savedPlanFB.planDevice) {
                plan.planDevice = savedPlanFB.planDevice;
            }
            if (!!savedPlanFB.simsQuantity) {
                plan.simsQuantity = savedPlanFB.simsQuantity;
            }
            if (!!savedPlanFB.planExpectedDevice) {
                plan.planExpectedDevice = savedPlanFB.planExpectedDevice;
            }
            if (!!savedPlanFB.phones) {
                plan.phones = Array.from(Object.values(savedPlanFB.phones));
            }
            if (!!savedPlanFB.addOns) {
                plan.addOns = [];
                savedPlanFB.addOns.forEach((addOn) => {
                    const _addOn = this.getAddonDetails(addOn);
                    plan.addOns.push(_addOn);
                });
            }
            return plan;
        }
        return;
    }
    getAddonDetails(addOn) {
        const confAddon = this.config.planAddOns.find((addon) => addon.id == addOn.id || (addon.id.indexOf(addOn.id) > -1));
        return { id: confAddon.id, title: confAddon.title, subtitle: confAddon.subtitle, type: confAddon.type, price: confAddon.price, quantity: addOn.quantity };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UserPlansService, deps: [{ token: EndpointUrl }, { token: PlansConfigurationService }, { token: i1.SimpleAuthService }, { token: i1.AuthHttp }, { token: FirebaseUserPlansService }, { token: FirebaseAccountPaymentService }, { token: FirebaseUserProfileService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UserPlansService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UserPlansService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [EndpointUrl]
                }] }, { type: PlansConfigurationService }, { type: i1.SimpleAuthService }, { type: i1.AuthHttp }, { type: FirebaseUserPlansService }, { type: FirebaseAccountPaymentService }, { type: FirebaseUserProfileService }] });

var PortInStatusEnum;
(function (PortInStatusEnum) {
    PortInStatusEnum[PortInStatusEnum["SUBMITTED"] = 'SUBMITTED'] = "SUBMITTED";
    PortInStatusEnum[PortInStatusEnum["DELAY"] = 'DELAY'] = "DELAY";
    PortInStatusEnum[PortInStatusEnum["CONFIRMED"] = 'CONFIRMED'] = "CONFIRMED";
    PortInStatusEnum[PortInStatusEnum["RESOLUTION"] = 'RESOLUTION'] = "RESOLUTION";
    PortInStatusEnum[PortInStatusEnum["REJECTED"] = 'REJECTED'] = "REJECTED";
    PortInStatusEnum[PortInStatusEnum["CANCELED"] = 'CANCELED'] = "CANCELED";
    PortInStatusEnum[PortInStatusEnum["CONFLICT"] = 'CONFLICT'] = "CONFLICT";
    PortInStatusEnum[PortInStatusEnum["DENIED"] = 'DENIED'] = "DENIED";
    PortInStatusEnum[PortInStatusEnum["ERROR"] = 'ERROR'] = "ERROR";
})(PortInStatusEnum || (PortInStatusEnum = {}));

class FirebaseUserAccountsService {
    constructor(angularFireDatabase, simpleAuthService) {
        this.angularFireDatabase = angularFireDatabase;
        this.simpleAuthService = simpleAuthService;
        this.userAccountsSubject = new ReplaySubject(1);
        this.userAccounts = this.userAccountsSubject.asObservable();
        this.simpleAuthService.registerDependentService('firebase-user-accounts', this);
    }
    buildSubscriptions(fbUser) {
        console.log('building subscriptions for firebase-user-accounts.service');
        const subscriptions = [];
        subscriptions.push(this.angularFireDatabase.list(FirebaseDatabasePaths.USER_ACCOUNTS(fbUser.uid))
            .valueChanges().pipe(throttleTime(50)).subscribe((accounts) => this.userAccountsSubject.next(accounts)));
        return subscriptions;
    }
    onLoggedOut() {
        this.userAccountsSubject.next([]);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FirebaseUserAccountsService, deps: [{ token: i1$1.AngularFireDatabase }, { token: i1.SimpleAuthService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FirebaseUserAccountsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FirebaseUserAccountsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$1.AngularFireDatabase }, { type: i1.SimpleAuthService }] });

class UserAccountService {
    constructor(endpointUrl, firebaseUserAccountsService, userPlansService, simpleAuthService, http) {
        this.endpointUrl = endpointUrl;
        this.firebaseUserAccountsService = firebaseUserAccountsService;
        this.userPlansService = userPlansService;
        this.simpleAuthService = simpleAuthService;
        this.http = http;
        this.userAccountsSubject = new ReplaySubject(1);
        this.selectedAccountSubject = new ReplaySubject(1);
        this.isSyncingAccountSubject = new ReplaySubject(1);
        this.isNewAccountActivatedSubject = new ReplaySubject(1);
        this.userAccounts = this.userAccountsSubject.asObservable();
        this.selectedAccount = this.selectedAccountSubject.asObservable();
        this.isSyncingAccount = this.isSyncingAccountSubject.asObservable();
        this.isNewAccountActivated = this.isNewAccountActivatedSubject.asObservable();
        this.simpleAuthService.registerDependentService('user-account', this);
    }
    getAvailableLocalAreaNumbers(zip) {
        return new Promise((resolve, reject) => this.npaListBackendUserAccount(zip)
            .then((npa) => !!npa && !!npa.numberingPlanArea ? resolve(npa.numberingPlanArea) : reject({ message: 'Invalid input' }), (error) => reject(error)));
    }
    activateUserAccount(account, planKey, recaptchaResponse, action, invisibleRecaptcha) {
        return this.activateBackendUserAccount(account, planKey, recaptchaResponse, action, invisibleRecaptcha);
    }
    cancelUserAccount(userAccount, immediately = false) {
        return this.cancelBackendUserAccount(userAccount.mdn, userAccount.id, immediately);
    }
    cancelTrialUserAccount(userAccount) {
        return this.cancelTrialBackendUserAccount(userAccount.mdn, userAccount.id);
    }
    changePin(changeRequest, planKey) {
        return this.changePinBackend(changeRequest, planKey);
    }
    isValidPhoneNumber(mdn, recaptchaResponse, action, invisibleRecaptcha) {
        return new Promise((resolve, reject) => {
            this.getBackendIsUserAccountExist(mdn, recaptchaResponse, action, invisibleRecaptcha).then((result) => result ? resolve() : reject({ message: 'Account not found' }), (error) => reject({ message: error.message }));
        });
    }
    setNewAccountTimer() {
        this.isNewAccountActivatedSubject.next(true);
        setTimeout(() => this.isNewAccountActivatedSubject.next(false), 60000);
    }
    buildSubscriptions(_fbUser) {
        console.log('building subscriptions for user-account.service');
        const subscriptions = [];
        subscriptions.push(this.firebaseUserAccountsService.userAccounts.subscribe((accounts) => {
            if (!!accounts) {
                this.userAccountsSubject.next(accounts);
            }
        }));
        subscriptions.push(
        // reload account from backend.
        this.userPlansService.isSelectedPlanReady.subscribe((isReady) => {
            if (isReady) {
                const userId = _fbUser.uid;
                const selectedUserPlan = this.userPlansService.selectedUserPlan;
                if (!!selectedUserPlan && ((!!selectedUserPlan.mdn && !selectedUserPlan.portInRequestNumber)
                    || (!!selectedUserPlan.mdn && !!selectedUserPlan.portInRequestNumber && !!selectedUserPlan.portInStatus && selectedUserPlan.portInStatus == PortInStatusEnum.CONFIRMED))) {
                    this.reloadFbAccountUsingBackend(userId, selectedUserPlan).catch((error) => console.warn(error));
                }
                else {
                    this.isSyncingAccountSubject.next(false);
                }
            }
        }));
        subscriptions.push(this.userPlansService.selectedUserPlanObservable
            .pipe(combineLatest(this.userAccounts, (selectedPlan, userAccounts) => {
            return {
                userPlan: selectedPlan,
                fbUser: _fbUser,
                accounts: userAccounts
            };
        })).subscribe((userPlansAndAccounts) => {
            if (!!userPlansAndAccounts.userPlan && (!!userPlansAndAccounts.userPlan.mdn && !userPlansAndAccounts.userPlan.portInRequestNumber) && !!userPlansAndAccounts.fbUser) {
                this.selectUserAccount(userPlansAndAccounts.accounts.find((account) => account.id == userPlansAndAccounts.userPlan.id));
            }
            else {
                this.selectedAccountSubject.next(undefined);
            }
        }));
        return subscriptions;
    }
    onLoggedOut() {
        this.clearSelectUserAccount();
    }
    getUserIpAddress() {
        const header = new HttpHeaders()
            .set('Access-Control-Allow-Origin', 'https://good2go-test.ztarmobile.io | localhost');
        return new Promise((resolve, reject) => {
            this.http.get(`https://jsonip.com`, { headers: header, withCredentials: false }).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    addShippingAddress(address) {
        return new Promise(((resolve, reject) => {
            this.http.post(`${this.endpointUrl}/api/profile/shippingAddresses`, address).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    udpateShippingAddress(address, addressId) {
        return new Promise(((resolve, reject) => {
            this.http.put(`${this.endpointUrl}/api/profile/shippingAddresses/${addressId}`, address).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    checkApplicablePromotion(mdn, basePlanId) {
        return new Promise(((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/promotions/${mdn}/${basePlanId}`).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    checkAvailablePromotion(mdn) {
        return new Promise(((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/promotions?mdn=${mdn}`).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    setWifiCalling(planId, address) {
        if (typeof address.address1 === 'object') {
            address.address1 = address.address1['main_text'];
        }
        return new Promise(((resolve, reject) => {
            this.http.put(`${this.endpointUrl}/api/account/${planId}/wifi-calling`, address).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    disableWifiCalling(planId) {
        return new Promise((resolve, reject) => {
            this.http.delete(`${this.endpointUrl}/api/account/${planId}/wifi-calling`)
                .pipe(take(1)).subscribe((account) => resolve(account), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    enable5G(mdn) {
        return new Promise(((resolve, reject) => {
            this.http.put(`${this.endpointUrl}/api/account/${mdn}/tech5G`, {}).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    disable5G(mdn) {
        return new Promise((resolve, reject) => {
            this.http.delete(`${this.endpointUrl}/api/account/${mdn}/tech5G`)
                .pipe(take(1)).subscribe((account) => resolve(account), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    deleteEbb() {
        return new Promise((resolve, reject) => {
            this.http.delete(`${this.endpointUrl}/api/profile/ebb`)
                .pipe(take(1)).subscribe((account) => resolve(account), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    getAccountLevelInfo() {
        return new Promise(((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/account`).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    setAccountDefaultCard(setDefault, paymentMethodKey) {
        const body = { setDefault, paymentMethodKey };
        if (!setDefault) {
            delete body.paymentMethodKey;
        }
        return new Promise(((resolve, reject) => {
            this.http.patch(`${this.endpointUrl}/api/account/payment-methods/default`, body).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    getAccountDefaultCard() {
        return new Promise(((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/account/payment-methods/default`).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    reloadFbAccountUsingBackend(userId, userPlan) {
        return new Promise((resolve, reject) => {
            this.isSyncingAccountSubject.next(true);
            this.syncBackendUserAccountByUserPlanId(userPlan.id, userPlan.mdn).then((backendUserAccount) => {
                this.isSyncingAccountSubject.next(false);
                resolve(backendUserAccount);
            }, (error) => {
                reject(error);
                // true since we want the page to not load.
                // almothafar: this is keeping annoying progress bar working, we need to handle page loading in another way
                if (error.status === 404) {
                    this.selectedAccountSubject.next(undefined);
                }
                this.isSyncingAccountSubject.next(false);
            });
        });
    }
    clearSelectUserAccount() {
        this.userAccountsSubject.next([]);
        this.selectedAccountSubject.next(undefined);
    }
    selectUserAccount(userAccount) {
        if (!!userAccount) {
            this.selectedAccountSubject.next(userAccount);
        }
    }
    getBackendUserAccountByUserPlanId(userPlanId, mdn) {
        return new Promise((resolve, reject) => {
            if (!!userPlanId && !!mdn) {
                this.http.get(`${this.endpointUrl}/api/plan/${userPlanId}/account/${mdn}`)
                    .pipe(take(1)).subscribe((account) => resolve(account), (error) => this.http.handlePromiseError(error, reject));
            }
            else {
                reject({ message: 'User plan id or MDN is missing ' });
            }
        });
    }
    syncBackendUserAccountByUserPlanId(userPlanId, mdn) {
        return new Promise((resolve, reject) => {
            if (!!userPlanId && !!mdn) {
                this.http.get(`${this.endpointUrl}/api/plan/${userPlanId}/account/${mdn}/sync`)
                    .pipe(take(1)).subscribe((account) => resolve(account), (error) => {
                    console.warn('sync error ', error);
                    this.http.handlePromiseError(error, reject);
                });
            }
            else {
                reject({ message: 'User plan id or MDN is missing ' });
            }
        });
    }
    cancelBackendUserAccount(mdn, planKey, immediately) {
        return new Promise((resolve, reject) => {
            this.http.delete(`${this.endpointUrl}/api/account/${mdn}/plan/${planKey}/${immediately}`)
                .pipe(take(1)).subscribe((account) => resolve(account), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    cancelTrialBackendUserAccount(mdn, planKey) {
        return new Promise((resolve, reject) => {
            this.http.delete(`${this.endpointUrl}/api/account/trial/${mdn}/plan/${planKey}`)
                .pipe(take(1)).subscribe((account) => resolve(account), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    getBackendIsUserAccountExist(mdn, recaptchaResponse, action, invisibleRecaptcha) {
        let header = new HttpHeaders()
            .set('reCaptcha', recaptchaResponse);
        if (!!action) {
            header = header.set('action', action);
        }
        header = header.set('invisibleRecaptcha', `${!!invisibleRecaptcha}`);
        return new Promise((resolve, reject) => {
            this.http.getWithOptions(`${this.endpointUrl}/api/account/check/${mdn}`, '', header)
                .pipe(take(1)).subscribe((account) => resolve(account.exist), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    activateBackendUserAccount(account, userPlanId, recaptchaResponse, action, invisibleRecaptcha) {
        let header = new HttpHeaders()
            .set('reCaptcha', recaptchaResponse);
        if (!!action) {
            header = header.set('action', action);
        }
        header = header.set('invisibleRecaptcha', `${!!invisibleRecaptcha}`);
        return new Promise((resolve, reject) => {
            this.http.postWithHeader(`${this.endpointUrl}/api/plan/${userPlanId}/activate`, account, header)
                .pipe(take(1)).subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    npaListBackendUserAccount(zip) {
        return new Promise((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/npa`, { zip }).pipe(take(1)).subscribe((npas) => resolve(npas), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    changePinBackend(changeRequest, userPlanId) {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.endpointUrl}/api/account/${userPlanId}/changePin`, changeRequest).pipe(take(1)).subscribe(() => resolve(), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UserAccountService, deps: [{ token: EndpointUrl }, { token: FirebaseUserAccountsService }, { token: UserPlansService }, { token: i1.SimpleAuthService }, { token: i1.AuthHttp }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UserAccountService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UserAccountService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [EndpointUrl]
                }] }, { type: FirebaseUserAccountsService }, { type: UserPlansService }, { type: i1.SimpleAuthService }, { type: i1.AuthHttp }] });

// tslint:disable:typedef
// tslint:disable:max-line-length
class MobileCustomPlansService {
    static { this.PLANS_STEPS = {
        PLAN: 'plan', CHILDREN_PLANS: 'child-plans', CHECKOUT: 'check-out'
    }; }
    static { this.ADD_ONS_TYPES = {
        BASE_PLAN: 'base-plan', DATA_ADD_ON: 'add-on-data', TALK_AND_TEXT_ADD_ON: 'add-on-talkandtext', INTERNATIONAL_TALK_ADD_ON: 'international-talk'
    }; }
    constructor(endpointUrl, plansConf, simpleAuthService, userPlansService, userAccountService, http, firebaseMobilePlansCartService) {
        this.endpointUrl = endpointUrl;
        this.plansConf = plansConf;
        this.simpleAuthService = simpleAuthService;
        this.userPlansService = userPlansService;
        this.userAccountService = userAccountService;
        this.http = http;
        this.firebaseMobilePlansCartService = firebaseMobilePlansCartService;
        this.isPlansConfigurationReadySubject = new ReplaySubject(1);
        this.simCardConfigSubject = new ReplaySubject(1);
        this.isConfigurationReadySubject = new ReplaySubject(1);
        this.currentPlanSubject = new ReplaySubject(1);
        this.currentAddonsSubject = new ReplaySubject(1);
        this.currentPlan = this.currentPlanSubject.asObservable().pipe(debounceTime(50));
        this.isPlansConfigurationReady = this.isPlansConfigurationReadySubject.asObservable();
        this.isConfigurationReady = this.isConfigurationReadySubject.asObservable();
        this.simCardConfig = this.simCardConfigSubject.asObservable();
        this.currentAddons = this.currentAddonsSubject.asObservable().pipe(debounceTime(50));
        // temp config TODO move to firebase
        this.simCardConfigSubject.next({ id: 'SIM_CARD', title: 'Sim Card', type: 'SIM_CARD', price: 10, details: null, quantity: 1 });
        this.loadPlansConfig();
        this.simpleAuthService.registerDependentService('mobile-plans', this);
    }
    setMarketingObject(marketingObject) {
        return this.updateUserCart({ marketingObject });
    }
    setFinancingDetails(details) {
        return this.updateUserCart({ financingDetails: details });
    }
    setAcpDevice(acpDevice) {
        return this.updateUserCart({ acpDevice });
    }
    setAgentCode(agentCode) {
        return this.updateUserCart({ agentCode });
    }
    setCatalogArea(catalogArea) {
        return this.updateUserCart({ catalogArea });
    }
    seteSIM(eSIM) {
        return this.updateUserCart({ eSIM });
    }
    setQrScanned(qrScanned) {
        return this.updateUserCart({ qrScanned });
    }
    setStorePickup(storePickup) {
        return this.updateUserCart({ storePickup });
    }
    setPhones(phones) {
        const phoneObj = Object.assign({}, phones);
        return this.updateUserCart({ phones: phoneObj });
    }
    setMigrationDetail(phoneNumber, pin) {
        return this.updateUserCart({ phoneNumber, pin });
    }
    getNoPlanMobilePlan() {
        const mobilePlan = new CustomizableMobilePlan('No Plan');
        mobilePlan.basePlan = new GenericMobilePlanItem('NO-PLAN', 'No Plan', '', 0, 'base-plan', new MobilePlanDetails(0, 0, 0, 0, 0), null, true, '', '');
        return mobilePlan;
    }
    buildSubscriptions(fbUser) {
        console.log('building subscriptions for mobile-plans.service');
        const subscriptions = [];
        this.fbUser = fbUser;
        subscriptions.push(this.firebaseMobilePlansCartService.userCart.subscribe((userCart) => {
            if (!!userCart) {
                console.log('Loading saved cart');
                if (!!userCart.basePlan) {
                    this.currentPlanSubject.next(this.fillCurrentPlanFromUserCart(userCart));
                }
                else {
                    this.setBasePlan(this.getNoPlanMobilePlan());
                }
            }
            else {
                this.currentPlanSubject.next(this.getNoPlanMobilePlan());
            }
        }));
        subscriptions.push(this.subscribeWithUserPlansAndAccountsWithConfig());
        return subscriptions;
    }
    onLoggedOut() {
        this.fbUser = null;
        this.currentPlanSubject.next(this.getNoPlanMobilePlan());
    }
    updateCartProperties(partial) {
        return this.updateUserCart({ ...partial });
    }
    setBasePlan(item) {
        if (!!item) {
            const basePlan = { id: item.id, price: item.price, data: item?.details?.data, type: item.type, parentId: item.parentId };
            if (!basePlan.data) {
                delete basePlan.data;
            }
            if (!basePlan.parentId) {
                delete basePlan.parentId;
            }
            return this.updateUserCart({ basePlan });
        }
        else {
            return this.setBasePlan(this.getNoPlanMobilePlan().basePlan);
        }
    }
    setGo2Plan(go2Plan) {
        return this.updateUserCart({ go2Plan });
    }
    setGo2SIM(go2SIM) {
        return this.updateUserCart({ go2SIM });
    }
    // if addons is null removes addon from cart, otherwuse set cart addons
    setAddonsList(addons, item) {
        if (!!item) {
            const basePlan = { id: item.id, type: item.type, parentId: item.parentId, price: item.price };
            if (!basePlan.parentId) {
                delete basePlan.parentId;
            }
            const addOns = [];
            if (addons) {
                addons.forEach((addOn) => {
                    const callAddOn = { id: addOn.id, type: addOn.type, price: addOn.price, quantity: addOn.quantity };
                    if (!callAddOn.parentId) {
                        delete callAddOn.parentId;
                    }
                    addOns.push(callAddOn);
                });
                return this.updateUserCart({ basePlan, addOns });
            }
            else {
                return this.firebaseMobilePlansCartService.deleteFromUserCart('addOns');
            }
        }
        else {
            // todo look into clearing addons for No Plan
            return this.setBasePlan(this.getNoPlanMobilePlan().basePlan);
        }
    }
    setTaxesAndFees(taxes, fees) {
        return this.updateUserCart({ taxes, fees });
    }
    setPromoCodes(promoCodes) {
        return this.updateUserCart({ promoCodes });
    }
    setReferralId(referralCode) {
        return this.updateUserCart({ referralCode });
    }
    setAutoRenewPlan(autoRenewPlan) {
        return this.updateUserCart({ autoRenewPlan });
    }
    setPlanDevice(planDevice) {
        if (!!planDevice) {
            const planDeviceCleanedUp = Object.assign({}, omitBy(planDevice, isNil));
            return this.updateUserCart({ planDevice: planDeviceCleanedUp });
        }
        else {
            return this.firebaseMobilePlansCartService.deleteFromUserCart('planDevice');
        }
    }
    setPlanExpectedDevice(planExpectedDevice) {
        if (!!planExpectedDevice) {
            return this.updateUserCart({ planExpectedDevice });
        }
        else {
            return this.firebaseMobilePlansCartService.deleteFromUserCart('planExpectedDevice');
        }
    }
    setSimCard(activationCode) {
        if (!!activationCode && activationCode !== '') {
            this.removeVoucherCode();
            return this.updateUserCart({ activationCode });
        }
        else {
            return this.firebaseMobilePlansCartService.deleteFromUserCart('activationCode');
        }
    }
    removeVoucherCode() {
        // had to add this because setting Object to null wasn't removing it
        this.callLoadPlansConfig();
        return this.firebaseMobilePlansCartService.deleteFromUserCart('voucherData');
    }
    removeVoucherData() {
        return this.firebaseMobilePlansCartService.deleteFromUserCart('voucherData');
    }
    removePhonesFromCart() {
        return this.firebaseMobilePlansCartService.deleteFromUserCart('phones');
    }
    setVoucherCode(voucherData) {
        this.callLoadPlansConfig(voucherData);
        return this.updateUserCart({ voucherData });
    }
    setVoucherData(voucherData) {
        return this.updateUserCart({ voucherData });
    }
    setSimPurchaseQuantity(simsQuantity) {
        return this.updateUserCart({ simsQuantity });
    }
    calculateTaxesAndFees(purchaseDetails) {
        return new Promise(((resolve, reject) => {
            this.http.patch(`${this.endpointUrl}/api/shop/calculateTaxes`, purchaseDetails).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    calculateAddonsTaxes(userPlanId, purchaseDetails) {
        return new Promise(((resolve, reject) => {
            this.http.patch(`${this.endpointUrl}/api/shop/addon/${userPlanId}/calculateTaxes`, purchaseDetails).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    calculateAddonSIMTaxesAndFees(userPlanId, purchaseDetails) {
        return new Promise(((resolve, reject) => {
            this.http.patch(`${this.endpointUrl}/api/shop/addon/${userPlanId}/calculateAddonSimTaxes`, purchaseDetails).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    calculateTopupTaxesAndFees(userPlanId, purchaseDetails) {
        return new Promise(((resolve, reject) => {
            this.http.patch(`${this.endpointUrl}/api/shop/topup/${userPlanId}/calculateTaxes`, purchaseDetails).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    getSelectedValueForOptionType(plan, type) {
        // just for note, this is currently used and tested for only type = 'base-plan'
        if (plan === undefined) {
            return undefined;
        }
        let field = '';
        switch (type) {
            case MobileCustomPlansService.ADD_ONS_TYPES.TALK_AND_TEXT_ADD_ON:
                field = 'talkAndTextAddOn';
                break;
            case MobileCustomPlansService.ADD_ONS_TYPES.DATA_ADD_ON:
                field = 'dataAddOn';
                break;
            case MobileCustomPlansService.ADD_ONS_TYPES.BASE_PLAN:
                field = 'basePlan';
                break;
            case MobileCustomPlansService.ADD_ONS_TYPES.INTERNATIONAL_TALK_ADD_ON:
                field = 'internationalTalkAddOn';
                break;
            default:
        }
        if (field !== '') {
            return plan[field];
        }
        return null;
    }
    getBundleIdOfPlan(plan) {
        return this.getDefaultBundleIdOfPlanById(plan.id);
    }
    getDefaultBundleIdOfPlanById(planId) {
        const founded = this.config.allPlans.find((firebasePlan) => firebasePlan.id === planId);
        return !!founded ? founded.bundleId : undefined;
    }
    getAllBundleIdsOfPlanById(planId) {
        const founded = this.config.allPlans.find((firebasePlan) => firebasePlan.id === planId);
        if (!!founded) {
            const allBundleIds = [];
            allBundleIds.push(founded.bundleId);
            if (!!founded.altBundleIds) {
                allBundleIds.concat(founded.altBundleIds);
            }
            return allBundleIds;
        }
        return [];
    }
    getMobilePlanByBundleId(bundleId) {
        if (!!bundleId) {
            const plan = this.config.allPlans.find((firebasePlan) => (firebasePlan.bundleId === bundleId)
                // OR if there is no plan with bundleId, look for alternative IDs (such as expired or another tech type ID).
                || (!!firebasePlan.altBundleIds && firebasePlan.altBundleIds.indexOf(bundleId) >= 0));
            // TODO use "includes(bundleId)" instead of "indexOf(bundleId)" >= 0 if we ever updated to es7 (ES2016)
            if (!!plan) {
                return new GenericMobilePlanItem(plan.id, plan.title, plan.subtitle, plan.price, 'base-plan', new MobilePlanDetails((plan.minutes || 0), plan.messages, plan.data, plan.mms, plan.extraData), plan.parentId, plan.virtual, plan.buttonText, plan.extTitle, plan.description, plan.promoMessage, plan.promoCode, plan.promoPrice, plan.promoMonths);
            }
        }
        return undefined;
    }
    startNewMobilePlan() {
        this.callLoadPlansConfig();
        return this.firebaseMobilePlansCartService.clearUserCart();
    }
    clearUserCart() {
        return this.firebaseMobilePlansCartService.clearUserCart();
    }
    setCartType(cartType) {
        return this.updateUserCart({ cartType });
    }
    setReferralCode(referralCode) {
        return this.updateUserCart({ referralCode });
    }
    setActivePlanId(activePlanId) {
        return this.updateUserCart({ activePlanId });
    }
    loadPlansConfig(voucherData) {
        console.log('loadPlansConfig');
        return this.callLoadPlansConfig(voucherData);
    }
    callLoadPlansConfig(voucherData) {
        this.plansConf.planConfiguration.subscribe((config) => {
            this.config = config;
            this.config.activePlans = (new OrderByPipe()).transform(this.config.activePlans, 'order');
            const plans = this.config.activePlans
                .map((plan) => new GenericMobilePlanItem(plan.id, plan.title, plan.subtitle, plan.price, MobileCustomPlansService.ADD_ONS_TYPES.BASE_PLAN, new MobilePlanDetails((plan.minutes || 0), plan.messages, plan.data, plan.mms, plan.extraData), plan.parentId, plan.virtual, plan.buttonText, plan.extTitle, plan.description, plan.promoMessage, plan.promoCode, plan.promoPrice, plan.promoMonths, plan.cycleDays, plan.promoMoPrice, plan.subscriptionCycles, plan.category, plan.annualSaving, plan.isSpecialPromotion, plan.specialPromotion, plan.availableForChange, plan.isVacation, plan.promoAvailableForChange, plan.multiLineDisount, plan.ebb, plan.subscriptionPrice, plan.subscriptionPromoPrice, null, plan?.data, plan?.hotspotData, plan?.uniquePlanIdentifier, plan?.bundleId, plan?.isGo2Plan, plan?.go2PlansAvailable));
            this.parentBasePlans = plans.filter((plan) => plan.parentId === undefined);
            this.allBasePlans = plans;
            if (!!voucherData) {
                // note: filtering parentBasePlans isn't needed, actually the FE components is handling it,
                // wasn't an issue on price filtering because the categories were being filtered by the lowest price,
                // left it on the price filtering since it was working like that need to investigate side effects
                if (!!voucherData.applicablePlans) {
                    // Filtering by applicable plans
                    // this.parentBasePlans = this.parentBasePlans.filter((plan) => plan.price <= voucherData.limit);
                    // TODO use "includes(id)" instead of "indexOf(id)" >= 0 if we ever updated to es7 (ES2016)
                    this.allBasePlans = this.allBasePlans.filter((plan) => this.getAllBundleIdsOfPlanById(plan.id).some((id) => voucherData.applicablePlans.indexOf(id) >= 0));
                }
                else {
                    console.warn('There are no applicable plans assigned to this voucher');
                }
            }
            this.isConfigurationReadySubject.next(true);
        });
    }
    fillCurrentPlanFromUserCart(userCart) {
        const plan = this.userPlansService.getCustomizableMobilePlanFromUserPlan(userCart);
        if (!!userCart && !!plan) {
            plan.simsQuantity = userCart.simsQuantity;
            plan.activationCode = userCart.activationCode;
            plan.voucherData = userCart.voucherData;
            plan.taxes = userCart.taxes || 0;
            plan.fees = userCart.fees || 0;
            plan.autoRenewPlan = userCart.autoRenewPlan;
            plan.locked = userCart.locked;
            plan.mdn = userCart.mdn;
            plan.cartType = userCart.cartType;
            plan.planExpectedDevice = userCart.planExpectedDevice;
            plan.activePlanId = userCart.activePlanId;
            plan.promoCodes = userCart.promoCodes;
            plan.referralCode = userCart.referralCode;
            plan.marketingObject = userCart.marketingObject;
            plan.phoneNumber = userCart.phoneNumber;
            plan.pin = userCart.pin;
            plan.eSIM = userCart.eSIM;
            plan.qrScanned = userCart.qrScanned;
            plan.storePickup = userCart.storePickup;
            plan.financingDetails = userCart.financingDetails;
            plan.acpDevice = userCart.acpDevice;
            plan.agentCode = userCart.agentCode;
            plan.catalogArea = userCart.catalogArea;
            if (!!userCart.phones) {
                plan.phones = Object.values(userCart.phones);
            }
            plan.go2Plan = userCart.go2Plan;
            plan.go2SIM = userCart.go2SIM;
            return plan;
        }
    }
    updateUserCart(partialCart) {
        // this.firebaseMobilePlansCartService.updateUserCart not always returns a promise directly, which results in an script error stopping it.
        // almothafar: I made the fix, the fix should be inside 'FirebaseMobilePlansCartService.updateUserCart' anyway
        return this.firebaseMobilePlansCartService.updateUserCart(partialCart)
            .then(() => Promise.resolve(), (error) => console.warn(error));
    }
    getFilteredAddOns(config, type, bundleId) {
        const optionsItem = new OptionsMobilePlanItem();
        let addOnOptions = config.planAddOns.filter((addOn) => {
            let isCompatible = true;
            const compatibleWith = addOn.compatibleWith instanceof Array ? addOn.compatibleWith : (addOn.compatibleWith.toString() || '').split(',').map((id) => id.trim());
            if (!!compatibleWith && !!bundleId) {
                const foundBundleId = compatibleWith.find((id) => id === bundleId);
                isCompatible = !!foundBundleId;
            }
            return addOn.type === type && isCompatible;
        }).map((option) => new GenericMobilePlanItem(option.id, option.title, option.subtitle, option.price, option.type, new MobilePlanDetails((option.minutes || 0), (option.messages || 0), (option.data || 0), (option.mms || 0)), null, null, null, null, option.description));
        if (addOnOptions.length > 0) {
            addOnOptions = (new OrderByPipe()).transform(addOnOptions, 'price');
            for (const option of addOnOptions) {
                optionsItem.addOption(option);
            }
            optionsItem.selectOption(addOnOptions[0]);
            return optionsItem;
        }
        else {
            return null;
        }
    }
    subscribeWithUserPlansAndAccountsWithConfig() {
        return this.isConfigurationReady
            .pipe(combineLatest(this.userAccountService.selectedAccount, this.userAccountService.isSyncingAccount, (configReady, userAccount, syncingAccount) => {
            if (configReady && !syncingAccount) {
                const bundleId = !!userAccount && !!userAccount.plan ? userAccount.plan.bundleId : undefined;
                const dataOptionsItem = this.getFilteredAddOns(this.config, MobileCustomPlansService.ADD_ONS_TYPES.DATA_ADD_ON, bundleId);
                const talkAndTextOptionsItem = this.getFilteredAddOns(this.config, MobileCustomPlansService.ADD_ONS_TYPES.TALK_AND_TEXT_ADD_ON, bundleId);
                const internationalTalkOptionsItem = this.getFilteredAddOns(this.config, MobileCustomPlansService.ADD_ONS_TYPES.INTERNATIONAL_TALK_ADD_ON, bundleId);
                this.allAddOns = [];
                this.allAddOns = this.allAddOns.concat(talkAndTextOptionsItem, dataOptionsItem, internationalTalkOptionsItem).filter((addOn) => addOn != null);
                return true;
            }
            else {
                return false;
            }
        })).subscribe((ready) => this.isPlansConfigurationReadySubject.next(ready));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: MobileCustomPlansService, deps: [{ token: EndpointUrl }, { token: PlansConfigurationService }, { token: i1.SimpleAuthService }, { token: UserPlansService }, { token: UserAccountService }, { token: i1.AuthHttp }, { token: FirebaseMobilePlansCartService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: MobileCustomPlansService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: MobileCustomPlansService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [EndpointUrl]
                }] }, { type: PlansConfigurationService }, { type: i1.SimpleAuthService }, { type: UserPlansService }, { type: UserAccountService }, { type: i1.AuthHttp }, { type: FirebaseMobilePlansCartService }] });

class UserAccountServiceNoSync {
    constructor(endpointUrl, firebaseUserAccountsService, userPlansService, simpleAuthService, http) {
        this.endpointUrl = endpointUrl;
        this.firebaseUserAccountsService = firebaseUserAccountsService;
        this.userPlansService = userPlansService;
        this.simpleAuthService = simpleAuthService;
        this.http = http;
        this.userAccountsSubject = new ReplaySubject(1);
        this.selectedAccountSubject = new ReplaySubject(1);
        this.isNewAccountActivatedSubject = new ReplaySubject(1);
        this.userAccounts = this.userAccountsSubject.asObservable();
        this.selectedAccount = this.selectedAccountSubject.asObservable();
        this.isNewAccountActivated = this.isNewAccountActivatedSubject.asObservable();
        this.simpleAuthService.registerDependentService('user-account', this);
    }
    getAvailableLocalAreaNumbers(zip) {
        return new Promise((resolve, reject) => this.npaListBackendUserAccount(zip)
            .then((npa) => !!npa && !!npa.numberingPlanArea ? resolve(npa.numberingPlanArea) : reject({ message: 'Invalid input' }), (error) => reject(error)));
    }
    activateUserAccount(account, planKey, recaptchaResponse, action, invisibleRecaptcha) {
        return this.activateBackendUserAccount(account, planKey, recaptchaResponse, action, invisibleRecaptcha);
    }
    cancelUserAccount(userAccount, immediately = false) {
        return this.cancelBackendUserAccount(userAccount.mdn, userAccount.id, immediately);
    }
    cancelTrialUserAccount(userAccount) {
        return this.cancelTrialBackendUserAccount(userAccount.mdn, userAccount.id);
    }
    changePin(changeRequest, planKey) {
        return this.changePinBackend(changeRequest, planKey);
    }
    isValidPhoneNumber(mdn, recaptchaResponse, action, invisibleRecaptcha) {
        return new Promise((resolve, reject) => {
            this.getBackendIsUserAccountExist(mdn, recaptchaResponse, action, invisibleRecaptcha).then((result) => result ? resolve() : reject({ message: 'Account not found' }), (error) => reject({ message: error.message }));
        });
    }
    setNewAccountTimer() {
        this.isNewAccountActivatedSubject.next(true);
        setTimeout(() => this.isNewAccountActivatedSubject.next(false), 60000);
    }
    buildSubscriptions(_fbUser) {
        console.log('building subscriptions for user-account-no-sync.service');
        const subscriptions = [];
        subscriptions.push(this.firebaseUserAccountsService.userAccounts.subscribe((accounts) => {
            if (!!accounts) {
                this.userAccountsSubject.next(accounts);
            }
        }));
        subscriptions.push(this.userPlansService.selectedUserPlanObservable
            .pipe(combineLatest(this.userAccounts, (selectedPlan, userAccounts) => {
            return {
                userPlan: selectedPlan,
                fbUser: _fbUser,
                accounts: userAccounts
            };
        })).subscribe((userPlansAndAccounts) => {
            if (!!userPlansAndAccounts.userPlan && (!!userPlansAndAccounts.userPlan.mdn && !userPlansAndAccounts.userPlan.portInRequestNumber) && !!userPlansAndAccounts.fbUser) {
                this.selectUserAccount(userPlansAndAccounts.accounts.find((account) => account.id == userPlansAndAccounts.userPlan.id));
            }
            else {
                this.selectedAccountSubject.next(undefined);
            }
        }));
        return subscriptions;
    }
    onLoggedOut() {
        this.clearSelectUserAccount();
    }
    getUserIpAddress() {
        const header = new HttpHeaders()
            .set('Access-Control-Allow-Origin', 'https://good2go-test.ztarmobile.io | localhost');
        return new Promise((resolve, reject) => {
            this.http.get(`https://jsonip.com`, { headers: header, withCredentials: false }).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    addShippingAddress(address) {
        return new Promise(((resolve, reject) => {
            this.http.post(`${this.endpointUrl}/api/profile/shippingAddresses`, address).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    udpateShippingAddress(address, addressId) {
        return new Promise(((resolve, reject) => {
            this.http.put(`${this.endpointUrl}/api/profile/shippingAddresses/${addressId}`, address).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    checkApplicablePromotion(mdn, basePlanId) {
        return new Promise(((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/promotions/${mdn}/${basePlanId}`).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    checkAvailablePromotion(mdn) {
        return new Promise(((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/promotions?mdn=${mdn}`).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    setWifiCalling(planId, address) {
        if (typeof address.address1 === 'object') {
            address.address1 = address.address1['main_text'];
        }
        return new Promise(((resolve, reject) => {
            this.http.put(`${this.endpointUrl}/api/account/${planId}/wifi-calling`, address).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    disableWifiCalling(planId) {
        return new Promise((resolve, reject) => {
            this.http.delete(`${this.endpointUrl}/api/account/${planId}/wifi-calling`)
                .pipe(take(1)).subscribe((account) => resolve(account), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    enable5G(mdn) {
        return new Promise(((resolve, reject) => {
            this.http.put(`${this.endpointUrl}/api/account/${mdn}/tech5G`, {}).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    disable5G(mdn) {
        return new Promise((resolve, reject) => {
            this.http.delete(`${this.endpointUrl}/api/account/${mdn}/tech5G`)
                .pipe(take(1)).subscribe((account) => resolve(account), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    deleteEbb() {
        return new Promise((resolve, reject) => {
            this.http.delete(`${this.endpointUrl}/api/profile/ebb`)
                .pipe(take(1)).subscribe((account) => resolve(account), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    getAccountLevelInfo() {
        return new Promise(((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/account`).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    setAccountDefaultCard(setDefault, paymentMethodKey) {
        const body = { setDefault, paymentMethodKey };
        if (!setDefault) {
            delete body.paymentMethodKey;
        }
        return new Promise(((resolve, reject) => {
            this.http.patch(`${this.endpointUrl}/api/account/payment-methods/default`, body).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    getAccountDefaultCard() {
        return new Promise(((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/account/payment-methods/default`).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    clearSelectUserAccount() {
        this.userAccountsSubject.next([]);
        this.selectedAccountSubject.next(undefined);
    }
    selectUserAccount(userAccount) {
        if (!!userAccount) {
            this.selectedAccountSubject.next(userAccount);
        }
    }
    getBackendUserAccountByUserPlanId(userPlanId, mdn) {
        return new Promise((resolve, reject) => {
            if (!!userPlanId && !!mdn) {
                this.http.get(`${this.endpointUrl}/api/plan/${userPlanId}/account/${mdn}`)
                    .pipe(take(1)).subscribe((account) => resolve(account), (error) => this.http.handlePromiseError(error, reject));
            }
            else {
                reject({ message: 'User plan id or MDN is missing ' });
            }
        });
    }
    cancelBackendUserAccount(mdn, planKey, immediately) {
        return new Promise((resolve, reject) => {
            this.http.delete(`${this.endpointUrl}/api/account/${mdn}/plan/${planKey}/${immediately}`)
                .pipe(take(1)).subscribe((account) => resolve(account), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    cancelTrialBackendUserAccount(mdn, planKey) {
        return new Promise((resolve, reject) => {
            this.http.delete(`${this.endpointUrl}/api/account/trial/${mdn}/plan/${planKey}`)
                .pipe(take(1)).subscribe((account) => resolve(account), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    getBackendIsUserAccountExist(mdn, recaptchaResponse, action, invisibleRecaptcha) {
        let header = new HttpHeaders()
            .set('reCaptcha', recaptchaResponse);
        if (!!action) {
            header = header.set('action', action);
        }
        header = header.set('invisibleRecaptcha', `${!!invisibleRecaptcha}`);
        return new Promise((resolve, reject) => {
            this.http.getWithOptions(`${this.endpointUrl}/api/account/check/${mdn}`, '', header)
                .pipe(take(1)).subscribe((account) => resolve(account.exist), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    activateBackendUserAccount(account, userPlanId, recaptchaResponse, action, invisibleRecaptcha) {
        let header = new HttpHeaders()
            .set('reCaptcha', recaptchaResponse);
        if (!!action) {
            header = header.set('action', action);
        }
        header = header.set('invisibleRecaptcha', `${!!invisibleRecaptcha}`);
        return new Promise((resolve, reject) => {
            this.http.postWithHeader(`${this.endpointUrl}/api/plan/${userPlanId}/activate`, account, header)
                .pipe(take(1)).subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    npaListBackendUserAccount(zip) {
        return new Promise((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/npa`, { zip }).pipe(take(1)).subscribe((npas) => resolve(npas), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    changePinBackend(changeRequest, userPlanId) {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.endpointUrl}/api/account/${userPlanId}/changePin`, changeRequest).pipe(take(1)).subscribe(() => resolve(), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UserAccountServiceNoSync, deps: [{ token: EndpointUrl }, { token: FirebaseUserAccountsService }, { token: UserPlansService }, { token: i1.SimpleAuthService }, { token: i1.AuthHttp }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UserAccountServiceNoSync, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UserAccountServiceNoSync, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [EndpointUrl]
                }] }, { type: FirebaseUserAccountsService }, { type: UserPlansService }, { type: i1.SimpleAuthService }, { type: i1.AuthHttp }] });

// tslint:disable:typedef
// tslint:disable:max-line-length
class MobileCustomPlansServiceNoSync {
    static { this.PLANS_STEPS = {
        PLAN: 'plan', CHILDREN_PLANS: 'child-plans', CHECKOUT: 'check-out'
    }; }
    static { this.ADD_ONS_TYPES = {
        BASE_PLAN: 'base-plan', DATA_ADD_ON: 'add-on-data', TALK_AND_TEXT_ADD_ON: 'add-on-talkandtext', INTERNATIONAL_TALK_ADD_ON: 'international-talk'
    }; }
    constructor(endpointUrl, plansConf, simpleAuthService, userPlansService, userAccountService, http, firebaseMobilePlansCartService) {
        this.endpointUrl = endpointUrl;
        this.plansConf = plansConf;
        this.simpleAuthService = simpleAuthService;
        this.userPlansService = userPlansService;
        this.userAccountService = userAccountService;
        this.http = http;
        this.firebaseMobilePlansCartService = firebaseMobilePlansCartService;
        this.isPlansConfigurationReadySubject = new ReplaySubject(1);
        this.simCardConfigSubject = new ReplaySubject(1);
        this.isConfigurationReadySubject = new ReplaySubject(1);
        this.currentPlanSubject = new ReplaySubject(1);
        this.currentAddonsSubject = new ReplaySubject(1);
        this.currentPlan = this.currentPlanSubject.asObservable().pipe(debounceTime(50));
        this.isPlansConfigurationReady = this.isPlansConfigurationReadySubject.asObservable();
        this.isConfigurationReady = this.isConfigurationReadySubject.asObservable();
        this.simCardConfig = this.simCardConfigSubject.asObservable();
        this.currentAddons = this.currentAddonsSubject.asObservable().pipe(debounceTime(50));
        // temp config TODO move to firebase
        this.simCardConfigSubject.next({ id: 'SIM_CARD', title: 'Sim Card', type: 'SIM_CARD', price: 10, details: null, quantity: 1 });
        this.loadPlansConfig();
        this.simpleAuthService.registerDependentService('mobile-plans', this);
    }
    setMarketingObject(marketingObject) {
        return this.updateUserCart({ marketingObject });
    }
    setFinancingDetails(details) {
        return this.updateUserCart({ financingDetails: details });
    }
    setAcpDevice(acpDevice) {
        return this.updateUserCart({ acpDevice });
    }
    setAgentCode(agentCode) {
        return this.updateUserCart({ agentCode });
    }
    setCatalogArea(catalogArea) {
        return this.updateUserCart({ catalogArea });
    }
    seteSIM(eSIM) {
        return this.updateUserCart({ eSIM });
    }
    setQrScanned(qrScanned) {
        return this.updateUserCart({ qrScanned });
    }
    setStorePickup(storePickup) {
        return this.updateUserCart({ storePickup });
    }
    setPhones(phones) {
        const phoneObj = Object.assign({}, phones);
        return this.updateUserCart({ phones: phoneObj });
    }
    setMigrationDetail(phoneNumber, pin) {
        return this.updateUserCart({ phoneNumber, pin });
    }
    getNoPlanMobilePlan() {
        const mobilePlan = new CustomizableMobilePlan('No Plan');
        mobilePlan.basePlan = new GenericMobilePlanItem('NO-PLAN', 'No Plan', '', 0, 'base-plan', new MobilePlanDetails(0, 0, 0, 0, 0), null, true, '', '');
        return mobilePlan;
    }
    buildSubscriptions(fbUser) {
        console.log('building subscriptions for mobile-plans-no-sync.service');
        const subscriptions = [];
        this.fbUser = fbUser;
        subscriptions.push(this.firebaseMobilePlansCartService.userCart.subscribe((userCart) => {
            if (!!userCart) {
                console.log('Loading saved cart');
                if (!!userCart.basePlan) {
                    this.currentPlanSubject.next(this.fillCurrentPlanFromUserCart(userCart));
                }
                else {
                    this.setBasePlan(this.getNoPlanMobilePlan());
                }
            }
            else {
                this.currentPlanSubject.next(this.getNoPlanMobilePlan());
            }
        }));
        subscriptions.push(this.subscribeWithUserPlansAndAccountsWithConfig());
        return subscriptions;
    }
    onLoggedOut() {
        this.fbUser = null;
        this.currentPlanSubject.next(this.getNoPlanMobilePlan());
    }
    updateCartProperties(partial) {
        return this.updateUserCart({ ...partial });
    }
    setBasePlan(item) {
        if (!!item) {
            const basePlan = { id: item.id, price: item.price, data: item?.details?.data, type: item.type, parentId: item.parentId };
            if (!basePlan.data) {
                delete basePlan.data;
            }
            if (!basePlan.parentId) {
                delete basePlan.parentId;
            }
            return this.updateUserCart({ basePlan });
        }
        else {
            return this.setBasePlan(this.getNoPlanMobilePlan().basePlan);
        }
    }
    setGo2Plan(go2Plan) {
        return this.updateUserCart({ go2Plan });
    }
    setGo2SIM(go2SIM) {
        return this.updateUserCart({ go2SIM });
    }
    // if addons is null removes addon from cart, otherwuse set cart addons
    setAddonsList(addons, item) {
        if (!!item) {
            const basePlan = { id: item.id, type: item.type, parentId: item.parentId, price: item.price };
            if (!basePlan.parentId) {
                delete basePlan.parentId;
            }
            const addOns = [];
            if (addons) {
                addons.forEach((addOn) => {
                    const callAddOn = { id: addOn.id, type: addOn.type, price: addOn.price, quantity: addOn.quantity };
                    if (!callAddOn.parentId) {
                        delete callAddOn.parentId;
                    }
                    addOns.push(callAddOn);
                });
                return this.updateUserCart({ basePlan, addOns });
            }
            else {
                return this.firebaseMobilePlansCartService.deleteFromUserCart('addOns');
            }
        }
        else {
            // todo look into clearing addons for No Plan
            return this.setBasePlan(this.getNoPlanMobilePlan().basePlan);
        }
    }
    setTaxesAndFees(taxes, fees) {
        return this.updateUserCart({ taxes, fees });
    }
    setPromoCodes(promoCodes) {
        return this.updateUserCart({ promoCodes });
    }
    setReferralId(referralCode) {
        return this.updateUserCart({ referralCode });
    }
    setAutoRenewPlan(autoRenewPlan) {
        return this.updateUserCart({ autoRenewPlan });
    }
    setPlanDevice(planDevice) {
        if (!!planDevice) {
            const planDeviceCleanedUp = Object.assign({}, omitBy(planDevice, isNil));
            return this.updateUserCart({ planDevice: planDeviceCleanedUp });
        }
        else {
            return this.firebaseMobilePlansCartService.deleteFromUserCart('planDevice');
        }
    }
    setPlanExpectedDevice(planExpectedDevice) {
        if (!!planExpectedDevice) {
            return this.updateUserCart({ planExpectedDevice });
        }
        else {
            return this.firebaseMobilePlansCartService.deleteFromUserCart('planExpectedDevice');
        }
    }
    setSimCard(activationCode) {
        if (!!activationCode && activationCode !== '') {
            this.removeVoucherCode();
            return this.updateUserCart({ activationCode });
        }
        else {
            return this.firebaseMobilePlansCartService.deleteFromUserCart('activationCode');
        }
    }
    removeVoucherCode() {
        // had to add this because setting Object to null wasn't removing it
        this.callLoadPlansConfig();
        return this.firebaseMobilePlansCartService.deleteFromUserCart('voucherData');
    }
    removeVoucherData() {
        return this.firebaseMobilePlansCartService.deleteFromUserCart('voucherData');
    }
    removePhonesFromCart() {
        return this.firebaseMobilePlansCartService.deleteFromUserCart('phones');
    }
    setVoucherCode(voucherData) {
        this.callLoadPlansConfig(voucherData);
        return this.updateUserCart({ voucherData });
    }
    setVoucherData(voucherData) {
        return this.updateUserCart({ voucherData });
    }
    setSimPurchaseQuantity(simsQuantity) {
        return this.updateUserCart({ simsQuantity });
    }
    calculateTaxesAndFees(purchaseDetails) {
        return new Promise(((resolve, reject) => {
            this.http.patch(`${this.endpointUrl}/api/shop/calculateTaxes`, purchaseDetails).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    calculateAddonsTaxes(userPlanId, purchaseDetails) {
        return new Promise(((resolve, reject) => {
            this.http.patch(`${this.endpointUrl}/api/shop/addon/${userPlanId}/calculateTaxes`, purchaseDetails).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    calculateAddonSIMTaxesAndFees(userPlanId, purchaseDetails) {
        return new Promise(((resolve, reject) => {
            this.http.patch(`${this.endpointUrl}/api/shop/addon/${userPlanId}/calculateAddonSimTaxes`, purchaseDetails).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    calculateTopupTaxesAndFees(userPlanId, purchaseDetails) {
        return new Promise(((resolve, reject) => {
            this.http.patch(`${this.endpointUrl}/api/shop/topup/${userPlanId}/calculateTaxes`, purchaseDetails).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    getSelectedValueForOptionType(plan, type) {
        // just for note, this is currently used and tested for only type = 'base-plan'
        if (plan === undefined) {
            return undefined;
        }
        let field = '';
        switch (type) {
            case MobileCustomPlansServiceNoSync.ADD_ONS_TYPES.TALK_AND_TEXT_ADD_ON:
                field = 'talkAndTextAddOn';
                break;
            case MobileCustomPlansServiceNoSync.ADD_ONS_TYPES.DATA_ADD_ON:
                field = 'dataAddOn';
                break;
            case MobileCustomPlansServiceNoSync.ADD_ONS_TYPES.BASE_PLAN:
                field = 'basePlan';
                break;
            case MobileCustomPlansServiceNoSync.ADD_ONS_TYPES.INTERNATIONAL_TALK_ADD_ON:
                field = 'internationalTalkAddOn';
                break;
            default:
        }
        if (field !== '') {
            return plan[field];
        }
        return null;
    }
    getBundleIdOfPlan(plan) {
        return this.getDefaultBundleIdOfPlanById(plan.id);
    }
    getDefaultBundleIdOfPlanById(planId) {
        const founded = this.config.allPlans.find((firebasePlan) => firebasePlan.id === planId);
        return !!founded ? founded.bundleId : undefined;
    }
    getAllBundleIdsOfPlanById(planId) {
        const founded = this.config.allPlans.find((firebasePlan) => firebasePlan.id === planId);
        if (!!founded) {
            const allBundleIds = [];
            allBundleIds.push(founded.bundleId);
            if (!!founded.altBundleIds) {
                allBundleIds.concat(founded.altBundleIds);
            }
            return allBundleIds;
        }
        return [];
    }
    getMobilePlanByBundleId(bundleId) {
        if (!!bundleId) {
            const plan = this.config.allPlans.find((firebasePlan) => (firebasePlan.bundleId === bundleId)
                // OR if there is no plan with bundleId, look for alternative IDs (such as expired or another tech type ID).
                || (!!firebasePlan.altBundleIds && firebasePlan.altBundleIds.indexOf(bundleId) >= 0));
            // TODO use "includes(bundleId)" instead of "indexOf(bundleId)" >= 0 if we ever updated to es7 (ES2016)
            if (!!plan) {
                return new GenericMobilePlanItem(plan.id, plan.title, plan.subtitle, plan.price, 'base-plan', new MobilePlanDetails((plan.minutes || 0), plan.messages, plan.data, plan.mms, plan.extraData), plan.parentId, plan.virtual, plan.buttonText, plan.extTitle, plan.description, plan.promoMessage, plan.promoCode, plan.promoPrice, plan.promoMonths);
            }
        }
        return undefined;
    }
    startNewMobilePlan() {
        this.callLoadPlansConfig();
        return this.firebaseMobilePlansCartService.clearUserCart();
    }
    clearUserCart() {
        return this.firebaseMobilePlansCartService.clearUserCart();
    }
    setCartType(cartType) {
        return this.updateUserCart({ cartType });
    }
    setReferralCode(referralCode) {
        return this.updateUserCart({ referralCode });
    }
    setActivePlanId(activePlanId) {
        return this.updateUserCart({ activePlanId });
    }
    loadPlansConfig(voucherData) {
        console.log('loadPlansConfig');
        return this.callLoadPlansConfig(voucherData);
    }
    callLoadPlansConfig(voucherData) {
        this.plansConf.planConfiguration.subscribe((config) => {
            this.config = config;
            this.config.activePlans = (new OrderByPipe()).transform(this.config.activePlans, 'order');
            const plans = this.config.activePlans
                .map((plan) => new GenericMobilePlanItem(plan.id, plan.title, plan.subtitle, plan.price, MobileCustomPlansServiceNoSync.ADD_ONS_TYPES.BASE_PLAN, new MobilePlanDetails((plan.minutes || 0), plan.messages, plan.data, plan.mms, plan.extraData), plan.parentId, plan.virtual, plan.buttonText, plan.extTitle, plan.description, plan.promoMessage, plan.promoCode, plan.promoPrice, plan.promoMonths, plan.cycleDays, plan.promoMoPrice, plan.subscriptionCycles, plan.category, plan.annualSaving, plan.isSpecialPromotion, plan.specialPromotion, plan.availableForChange, plan.isVacation, plan.promoAvailableForChange, plan.multiLineDisount, plan.ebb, plan.subscriptionPrice, plan.subscriptionPromoPrice, null, plan?.data, plan?.hotspotData, plan?.uniquePlanIdentifier, plan?.bundleId, plan?.isGo2Plan, plan?.go2PlansAvailable));
            this.parentBasePlans = plans.filter((plan) => plan.parentId === undefined);
            this.allBasePlans = plans;
            if (!!voucherData) {
                // note: filtering parentBasePlans isn't needed, actually the FE components is handling it,
                // wasn't an issue on price filtering because the categories were being filtered by the lowest price,
                // left it on the price filtering since it was working like that need to investigate side effects
                if (!!voucherData.applicablePlans) {
                    // Filtering by applicable plans
                    // this.parentBasePlans = this.parentBasePlans.filter((plan) => plan.price <= voucherData.limit);
                    // TODO use "includes(id)" instead of "indexOf(id)" >= 0 if we ever updated to es7 (ES2016)
                    this.allBasePlans = this.allBasePlans.filter((plan) => this.getAllBundleIdsOfPlanById(plan.id).some((id) => voucherData.applicablePlans.indexOf(id) >= 0));
                }
                else {
                    console.warn('There are no applicable plans assigned to this voucher');
                }
            }
            this.isConfigurationReadySubject.next(true);
        });
    }
    fillCurrentPlanFromUserCart(userCart) {
        const plan = this.userPlansService.getCustomizableMobilePlanFromUserPlan(userCart);
        if (!!userCart && !!plan) {
            plan.simsQuantity = userCart.simsQuantity;
            plan.activationCode = userCart.activationCode;
            plan.voucherData = userCart.voucherData;
            plan.taxes = userCart.taxes || 0;
            plan.fees = userCart.fees || 0;
            plan.autoRenewPlan = userCart.autoRenewPlan;
            plan.locked = userCart.locked;
            plan.mdn = userCart.mdn;
            plan.cartType = userCart.cartType;
            plan.planExpectedDevice = userCart.planExpectedDevice;
            plan.activePlanId = userCart.activePlanId;
            plan.promoCodes = userCart.promoCodes;
            plan.referralCode = userCart.referralCode;
            plan.marketingObject = userCart.marketingObject;
            plan.phoneNumber = userCart.phoneNumber;
            plan.pin = userCart.pin;
            plan.eSIM = userCart.eSIM;
            plan.qrScanned = userCart.qrScanned;
            plan.storePickup = userCart.storePickup;
            plan.financingDetails = userCart.financingDetails;
            plan.acpDevice = userCart.acpDevice;
            plan.agentCode = userCart.agentCode;
            plan.catalogArea = userCart.catalogArea;
            if (!!userCart.phones) {
                plan.phones = Object.values(userCart.phones);
            }
            plan.go2Plan = userCart.go2Plan;
            plan.go2SIM = userCart.go2SIM;
            return plan;
        }
    }
    updateUserCart(partialCart) {
        // this.firebaseMobilePlansCartService.updateUserCart not always returns a promise directly, which results in an script error stopping it.
        // almothafar: I made the fix, the fix should be inside 'FirebaseMobilePlansCartService.updateUserCart' anyway
        return this.firebaseMobilePlansCartService.updateUserCart(partialCart)
            .then(() => Promise.resolve(), (error) => console.warn(error));
    }
    getFilteredAddOns(config, type, bundleId) {
        const optionsItem = new OptionsMobilePlanItem();
        let addOnOptions = config.planAddOns.filter((addOn) => {
            let isCompatible = true;
            const compatibleWith = addOn.compatibleWith instanceof Array ? addOn.compatibleWith : (addOn.compatibleWith.toString() || '').split(',').map((id) => id.trim());
            if (!!compatibleWith && !!bundleId) {
                const foundBundleId = compatibleWith.find((id) => id === bundleId);
                isCompatible = !!foundBundleId;
            }
            return addOn.type === type && isCompatible;
        }).map((option) => new GenericMobilePlanItem(option.id, option.title, option.subtitle, option.price, option.type, new MobilePlanDetails((option.minutes || 0), (option.messages || 0), (option.data || 0), (option.mms || 0)), null, null, null, null, option.description));
        if (addOnOptions.length > 0) {
            addOnOptions = (new OrderByPipe()).transform(addOnOptions, 'price');
            for (const option of addOnOptions) {
                optionsItem.addOption(option);
            }
            optionsItem.selectOption(addOnOptions[0]);
            return optionsItem;
        }
        else {
            return null;
        }
    }
    subscribeWithUserPlansAndAccountsWithConfig() {
        return this.isConfigurationReady
            .pipe(combineLatest(this.userAccountService.selectedAccount, (configReady, userAccount) => {
            if (configReady) {
                const bundleId = !!userAccount && !!userAccount.plan ? userAccount.plan.bundleId : undefined;
                const dataOptionsItem = this.getFilteredAddOns(this.config, MobileCustomPlansServiceNoSync.ADD_ONS_TYPES.DATA_ADD_ON, bundleId);
                const talkAndTextOptionsItem = this.getFilteredAddOns(this.config, MobileCustomPlansServiceNoSync.ADD_ONS_TYPES.TALK_AND_TEXT_ADD_ON, bundleId);
                const internationalTalkOptionsItem = this.getFilteredAddOns(this.config, MobileCustomPlansServiceNoSync.ADD_ONS_TYPES.INTERNATIONAL_TALK_ADD_ON, bundleId);
                this.allAddOns = [];
                this.allAddOns = this.allAddOns.concat(talkAndTextOptionsItem, dataOptionsItem, internationalTalkOptionsItem).filter((addOn) => addOn != null);
                return true;
            }
            else {
                return false;
            }
        }))
            .subscribe((ready) => this.isPlansConfigurationReadySubject.next(ready));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: MobileCustomPlansServiceNoSync, deps: [{ token: EndpointUrl }, { token: PlansConfigurationService }, { token: i1.SimpleAuthService }, { token: UserPlansService }, { token: UserAccountServiceNoSync }, { token: i1.AuthHttp }, { token: FirebaseMobilePlansCartService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: MobileCustomPlansServiceNoSync, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: MobileCustomPlansServiceNoSync, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [EndpointUrl]
                }] }, { type: PlansConfigurationService }, { type: i1.SimpleAuthService }, { type: UserPlansService }, { type: UserAccountServiceNoSync }, { type: i1.AuthHttp }, { type: FirebaseMobilePlansCartService }] });

class OrderCheckoutService {
    constructor(endpointUrl, http) {
        this.endpointUrl = endpointUrl;
        this.http = http;
    }
    checkoutNewPlan(cart) {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.endpointUrl}/api/shop/checkout`, cart).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    checkoutChangePlan(userPlanId, cart) {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.endpointUrl}/api/plan/${userPlanId}/shopPlan`, cart).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    checkoutPlanItem(userPlanId, cart) {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.endpointUrl}/api/plan/${userPlanId}/addon-and-sim`, cart).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    checkoutChangeFreePlan(userPlanId, cart) {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.endpointUrl}/api/plan/${userPlanId}/shopFreePlan`, cart).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    orderNewSimCard(userPlanId, cart) {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.endpointUrl}/api/plan/${userPlanId}/orderNewSim`, cart).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    orderNewSim(userPlanId, cartInfo) {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.endpointUrl}/api/plan/${userPlanId}/orderNewSim`, cartInfo).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    orderSimMigration(mdn, cart) {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.endpointUrl}/api/plans/${mdn}/migration/checkout`, cart).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    checkoutDeferredPlan(orderId, paymentInfo) {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.endpointUrl}/api/plans/deferred/${orderId}`, paymentInfo).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: OrderCheckoutService, deps: [{ token: EndpointUrl }, { token: i1.AuthHttp }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: OrderCheckoutService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: OrderCheckoutService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [EndpointUrl]
                }] }, { type: i1.AuthHttp }] });

class PlacesAutocompleteService {
    constructor(endpointUrl, httpClient, geocodingService) {
        this.endpointUrl = endpointUrl;
        this.httpClient = httpClient;
        this.geocodingService = geocodingService;
        this.location = { longitude: 0, latitude: 0 };
        this.geocodingService.getCurrentLocation().subscribe((location) => this.location = location);
    }
    findAddress(input) {
        return this.getAutoCompleteListAsString(input);
    }
    findDetailedAddress(input) {
        const observer = new ReplaySubject();
        this.getAutoCompleteTerms(input).subscribe((terms) => {
            const termsArray = terms['0']; //FIXME: this fails sometimes, xD reached daily quota limit on google api calls seems that broke it
            const termsLength = termsArray.length;
            const address = { country: termsArray[termsLength - 1].value };
            if (termsLength > 1) {
                address.state = termsArray[termsLength - 2].value;
                if (termsLength > 2) {
                    address.city = termsArray[termsLength - 3].value;
                }
            }
            observer.next(address);
        }, (error) => {
            observer.error(`No details for address`);
            console.warn('error finding address details ', error);
        });
        return observer;
    }
    findDetailedAddressFields(placeId) {
        const observer = new ReplaySubject(1);
        if (!!placeId) {
            this.getAddressDetails(placeId).subscribe((addressDetails) => {
                if (!!addressDetails) {
                    //  console.log(addressDetails);
                    //debugger
                    const country = this.getAddressComponentByType(addressDetails, 'country', true);
                    let state = this.getAddressComponentByType(addressDetails, 'administrative_area_level_1', true);
                    if (country === 'PR') { // Paurto Rico
                        state = country;
                    }
                    let city = this.getAddressComponentByType(addressDetails, 'locality');
                    if (city === '') { // test case for this try look up for '3738 Park Avenue'
                        city = this.getAddressComponentByType(addressDetails, 'sublocality');
                    }
                    const fullStreet = (this.getAddressComponentByType(addressDetails, 'street_number', true) + ' ' + this.getAddressComponentByType(addressDetails, 'route', true)).trim();
                    const address = {
                        address1: fullStreet,
                        address2: '',
                        country: this.getAddressComponentByType(addressDetails, 'country'),
                        city,
                        state,
                        postalCode: this.getAddressComponentByType(addressDetails, 'postal_code')
                    };
                    observer.next(address);
                }
                else {
                    observer.error(`No details for placeId : ${placeId}`);
                }
            });
        }
        else {
            console.warn('placeId is undefined');
        }
        return observer;
    }
    getAutoCompleteListAsString(input) {
        return this.httpClient
            .get(this.endpointUrl + GoogleAPIConfig.placesApiUrl(input, this.location.latitude, this.location.longitude), { withCredentials: true })
            .pipe(map((obj) => {
            return obj.predictions.map((p) => ({
                description: p.description,
                main_text: this.cleanDescriptionForMainText(p),
                place_id: p.place_id
            }));
        }));
    }
    cleanDescriptionForMainText(p) {
        if (!!p.description.split(',')[0]) {
            return p.description.split(',')[0];
        }
        else {
            return p.structured_formatting.main_text;
        }
    }
    getAutoCompleteTerms(input) {
        return this.httpClient
            .get(this.endpointUrl + GoogleAPIConfig.placesApiUrl(input, this.location.latitude, this.location.longitude), { withCredentials: true })
            .pipe(map((obj) => {
            return obj.predictions.map((p) => p.terms);
        }));
    }
    getAddressDetails(placeId) {
        return this.httpClient.get(this.endpointUrl + GoogleAPIConfig.placesDetailsApiUrl(placeId), { withCredentials: true })
            .pipe(map((obj) => {
            if (!!obj && !!obj.result) {
                return obj.result.address_components.map((ac) => ({
                    long_name: ac.long_name,
                    short_name: ac.short_name,
                    types: ac.types
                }));
            }
        }));
    }
    /**
     *  Refer to for more info:
     * https://developers.google.com/maps/documentation/geocoding/intro#Types
     * Possible Values for Type field
     * country
     * locality -> City, town
     * administrative_area_level_1 -> State
     * administrative_area_level_2 -> County
     * street_address
     * postal_code
     *
     * @param addressComponents
     * @param type
     * @param short
     * @returns {string}
     * @private
     */
    getAddressComponentByType(addressComponents, type, short) {
        let selectedComponentText = '';
        const selectedComponent = addressComponents.find((ac) => ac.types.indexOf(type) !== -1);
        if (!!selectedComponent) {
            if (short) {
                selectedComponentText = selectedComponent.short_name;
            }
            else {
                selectedComponentText = selectedComponent.long_name;
            }
        }
        else {
            selectedComponentText = ''; // Failed to find
        }
        return selectedComponentText;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PlacesAutocompleteService, deps: [{ token: EndpointUrl }, { token: i1$2.HttpClient }, { token: i2.GeocodingService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PlacesAutocompleteService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PlacesAutocompleteService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [EndpointUrl]
                }] }, { type: i1$2.HttpClient }, { type: i2.GeocodingService }] });

class ReferralService {
    constructor(endpointUrl, http) {
        this.endpointUrl = endpointUrl;
        this.http = http;
    }
    sendReferralEmailInvitations(invitationData, invisibleRecaptchaToken, action, invisibleRecaptcha) {
        let header = new HttpHeaders()
            .set('reCaptcha', invisibleRecaptchaToken);
        if (!!action) {
            header = header.set('action', action);
        }
        header = header.set('invisibleRecaptcha', `${!!invisibleRecaptcha}`);
        return new Promise((resolve, reject) => {
            this.http.postWithHeader(`${this.endpointUrl}/api/referral/send-emails`, invitationData, header)
                .pipe(take(1)).subscribe(() => resolve(), (error) => reject(error));
        });
    }
    setReferralMdn(selectedMdn) {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.endpointUrl}/api/referral`, { mdn: selectedMdn })
                .pipe(take(1)).subscribe((result) => resolve(result), (error) => reject(error));
        });
    }
    getReferralDetails(limit, page) {
        return new Promise((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/referral?limit=${limit}&page=${page}`)
                .subscribe((response) => resolve(response), (error) => reject(error));
        });
    }
    getAccountReferralDetails(limit, page) {
        return new Promise((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/referral/accepted?limit=${limit}&page=${page}`)
                .subscribe((response) => resolve(response), (error) => reject(error));
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ReferralService, deps: [{ token: EndpointUrl }, { token: i1.AuthHttp }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ReferralService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ReferralService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [EndpointUrl]
                }] }, { type: i1.AuthHttp }] });

class ShippingConfigurationService {
    constructor(angularFireDatabase) {
        this.angularFireDatabase = angularFireDatabase;
        this.newSimOrder = new ReplaySubject(1);
        this.shippingMethods = this.angularFireDatabase.list(FirebaseDatabasePaths.SHIPPING_CONFIGURATION.METHODS, (ref) => ref.orderByChild('order')).valueChanges();
        this.angularFireDatabase.object(FirebaseDatabasePaths.SHIPPING_CONFIGURATION.NEW_SIM_ORDER).valueChanges().subscribe((conf) => {
            this.newSimOrder.next(conf);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ShippingConfigurationService, deps: [{ token: i1$1.AngularFireDatabase }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ShippingConfigurationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ShippingConfigurationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$1.AngularFireDatabase }] });

class ShippingService {
    constructor(endpointUrl, http) {
        this.endpointUrl = endpointUrl;
        this.http = http;
    }
    verifyShippingAddress(shippingAddress) {
        return this.postBackendVerifyShippingAddress(shippingAddress);
    }
    shippingEstimate(estimateShipping) {
        return this.postBackendShippingEstimate(estimateShipping);
    }
    saveTrackingCode(orderId, trackingNumber) {
        return this.patchBackendSaveTrackingCode(orderId, trackingNumber);
    }
    postBackendVerifyShippingAddress(shippingAddress) {
        if (typeof shippingAddress.address1 === 'object' && shippingAddress?.address1) {
            shippingAddress.address1 = shippingAddress?.address1['main_text'];
        }
        return new Promise((resolve, reject) => {
            this.http.post(`${this.endpointUrl}/api/shipment/placedetails`, shippingAddress).pipe(take(1))
                .subscribe((addresses) => resolve(addresses), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    postBackendShippingEstimate(estimateShipping) {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.endpointUrl}/api/shipment/estimate`, estimateShipping).pipe(take(1))
                .subscribe((days) => resolve(days), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    patchBackendSaveTrackingCode(orderId, trackingNumber) {
        return new Promise((resolve, reject) => {
            this.http.patch(`${this.endpointUrl}/api/orders/${orderId}/shipping/trackingNumber`, trackingNumber)
                .pipe(take(1)).subscribe((order) => resolve(order), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ShippingService, deps: [{ token: EndpointUrl }, { token: i1.AuthHttp }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ShippingService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ShippingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [EndpointUrl]
                }] }, { type: i1.AuthHttp }] });

class ShopConfigurationService {
    constructor(endpointUrl, http, angularFireDatabase) {
        this.endpointUrl = endpointUrl;
        this.http = http;
        this.angularFireDatabase = angularFireDatabase;
        this.storesSubscription = new ReplaySubject(1);
        this.counter = 0;
        this.categories = new ReplaySubject();
        this.stores = this.storesSubscription.asObservable();
        this.angularFireDatabase.list(FirebaseDatabasePaths.SHOP_CONFIGURATION.CATEGORIES, (ref) => ref.orderByChild('order'))
            .valueChanges().subscribe((conf) => this.categories.next(conf));
    }
    getPhonesOfCategory(categoryId) {
        const phonesSubject = new ReplaySubject(1);
        if (!!categoryId) {
            this.categories.subscribe((categories) => {
                const filteredCategoryById = categories.find((category) => category.id === categoryId);
                if (!!filteredCategoryById) {
                    const sortedArray = (new ValuesPipe()).transform(filteredCategoryById.phones)
                        .sort((first, second) => first.order > second.order ? 1 : (first.order < second.order ? -1 : 0));
                    phonesSubject.next(sortedArray);
                }
                else {
                    phonesSubject.error(`Phones of category id ${categoryId} not found`);
                }
            });
        }
        else {
            phonesSubject.error(`Category id ${categoryId} is undefined`);
        }
        return phonesSubject;
    }
    getCategoryDetails(categoryId) {
        const categorySubject = new ReplaySubject(1);
        if (!!categoryId) {
            this.categories.subscribe((categories) => {
                const filteredCategoryById = categories.find((category) => category.id === categoryId);
                if (!!filteredCategoryById) {
                    categorySubject.next(filteredCategoryById);
                }
                else {
                    categorySubject.error(`Category of id ${categoryId} not found`);
                }
            });
        }
        else {
            categorySubject.error(`Category id ${categoryId} is undefined`);
        }
        return categorySubject;
    }
    getPhoneImageURL(phoneId, finishId) {
        return new Promise((resolve, reject) => {
            this.getPhoneDetails(phoneId).subscribe((phoneDetails) => {
                const finish = phoneDetails.finishes[finishId];
                resolve((new ValuesPipe()).transform(finish.images)[0].imageURL);
            }, (error) => reject(error));
        });
    }
    getPhoneDetails(phoneId) {
        const phoneDetailsSubject = new ReplaySubject(1);
        this.angularFireDatabase.object(FirebaseDatabasePaths.SHOP_CONFIGURATION.PHONE_DETAILS + '/' + phoneId).valueChanges().subscribe((phoneDetails) => {
            if (!!phoneDetails) {
                const fixedPhoneDetails = phoneDetails;
                fixedPhoneDetails.capacities = (new ValuesPipe()).transform(phoneDetails.capacities)
                    .sort((first, second) => {
                    return first.price > second.price ? 1 : (first.price < second.price ? -1 : 0);
                });
                fixedPhoneDetails.products = (new ValuesPipe()).transform(phoneDetails.products);
                phoneDetailsSubject.next(fixedPhoneDetails);
            }
            else {
                phoneDetailsSubject.error(`Phone with id ${phoneId} not found`);
            }
        }, (error) => phoneDetailsSubject.error(error.message));
        return phoneDetailsSubject;
    }
    getPhoneSpecification(phoneId, capacityId, finishId) {
        const phoneSpecifications = new ReplaySubject(1);
        this.getPhoneDetails(phoneId).subscribe((phoneDetails) => {
            const genericSpecifications = (new ValuesPipe()).transform(phoneDetails.technical_specifications);
            const selectedProduct = (new ValuesPipe()).transform(phoneDetails.products)
                .find((product) => capacityId === product.capacityId && product.finishId === finishId);
            if (!!selectedProduct) {
                const productSpecificationOverrides = selectedProduct.technical_specifications_override;
                if (!!productSpecificationOverrides) {
                    (new ValuesPipe()).transform(productSpecificationOverrides).forEach((value) => {
                        const index = genericSpecifications.findIndex((spec) => spec.id === value.id);
                        if (index >= 0) {
                            genericSpecifications[index] = value;
                        }
                    });
                }
            }
            const sortedArray = genericSpecifications
                .sort((first, second) => first.order > second.order ? 1 : (first.order < second.order ? -1 : 0));
            const finalArray = sortedArray.map((item) => {
                item.specifications = (new ValuesPipe()).transform(item.specifications)
                    .sort((first, second) => first.order > second.order ? 1 : (first.order < second.order ? -1 : 0));
                return item;
            });
            phoneSpecifications.next(finalArray);
        });
        return phoneSpecifications;
    }
    getStoresWithinRange(zip, distance, product, limit, page) {
        return this.getStoresWithinRangeFromBackend(zip, distance, product, limit, page).then((storesList) => {
            if (!!storesList.stores) {
                this.storesSubscription.next(storesList);
            }
            else {
                this.storesSubscription.next({ stores: [], totalItems: 0 });
            }
        }, (error) => {
            const errorObject = Object.assign({}, error, { datetime: new Date(), id: ++this.counter });
            this.storesSubscription.next(errorObject);
            this.storesSubscription.next({ stores: [], totalItems: 0 });
        });
    }
    getAllStores() {
        return this.getStoresListFromBackend();
    }
    getStoresWithinRangeFromBackend(zip, distance, product, limit, page) {
        return new Promise((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/stores/zip/${zip}/${distance}/${product}`, { limit, page })
                .pipe(take(1)).subscribe((store) => resolve(store), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    getStoresListFromBackend() {
        return new Promise((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/stores`)
                .pipe(take(1)).subscribe((stores) => resolve(stores), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ShopConfigurationService, deps: [{ token: EndpointUrl }, { token: i1.AuthHttp }, { token: i1$1.AngularFireDatabase }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ShopConfigurationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ShopConfigurationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [EndpointUrl]
                }] }, { type: i1.AuthHttp }, { type: i1$1.AngularFireDatabase }] });

class UserDeviceService {
    constructor(endpointUrl, http) {
        this.endpointUrl = endpointUrl;
        this.http = http;
        this.compatibleDeviceSubject = new ReplaySubject(1);
        this.expectedDeviceSubject = new ReplaySubject(1);
        this.isChangingDeviceSubject = new ReplaySubject(0);
        this.codeSubject = new ReplaySubject(1);
        this.compatibleDevice = this.compatibleDeviceSubject.asObservable().pipe(debounceTime(100));
        this.expectedDevice = this.expectedDeviceSubject.asObservable().pipe(debounceTime(100));
        this.code = this.codeSubject.asObservable().pipe(debounceTime(100));
        this.isChangingDevice = this.isChangingDeviceSubject.asObservable().pipe(debounceTime(100));
    }
    checkDeviceCompatabilityWithMdn(mdn, recaptchaResponse, action, invisibleRecaptcha) {
        return this.getBackendCheckCompatibilityWithMdn(mdn, recaptchaResponse, action, invisibleRecaptcha);
    }
    isSupportedDevice(equipment, iccid, validateOnly) {
        this.codeSubject.next(equipment);
        return this.getBackendCheckCompatibility(equipment, iccid, validateOnly);
    }
    // tslint:disable-next-line:max-line-length
    isSupportedDeviceWithZipCode(equipment, zipCode, carrier, recaptchaRespone, action, invisibleRecaptcha) {
        return this.getBackendCheckCompatibilityWithZipCode(equipment, zipCode, carrier, recaptchaRespone, action, invisibleRecaptcha);
    }
    isValidSimCard(iccid, equipment) {
        return this.getBackendValidateSim(iccid, equipment);
    }
    isValidSimCardWithNoAuth(iccid, equipment) {
        return this.getBackendValidateSimWithNoAuth(iccid, equipment);
    }
    changeUserDevice(changeRequest, userPlanId) {
        return this.getBackendChangeDevice(changeRequest, userPlanId);
    }
    setCompatibleDevice(device) {
        this.isChangingDeviceSubject.next(false);
        this.compatibleDeviceSubject.next(device);
        this.expectedDeviceSubject.next(undefined);
    }
    setExpectedDevice(device) {
        if (!!device.technical_specifications_override) {
            delete device.technical_specifications_override; // no need for this to save here
        }
        this.expectedDeviceSubject.next(device);
        this.compatibleDeviceSubject.next(undefined);
    }
    clearDevices() {
        this.expectedDeviceSubject.next(undefined);
        this.compatibleDeviceSubject.next(undefined);
    }
    setIsChangingDevice(isChanging) {
        this.isChangingDeviceSubject.next(isChanging);
    }
    checkDeviceNetworkByMdn(mdn) {
        return this.getBackendDeviceNetwork(mdn);
    }
    checkCompatibilityWithEquipmentOrWithout(recaptcha, zipCode, equipment, isLte) {
        if (!!equipment) {
            const equipmentValue = `equipment=${equipment}`;
            return this.getBackendCheckCompatibilityWithEquipmentOrWithout(recaptcha, zipCode, equipmentValue);
        }
        else if (!equipment && isLte !== null) {
            const lteValue = `isLte=${isLte}`;
            return this.getBackendCheckCompatibilityWithEquipmentOrWithout(recaptcha, zipCode, lteValue);
        }
        else if (isLte === null) {
            return this.getBackendCheckCompatibilityWithEquipmentOrWithout(recaptcha, zipCode);
        }
    }
    getBackendCheckCompatibility(equipment, iccid, validateOnly) {
        return new Promise((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/equipment/compatibility?equipment=${equipment}&validateOnly=${validateOnly || false}&iccid=${iccid || ''}`)
                .pipe(take(1)).subscribe((device) => resolve(device), (error) => reject(error));
        });
    }
    getBackendCheckCompatibilityWithMdn(mdn, recaptchaResponse, action, invisibleRecaptcha) {
        if (!!recaptchaResponse) {
            let header = new HttpHeaders()
                .set('reCaptcha', recaptchaResponse);
            if (!!action) {
                header = header.set('action', action);
            }
            header = header.set('invisibleRecaptcha', `${!!invisibleRecaptcha}`);
            return new Promise((resolve, reject) => {
                this.http.getWithOptions(`${this.endpointUrl}/api/equipment/compatibility?mdn=${mdn}`, '', header)
                    .pipe(take(1)).subscribe((device) => resolve(device), (error) => reject(error));
            });
        }
        return new Promise((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/equipment/compatibility?mdn=${mdn}`)
                .pipe(take(1)).subscribe((device) => resolve(device), (error) => reject(error));
        });
    }
    // tslint:disable-next-line:max-line-length
    getBackendCheckCompatibilityWithZipCode(equipment, zipCode, carrier, recaptchaResponse, action, invisibleRecaptcha) {
        if (!!recaptchaResponse) {
            let header = new HttpHeaders()
                .set('reCaptcha', recaptchaResponse);
            if (!!action) {
                header = header.set('action', action);
            }
            header = header.set('invisibleRecaptcha', `${!!invisibleRecaptcha}`);
            return new Promise((resolve, reject) => {
                this.http.getWithOptions(`${this.endpointUrl}/api/equipment/compatibility?equipment=${equipment}&zipCode=${zipCode}&carrier=${carrier}`, '', header)
                    .pipe(take(1)).subscribe((device) => resolve(device), (error) => reject(error));
            });
        }
        return new Promise((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/equipment/compatibility?equipment=${equipment}&zipCode=${zipCode}&carrier=${carrier}`)
                .pipe(take(1)).subscribe((device) => resolve(device), (error) => reject(error));
        });
    }
    getBackendValidateSim(iccid, equipment) {
        return new Promise((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/equipment/validateSim?code=${iccid}&equipment=${equipment || ''}`)
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    getBackendValidateSimWithNoAuth(iccid, equipment) {
        return new Promise((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/equipment/validateSimNoAuth?code=${iccid}&equipment=${equipment || ''}`)
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    getBackendChangeDevice(changeRequest, userPlanId) {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.endpointUrl}/api/plan/${userPlanId}/equipment/modify`, changeRequest).pipe(take(1))
                .subscribe(() => resolve(), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    getBackendDeviceNetwork(mdn) {
        return new Promise((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/mdn/${mdn}/details`).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    getBackendCheckCompatibilityWithEquipmentOrWithout(recaptcha, zipCode, option) {
        const header = new HttpHeaders().set('reCaptcha', recaptcha);
        return new Promise((resolve, reject) => {
            this.http.getWithOptions(`${this.endpointUrl}/api/equipment/coverage?zipCode=${zipCode}&${option}`, '', header)
                .pipe(take(1)).subscribe((device) => resolve(device), (error) => reject(error));
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UserDeviceService, deps: [{ token: EndpointUrl }, { token: i1.AuthHttp }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UserDeviceService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UserDeviceService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [EndpointUrl]
                }] }, { type: i1.AuthHttp }] });

class SimActivationService {
    constructor(userDeviceService) {
        this.userDeviceService = userDeviceService;
    }
    checkActivationCode(activationCode, equipment) {
        return new Promise((resolve, reject) => {
            this.userDeviceService.isValidSimCard(activationCode, equipment).then((response) => {
                return resolve(response);
            }, (error) => reject(error));
        });
    }
    checkActivationCodeWithNoAuth(activationCode, equipment) {
        return new Promise((resolve, reject) => {
            this.userDeviceService.isValidSimCardWithNoAuth(activationCode, equipment).then((response) => {
                return resolve(response);
            }, (error) => reject(error));
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: SimActivationService, deps: [{ token: UserDeviceService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: SimActivationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: SimActivationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: UserDeviceService }] });

class StoreLocatorService {
    constructor(endpointUrl, http) {
        this.endpointUrl = endpointUrl;
        this.http = http;
    }
    getStoresWithinRange(zip, distance, product) {
        console.log('getStoresWithinRange');
        return this.getStoresWithinRangeFromBackend(zip, distance, product);
    }
    getAllStores() {
        console.log('get stores list');
        return this.getStoresListFromBackend();
    }
    getStoresWithinRangeFromBackend(zip, distance, storeName, product) {
        return new Promise((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/stores/zip/${zip}/${distance}/${storeName}/${product}`)
                .pipe(take(1)).subscribe((store) => resolve(store), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    getStoresListFromBackend() {
        return new Promise((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/stores`)
                .pipe(take(1)).subscribe((stores) => resolve(stores), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: StoreLocatorService, deps: [{ token: EndpointUrl }, { token: i1.AuthHttp }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: StoreLocatorService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: StoreLocatorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [EndpointUrl]
                }] }, { type: i1.AuthHttp }] });

class TaxConfigurationService {
    constructor(_angularFireDatabase) {
        this._angularFireDatabase = _angularFireDatabase;
        this.taxesEnabledSubject = new ReplaySubject(1);
        this.taxConfigurationSubject = new ReplaySubject(1);
        this.taxesEnabled = this.taxesEnabledSubject.asObservable();
        this.taxConfigurationSubject.subscribe((taxConf) => {
            this.taxes = (new ValuesPipe()).transform(taxConf.taxes);
            this.isTaxesEnabled = taxConf.enableTaxes;
            this.taxesEnabledSubject.next(this.isTaxesEnabled);
        });
        this._angularFireDatabase.object(FirebaseDatabasePaths.TAX_CONFIGURATION.BASE).valueChanges().subscribe((conf) => this.taxConfigurationSubject.next(conf));
    }
    calculateTaxesAndFees(amount, address, withoutFees) {
        if (this.isTaxesEnabled && !!address) {
            let selectedTaxes = this.taxes.filter((tax) => tax.country === address.country && tax.state === address.state && tax.city === address.city);
            if (selectedTaxes.length === 0) {
                // DEV Note, this could be recursive but not big deal anyway :)
                // just in case a record missing, return default value
                selectedTaxes = this.taxes.filter((tax) => tax.country === address.country && tax.state === address.state && tax.city === undefined);
                if (selectedTaxes.length === 0) {
                    selectedTaxes = this.taxes.filter((tax) => tax.country === address.country && tax.state === undefined && tax.city === undefined);
                }
            }
            const selectedTax = selectedTaxes[0];
            if (!!selectedTax) {
                return { fees: withoutFees ? 0 : (amount * selectedTax.wirelessTax / 100) + (selectedTax.wirelessFees || 0), taxes: amount * selectedTax.salesTax / 100, price: amount };
            }
        }
        return { fees: 0, taxes: 0, price: amount };
    }
    calculateTaxes(amount, address) {
        return this.calculateTaxesAndFees(amount, address, true);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: TaxConfigurationService, deps: [{ token: i1$1.AngularFireDatabase }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: TaxConfigurationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: TaxConfigurationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$1.AngularFireDatabase }] });

class UserAccountPortInService {
    constructor(endpointUrl, http) {
        this.endpointUrl = endpointUrl;
        this.http = http;
    }
    newPortInAccount(planKey, account, recaptchaResponse, action, invisibleRecaptcha) {
        return this.newPortInAccountBackend(planKey, account, recaptchaResponse, action, invisibleRecaptcha);
    }
    checkPortInAccount(planKey) {
        return this.checkPortInAccountBackend(planKey);
    }
    updatePortInAccount(planKey, account) {
        return this.updatePortInAccountBackend(planKey, account);
    }
    deletePortInAccount(planKey) {
        return this.deletePortInAccountBackend(planKey);
    }
    newPortInAccountBackend(planKey, account, recaptchaResponse, action, invisibleRecaptcha) {
        let header = new HttpHeaders()
            .set('reCaptcha', recaptchaResponse);
        if (!!action) {
            header = header.set('action', action);
        }
        header = header.set('invisibleRecaptcha', `${!!invisibleRecaptcha}`);
        return new Promise((resolve, reject) => {
            this.http.postWithHeader(`${this.endpointUrl}/api/plan/${planKey}/portin`, account, header)
                .pipe(take(1)).subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    checkPortInAccountBackend(planKey) {
        return new Promise((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/plan/${planKey}/portin`)
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    updatePortInAccountBackend(planKey, account) {
        return new Promise((resolve, reject) => {
            this.http.put(`${this.endpointUrl}/api/plan/${planKey}/portin`, account)
                .pipe(take(1)).subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    deletePortInAccountBackend(planKey) {
        return new Promise((resolve, reject) => {
            if (!!planKey) {
                this.http.delete(`${this.endpointUrl}/api/plan/${planKey}/portin`)
                    .pipe(take(1)).subscribe((result) => resolve(), (error) => this.http.handlePromiseError(error, reject));
            }
            else {
                reject({ message: 'Could not cancel port in request for undefined plan' });
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UserAccountPortInService, deps: [{ token: EndpointUrl }, { token: i1.AuthHttp }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UserAccountPortInService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UserAccountPortInService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [EndpointUrl]
                }] }, { type: i1.AuthHttp }] });

class UserOrdersService {
    constructor(endpointUrl, http) {
        this.endpointUrl = endpointUrl;
        this.http = http;
    }
    updateOrderShippingAddress(orderId, address) {
        return new Promise((resolve, reject) => {
            this.http.put(`${this.endpointUrl}/api/pendingPlan/shippingAddress?orderId=${orderId}`, address)
                .pipe(take(1)).subscribe((result) => resolve(), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    cancelWithRefund(orderId) {
        return new Promise((resolve, reject) => {
            this.http.delete(this.endpointUrl + `/api/orders/${orderId}`).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    getOrders(itemsPerPage, page, filter) {
        return new Promise(((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/orders?pageSize=${itemsPerPage || 10}&page=${page || 0}&filter=${filter || 180}`).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    getOrderById(orderID) {
        return new Promise(((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/orders/${orderID}`).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    getTrackingInfo(orderID) {
        return new Promise(((resolve, reject) => {
            this.http.get(`${this.endpointUrl}/api/orders/${orderID}/tracking`).pipe(take(1))
                .subscribe((response) => resolve(response), (APIerror) => reject(APIerror));
        }));
    }
    getFilteredOrders(filters, itemsPerPage, page) {
        const path = !!itemsPerPage ? `api/v2/orders?pageSize=${itemsPerPage || 3}&page=${page || 0}` : `api/v2/orders`;
        return new Promise((resolve, reject) => {
            this.http.post(`${this.endpointUrl}/${path}`, filters)
                .pipe(take(1)).subscribe((result) => resolve(result), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UserOrdersService, deps: [{ token: EndpointUrl }, { token: i1.AuthHttp }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UserOrdersService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UserOrdersService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [EndpointUrl]
                }] }, { type: i1.AuthHttp }] });

class VoucherActivationService {
    constructor(endpointUrl, http) {
        this.endpointUrl = endpointUrl;
        this.http = http;
    }
    checkVoucherCode(code) {
        return new Promise((resolve, reject) => {
            this.getBackendVoucherCodeDetails(code).then((response) => {
                resolve({ code, limit: response.amount, applicablePlans: !!response.applicablePlans ? response.applicablePlans : null });
            }, (error) => reject(error));
        });
    }
    getBackendVoucherCodeDetails(voucherCode) {
        return new Promise((resolve, reject) => {
            this.http.get(this.endpointUrl + '/api/account/voucher/' + voucherCode)
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: VoucherActivationService, deps: [{ token: EndpointUrl }, { token: i1.AuthHttp }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: VoucherActivationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: VoucherActivationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [EndpointUrl]
                }] }, { type: i1.AuthHttp }] });

/**
 * Implementation of the IFirebaseEBBService
 * @property paymentMethodsList:  list of credit cards details of the user
 * @property defaultPaymentMethod: the default credit card to be used
 * @property _paymentMethodListSubject: replySubject to subscribe for the paymentMethodsList
 * @property _defaultPaymentMethodSubject : replySubject to subscribe for the defaultPaymentMethod
 */
class FirebaseEBBService {
    constructor(angularFireDatabase, simpleAuth) {
        this.angularFireDatabase = angularFireDatabase;
        this.simpleAuth = simpleAuth;
        this.ebbSubject = new ReplaySubject(1);
        this.ebbDetails = this.ebbSubject.asObservable();
        this.simpleAuth.registerDependentService('firebase-ebb-service', this);
    }
    /**
     * Build all subscriptions for this service
     * @param fbUser : current firebase logged in user
     */
    buildSubscriptions(fbUser) {
        console.log('building subscriptions for firebase-ebb.service');
        const subscriptions = [];
        this.fbUser = fbUser;
        subscriptions.push(
        //get ebb details from firebase
        this.angularFireDatabase.object(FirebaseDatabasePaths.USER_EBB_DETAILS(fbUser.uid))
            .valueChanges()
            .pipe(debounceTime(30)).subscribe((data) => {
            if (!!data) {
                this.ebbSubject.next(data);
            }
            else {
                this.ebbSubject.next(undefined);
            }
        }));
        return subscriptions;
    }
    onLoggedOut() {
        this.ebbSubject.next({});
    }
    clearEBBDetails() {
        return new Promise((resolve) => {
            if (!!this.fbUser && !!this.fbUser.uid) {
                setTimeout(() => {
                    this.angularFireDatabase
                        .object(FirebaseDatabasePaths.USER_EBB_DETAILS(this.fbUser.uid))
                        .remove()
                        .then(() => {
                        resolve();
                    });
                });
            }
            else {
                resolve();
            }
        });
    }
    saveEbbDetails(data, userId) {
        return new Promise((resolve, reject) => {
            const ebbDetails = Object.assign({}, omitBy(data, isNil));
            if (Object.keys(ebbDetails).length === 0) { // if the object empty no need to update anything!
                resolve();
            }
            if (!!userId) {
                ebbDetails.updatedAt = serverTimestamp();
                ;
                if (!ebbDetails.createdAt) {
                    ebbDetails.createdAt = serverTimestamp();
                    ;
                }
                this.angularFireDatabase.object(FirebaseDatabasePaths.USER_EBB_DETAILS(userId)).update(ebbDetails)
                    .then(() => {
                    console.debug('User Details Saved Successfully');
                    resolve();
                }).catch((error) => {
                    console.error('Update User EBB Details Failed ', error.message);
                    reject();
                });
            }
            else {
                console.warn('User is not loggedIn');
                reject();
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FirebaseEBBService, deps: [{ token: i1$1.AngularFireDatabase }, { token: i1.SimpleAuthService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FirebaseEBBService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FirebaseEBBService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1$1.AngularFireDatabase }, { type: i1.SimpleAuthService }] });

class ManufacturerConfigurationService {
    constructor(angularFireDatabase) {
        this.angularFireDatabase = angularFireDatabase;
        this.phonesList = this.angularFireDatabase.list(FirebaseDatabasePaths.MANUFACTURER_CONFIGURATION.BASE).valueChanges();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ManufacturerConfigurationService, deps: [{ token: i1$1.AngularFireDatabase }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ManufacturerConfigurationService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ManufacturerConfigurationService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1$1.AngularFireDatabase }] });

class CatalogService {
    constructor(endpointUrl, http) {
        this.endpointUrl = endpointUrl;
        this.http = http;
        this.CATALOG_V1_URL = `${this.endpointUrl}/api/catalog/external`;
    }
    getCatalog(type) {
        const path = !!type ? `${this.CATALOG_V1_URL}?type=${type}` : `${this.CATALOG_V1_URL}`;
        return new Promise((resolve, reject) => {
            this.http.get(`${path}`).subscribe((response) => resolve(response), (error) => reject(error));
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: CatalogService, deps: [{ token: EndpointUrl }, { token: i1.AuthHttp }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: CatalogService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: CatalogService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [EndpointUrl]
                }] }, { type: i1.AuthHttp }] });

class BackendModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: BackendModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: BackendModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: BackendModule, providers: [AccountPaymentService, FirebaseAccountPaymentService, AccountRefillService,
            AccountUsageService, ActionsAnalyticsService, FirebaseAdsService, ChangePlanService,
            ContactEmailService, DealersService, InternationalCallingConfigurationService,
            FirebaseMobilePlansCartService, MobileCustomPlansService, OrderCheckoutService,
            PlacesAutocompleteService, PlansConfigurationService, ReferralService,
            ShippingConfigurationService, ShippingService, ShopConfigurationService,
            SimActivationService, StoreLocatorService, TaxConfigurationService, CatalogService,
            UserAccountPortInService, UserAccountService, FirebaseUserAccountsService,
            UserDeviceService, UserOrdersService, UserPlansService, FirebaseUserPlansService,
            FirebaseUserProfileService, VoucherActivationService, FirebaseEBBService, ManufacturerConfigurationService,
            UserAccountServiceNoSync, MobileCustomPlansServiceNoSync
        ] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: BackendModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [AccountPaymentService, FirebaseAccountPaymentService, AccountRefillService,
                        AccountUsageService, ActionsAnalyticsService, FirebaseAdsService, ChangePlanService,
                        ContactEmailService, DealersService, InternationalCallingConfigurationService,
                        FirebaseMobilePlansCartService, MobileCustomPlansService, OrderCheckoutService,
                        PlacesAutocompleteService, PlansConfigurationService, ReferralService,
                        ShippingConfigurationService, ShippingService, ShopConfigurationService,
                        SimActivationService, StoreLocatorService, TaxConfigurationService, CatalogService,
                        UserAccountPortInService, UserAccountService, FirebaseUserAccountsService,
                        UserDeviceService, UserOrdersService, UserPlansService, FirebaseUserPlansService,
                        FirebaseUserProfileService, VoucherActivationService, FirebaseEBBService, ManufacturerConfigurationService,
                        UserAccountServiceNoSync, MobileCustomPlansServiceNoSync
                    ],
                }]
        }] });

/**
 * (Used when): If the user already has a phone then an object of type UserDevice is sent with the imei code inserted by the user.
 * (Scenario): when the user checks his phone compaitablility the backend returns device information(brand , os ,...) about the user's phone based on its imei
 */
class UserDevice {
    get equipmentType() {
        return this._equipmentType;
    }
    set equipmentType(value) {
        this._equipmentType = value;
    }
    get verified() {
        return this._verified;
    }
    set verified(value) {
        this._verified = value;
    }
    get networkType() {
        return this._networkType;
    }
    set networkType(value) {
        this._networkType = value;
    }
    get activationCode() {
        return this._activationCode;
    }
    set activationCode(value) {
        this._activationCode = value;
    }
    get iccid() {
        return this._iccid;
    }
    set iccid(value) {
        this._iccid = value;
    }
    get id() {
        return this._id;
    }
    set id(value) {
        this._id = value;
    }
    get network() {
        return this._network;
    }
    set network(value) {
        this._network = value;
    }
    get brand() {
        return this._brand;
    }
    set brand(value) {
        this._brand = value;
    }
    get model() {
        return this._model;
    }
    set model(value) {
        this._model = value;
    }
    get marketingName() {
        return this._marketingName;
    }
    set marketingName(value) {
        this._marketingName = value;
    }
    get manufacturer() {
        return this._manufacturer;
    }
    set manufacturer(value) {
        this._manufacturer = value;
    }
    get os() {
        return this._os;
    }
    set os(value) {
        this._os = value;
    }
}

var OrderStatus;
(function (OrderStatus) {
    OrderStatus[OrderStatus["new"] = 0] = "new";
    OrderStatus[OrderStatus["pending"] = 1] = "pending";
    OrderStatus[OrderStatus["processed"] = 2] = "processed";
    OrderStatus[OrderStatus["shipped"] = 3] = "shipped";
    OrderStatus[OrderStatus["delivered"] = 4] = "delivered";
    OrderStatus[OrderStatus["svc_purchased"] = 5] = "svc_purchased";
    OrderStatus[OrderStatus["canceled"] = 6] = "canceled";
    OrderStatus[OrderStatus["voided"] = 7] = "voided";
    OrderStatus[OrderStatus["investigate"] = 8] = "investigate";
    OrderStatus[OrderStatus["failed"] = 9] = "failed";
})(OrderStatus || (OrderStatus = {}));
var OrderIntent;
(function (OrderIntent) {
    OrderIntent[OrderIntent["new"] = 0] = "new";
    OrderIntent[OrderIntent["refill"] = 1] = "refill";
    OrderIntent[OrderIntent["change_plan"] = 2] = "change_plan";
    OrderIntent[OrderIntent["new_sim"] = 3] = "new_sim";
    OrderIntent[OrderIntent["replacement_sim"] = 4] = "replacement_sim";
    OrderIntent[OrderIntent["addon"] = 5] = "addon";
})(OrderIntent || (OrderIntent = {}));

/**
 * Represents saved details for each EBB step
 */

/*
 * Public API Surface of backend
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AccountPaymentService, AccountRefillService, AccountUsageService, ActionsAnalyticsService, BackendModule, CART_TYPES, COUPONS, CatalogService, ChangePlanService, ContactEmailService, CustomizableMobilePlan, DealersService, FirebaseAccountPaymentService, FirebaseAdsService, FirebaseEBBService, FirebaseMobilePlansCartService, FirebaseUserAccountsService, FirebaseUserPlansService, FirebaseUserProfileService, GenericMobilePlanItem, InternationalCallingConfigurationService, ManufacturerConfigurationService, MobileCustomPlansService, MobileCustomPlansServiceNoSync, MobilePlanDetails, MobilePlanItem, OptionsMobilePlanItem, OrderCheckoutService, OrderIntent, OrderStatus, PAYMENT_SORT_DIRECTION, PAYMENT_SORT_FIELDS, PURCHASE_INTENT, PlacesAutocompleteService, PlansConfigurationService, PortInStatusEnum, ReferralService, ShippingConfigurationService, ShippingService, ShopConfigurationService, SimActivationService, StoreLocatorService, TaxConfigurationService, USAGE_SORT_DIRECTION, USAGE_SORT_FIELDS, UserAccountPortInService, UserAccountService, UserAccountServiceNoSync, UserData, UserDevice, UserDeviceService, UserOrdersService, UserPlansService, VoucherActivationService };
